<template>
  <div class="map-drawer" :class="{ 'map-drawer--maximized': cameraMaximized }">
    <!-- Header: compact — avatar, title, status + ID, close -->
    <div class="drawer-header" :style="{ borderLeftColor: entityColor }">
      <div
        class="drawer-avatar"
        :class="{ 'drawer-avatar--svg': deviceSvgUrl }"
        :style="deviceSvgUrl ? null : { background: entityColor }"
      >
        <img v-if="deviceSvgUrl" :src="deviceSvgUrl" class="drawer-avatar__img" alt="" />
        <v-icon v-else small color="white">{{ entityIcon }}</v-icon>
      </div>
      <div class="drawer-title-wrap">
        <div class="drawer-title" :title="entityTitle">{{ entityTitle }}</div>
        <div class="drawer-subtitle" :title="entitySubtitle">
          <span>{{ entitySubtitle }}</span>
          <!-- Para incident agregamos el ícono i con tooltip al lado del
               subtítulo. Mantiene el contexto del banner viejo (Zona 2)
               sin ocupar espacio vertical adicional. -->
          <v-btn
            v-if="entityType === 'incident' && incidentActionContext"
            x-small
            icon
            class="drawer-subtitle__info-btn"
            v-auto-tooltip="incidentActionContext"
          >
            <v-icon
              x-small
              :color="incidentPrioridadIsAlta && incidentIsActive ? 'var(--kln-alert-danger)' : 'var(--kln-text-muted)'"
            >
              {{ incidentPrioridadIsAlta && incidentIsActive ? 'mdi-alert-circle' : 'mdi-information-outline' }}
            </v-icon>
          </v-btn>
        </div>
      </div>
      <!-- El chip de status no aplica a reportant: su estado siempre es
           'alert' (entityConfig.getStatus() es constante), no aporta info
           variable, y rompe el teléfono del title en dos líneas. -->
      <div v-if="entityType !== 'kpi' && entityType !== 'reportant'" class="drawer-header-right">
        <v-chip x-small :color="statusChipColor" text-color="white" class="status-chip">{{ statusLabel }}</v-chip>
        <span v-if="showDrawerId" class="drawer-id">#{{ drawerIdText }}</span>
      </div>
      <v-btn icon x-small class="drawer-close" v-auto-tooltip="'Copiar'" @click="copyInfo">
        <v-icon small>mdi-content-copy</v-icon>
      </v-btn>
      <v-btn
        icon
        x-small
        class="drawer-close"
        v-auto-tooltip="isPinned ? 'Quitar pin' : 'Pinear para navegar entre varios'"
        @click="$emit('toggle-pin')"
      >
        <v-icon small :color="isPinned ? 'var(--kln-accent)' : ''">{{ isPinned ? 'mdi-pin' : 'mdi-pin-outline' }}</v-icon>
      </v-btn>
      <v-btn icon x-small class="drawer-close" v-auto-tooltip="'Colapsar panel (mantiene la selección)'" @click="$emit('collapse')">
        <v-icon small>mdi-chevron-right</v-icon>
      </v-btn>
    </div>

    <!-- Action buttons (hidden for Murphy results and KPI).
         Jerarquía visual por estilo y color:
           - Utilitarios (Centrar, Maps): icon-only outlined, bajo perfil
           - CTA primaria (Despachar): fondo sólido warning, texto blanco
           - Acción secundaria (Editar): fondo sólido neutro blue-grey, texto blanco
         Filled = peso visual alto, así el operador identifica rápido las
         dos acciones "que modifican algo" vs las utilitarias. -->
    <div v-if="entityType !== 'kpi'" class="drawer-actions">
      <!-- 1. Centrar (icon-only, todos los entity types) -->
      <v-btn
        icon
        x-small
        outlined
        color="var(--kln-accent)"
        class="drawer-action-btn drawer-action-btn--icon"
        v-auto-tooltip="'Centrar este elemento en el mapa'"
        @click="$emit('center', item)"
      >
        <v-icon x-small>mdi-crosshairs-gps</v-icon>
      </v-btn>
      <!-- 2. Maps (icon-only, solo incident, tooltip con coords) -->
      <v-btn
        v-if="entityType === 'incident' && incidentMapsUrl"
        icon
        x-small
        outlined
        color="grey lighten-1"
        class="drawer-action-btn drawer-action-btn--icon"
        v-auto-tooltip="incidentMapsTooltip"
        :href="incidentMapsUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        <v-icon x-small>mdi-google-maps</v-icon>
      </v-btn>
      <!-- 3. Despachar (filled warning, CTA principal) -->
      <v-btn
        v-if="canDispatchIncident"
        x-small
        depressed
        dark
        color="warning"
        class="drawer-action-btn"
        v-auto-tooltip="'Despachar unidades al incidente (CAD)'"
        @click="$emit('dispatch-incident', item)"
      >
        <v-icon x-small left>mdi-alert-circle</v-icon>
        Despachar
      </v-btn>
      <!-- 4. Editar (filled neutro, acción secundaria) -->
      <v-btn
        v-if="entityType === 'incident' && canEditIncident"
        x-small
        depressed
        dark
        color="blue-grey darken-1"
        class="drawer-action-btn"
        v-auto-tooltip="'Editar información del incidente'"
        @click="openEditIncidentDialog"
      >
        <v-icon x-small left>mdi-pencil-outline</v-icon>
        Editar
      </v-btn>
      <!-- Medida cautelar: abre el seguimiento en vivo de la persona desde
           la alerta. El mapa principal no muestra a las personas; este botón
           es el único punto de entrada al seguimiento. -->
      <v-btn
        v-if="mcPersonaId"
        x-small
        depressed
        dark
        color="var(--kln-marker-sensor-standby)"
        class="drawer-action-btn"
        v-auto-tooltip="'Abrir el expediente de seguimiento en la consola de monitoreo'"
        @click="$emit('mc-seguir', item)"
      >
        <v-icon x-small left>mdi-account-arrow-right</v-icon>
        Abrir expediente
      </v-btn>
      <!-- Seguir / Historial: aplican a otros entity types (devices, etc.) -->
      <v-btn
        v-if="canFollow"
        x-small
        outlined
        :color="isFollowing ? 'error' : 'secondary'"
        class="drawer-action-btn"
        v-auto-tooltip="isFollowing ? 'Dejar de seguir en tiempo real' : 'Seguir en tiempo real'"
        @click="$emit('follow', item)"
      >
        <v-icon x-small left>mdi-navigation</v-icon>
        {{ isFollowing ? 'Dejar' : 'Seguir' }}
      </v-btn>
      <v-menu
        v-if="canViewHistory"
        offset-y
        :close-on-content-click="true"
      >
        <template #activator="{ on, attrs }">
          <v-btn
            x-small
            outlined
            color="secondary"
            class="drawer-action-btn"
            v-auto-tooltip="'Ver historial de trayectos'"
            v-bind="attrs"
            v-on="on"
          >
            <v-icon x-small left>mdi-history</v-icon>
            Historial
            <v-icon x-small right>mdi-menu-down</v-icon>
          </v-btn>
        </template>
        <v-list dense class="drawer-history-menu">
          <v-list-item
            v-for="opt in historyPeriodOptions"
            :key="opt.id"
            @click="$emit('history', { item: item, period: opt.id })"
          >
            <v-list-item-title>{{ opt.label }}</v-list-item-title>
          </v-list-item>
          <v-divider />
          <v-list-item @click="$emit('history', { item: item, period: 'custom' })">
            <v-list-item-title>Personalizado…</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-btn
        v-if="canDiscardAlert"
        x-small
        outlined
        color="error"
        class="drawer-action-btn"
        v-auto-tooltip="'Descartar alerta sin generar reporte'"
        @click="$emit('discard-alert', item)"
      >
        <v-icon x-small left>mdi-delete</v-icon>
        Descartar
      </v-btn>
      <v-btn
        v-if="canGenerateSensorAlert"
        x-small
        outlined
        color="warning"
        class="drawer-action-btn"
        v-auto-tooltip="'Generar alerta manual en este sensor'"
        @click="$emit('generate-sensor-alert', item)"
      >
        <v-icon x-small left>mdi-alert-plus</v-icon>
        Generar alerta
      </v-btn>
      <v-btn
        v-if="showReportInActionBar"
        x-small
        outlined
        color="secondary"
        class="drawer-action-btn"
        v-auto-tooltip="'Reportar al CAD: abrir folio nuevo o asociar a uno existente'"
        @click="$emit('report-cad', item)"
      >
        <v-icon x-small left>mdi-file-document-edit-outline</v-icon>
        Reporte
      </v-btn>
      <v-btn
        v-if="canViewDeviceGeofences"
        x-small
        outlined
        :color="deviceGeofencesVisible ? 'warning' : 'secondary'"
        class="drawer-action-btn"
        v-auto-tooltip="deviceGeofencesVisible ? 'Ocultar geocercas del dispositivo' : 'Ver geocercas asignadas al dispositivo'"
        @click="$emit('toggle-device-geofences', item)"
      >
        <v-icon x-small left>mdi-shape-polygon-plus</v-icon>
        {{ deviceGeofencesVisible ? 'Ocultar geocercas' : 'Ver geocercas' }}
      </v-btn>
      <v-btn
        v-if="canOpenTetraCommunication"
        x-small
        outlined
        color="var(--kln-accent)"
        class="drawer-action-btn drawer-action-btn--tetra"
        v-auto-tooltip="'Abrir canal de comunicación TETRA con este dispositivo'"
        @click="$emit('open-tetra-communication', item)"
      >
        <v-icon x-small left>mdi-radio-handheld</v-icon>
        Comunicación
      </v-btn>
      <v-btn
        v-if="canOpenIrp"
        x-small
        outlined
        color="secondary"
        class="drawer-action-btn"
        v-auto-tooltip="'Abrir panel IRP externo de seguimiento'"
        @click="$emit('open-irp', item)"
      >
        <v-icon x-small left>mdi-open-in-new</v-icon>
        Seguimiento IRP
      </v-btn>
      <v-btn
        v-if="canCloseAlert"
        x-small
        outlined
        color="error"
        class="drawer-action-btn"
        v-auto-tooltip="'Cerrar alerta (genera reporte en el sistema)'"
        @click="$emit('close-alert', item)"
      >
        <v-icon x-small left>mdi-check-all</v-icon>
        Cerrar alerta
      </v-btn>
    </div>

    <v-divider class="drawer-divider" />

    <!-- Scrollable content: details + nearby.
         data-simplebar inicializa SimpleBar automaticamente al montar
         este div, creando un scrollbar div-based always-visible que
         funciona identico en macOS/Windows/Linux y todos los browsers
         (no depende del scrollbar nativo del OS que se auto-oculta). -->
    <div ref="scrollContent" class="drawer-scroll-content" data-simplebar>
    <!-- Detail rows (contextual per entity type) -->
    <div class="drawer-details">

      <!-- Map Point -->
      <template v-if="entityType === 'mapPoint'">
        <div class="drawer-detail-mappoint">
          <v-icon small color="var(--kln-text-muted)" class="mr-2">mdi-map-marker-outline</v-icon>
          <div>
            <div class="drawer-detail-title">Punto del mapa</div>
            <div class="drawer-detail-subtitle-mono">{{ data.subtitle || '—' }}</div>
          </div>
        </div>
      </template>

      <!-- Device — paridad con fieldsMap.devices del mapa legacy -->
      <template v-else-if="entityType === 'device'">
        <!-- ZONA 1 — Crítica. DRAWERS-VARIANTS-SPEC §1.2. -->
        <div class="drawer-zone drawer-zone--1">
          <!-- Foto del operador + badges LTE/TETRA visibles a primera vista. -->
          <div class="device-photo-row">
            <div class="device-photo-wrap">
              <img
                :src="deviceUserPhoto"
                class="device-photo"
                alt="Foto usuario"
                @error="onDevicePhotoError"
              />
            </div>
            <div class="device-badge-col">
              <img v-if="deviceLteIcon" :src="deviceLteIcon" class="device-badge device-badge--lte" :class="deviceLteClass" alt="" />
              <img v-if="deviceTypeIcon" :src="deviceTypeIcon" class="device-badge device-badge--type" alt="" />
            </div>
          </div>
          <drawer-zone-row zone="zone-1" label="Dispositivo" :value="data.name || '—'" />
          <drawer-zone-row zone="zone-1" label="Operador" :value="deviceUsuario">
            <template #action>
              <drawer-cross-ref-button
                v-if="puedeMurphy && deviceCanSearchMurphy"
                icon="mdi-card-account-details-outline"
                tooltip="Consultar conductor en Murphy"
                @click="onSearchConductorInMurphy"
              />
            </template>
          </drawer-zone-row>
          <drawer-zone-row
            zone="zone-1"
            label="Estatus"
            :value="deviceEstatus"
            :value-color="deviceStatusColor"
            :fresh="deviceLastUpdateFresh"
            :flash-key="data.lastUpdate || data.updatedAt || ''"
          />
          <drawer-zone-row
            v-if="devicePosition"
            zone="zone-1"
            label="Ubicación"
            :value="deviceUbicacion"
          >
            <template #action>
              <drawer-cross-ref-button
                icon="mdi-crosshairs-gps"
                tooltip="Centrar en mapa"
                @click="$emit('center', item)"
              />
            </template>
          </drawer-zone-row>
          <drawer-zone-row
            v-if="devicePosition"
            zone="zone-1"
            label="Velocidad"
            :value="deviceVelocidad"
            :flash-key="devicePosition && devicePosition.speed"
          />
          <drawer-zone-row v-if="deviceRumbo" zone="zone-1" label="Rumbo" :value="deviceRumbo" />
        </div>

        <!-- ZONA 2 — Acción. Se oculta cuando no hay acción primaria. -->
        <div v-if="devicePrimaryAction" class="drawer-zone drawer-zone--2">
          <div class="drawer-zone__context">{{ deviceActionContext }}</div>
          <drawer-action-button
            variant="primary"
            :label="devicePrimaryAction.label"
            :icon="devicePrimaryAction.icon"
            :tone="devicePrimaryAction.tone"
            @click="onDevicePrimaryAction"
          />
        </div>

        <!-- ZONA 3 — Contextual (eventos activos con tratamiento de alerta). -->
        <div v-if="deviceHasActiveEvents" class="drawer-zone drawer-zone--3">
          <drawer-section-header
            label="Eventos activos"
            :count="(data.events || []).length"
            :alert="true"
          />
          <div
            v-for="(ev, idx) in (data.events || [])"
            :key="(ev.id || ev.type) + '-' + idx"
            class="drawer-event-row"
          >
            <span class="drawer-event-row__type">{{ ev.type || ev.name || 'Evento' }}</span>
            <span class="drawer-event-row__ts">{{ deviceEventTimeAgo(ev) }}</span>
          </div>
        </div>

        <!-- Empate del elemento: radios y unidades que comparten conductor
             (attributes.conductor). Bidireccional — desde un radio se ven
             las unidades del elemento y desde una unidad sus radios. -->
        <div v-if="conductorLinkGroups.length" class="drawer-zone drawer-zone--3">
          <drawer-section-header
            label="Equipo del elemento"
            :count="conductorLinkCount"
            :collapsible="true"
            :expanded="equipoOpen"
            @toggle="equipoOpen = !equipoOpen"
          />
          <div v-show="equipoOpen" class="drawer-zone__content">
            <div v-for="group in conductorLinkGroups" :key="group.key" class="equipo-group">
              <div class="equipo-group__label">
                <v-icon x-small class="mr-1">{{ group.icon }}</v-icon>
                <span>{{ group.label }} ({{ group.total }})</span>
              </div>
              <div
                v-for="it in group.items"
                :key="it.key"
                class="nearby-item"
                :class="{ 'equipo-item--offmap': !it.onMap }"
                v-auto-tooltip="!it.onMap
                  ? 'Sin posición reciente — no está en el mapa'
                  : (group.key === 'radios' ? 'Ver este radio en el mapa' : 'Ver esta unidad en el mapa')"
                @click="it.onMap && $emit('select-nearby', it.monitor)"
              >
                <div class="nearby-item-row">
                  <span class="nearby-name">
                    <v-icon v-if="!it.onMap" x-small class="equipo-offmap-icon">mdi-map-marker-off-outline</v-icon>
                    {{ it.title }}
                  </span>
                  <span class="nearby-status" :class="'nearby-status--' + (it.status === 'online' ? 'online' : 'offline')">
                    {{ it.status === 'online' ? 'En línea' : 'Fuera de línea' }}
                  </span>
                </div>
              </div>
              <div v-if="group.extra > 0" class="equipo-group__more">+{{ group.extra }} más</div>
            </div>
          </div>
        </div>

        <!-- ZONA 4 — Administrativa, colapsable. -->
        <div class="drawer-zone drawer-zone--4">
          <drawer-section-header
            label="Datos administrativos"
            :collapsible="true"
            :expanded="zone4Expanded"
            @toggle="zone4Expanded = !zone4Expanded"
          />
          <div v-show="zone4Expanded" class="drawer-zone__content">
            <drawer-zone-row zone="zone-4" label="Unidad Administrativa" :value="deviceUnidadAdmin" />
            <drawer-zone-row zone="zone-4" label="Adscripción" :value="deviceAdscripcion" />
            <drawer-zone-row zone="zone-4" label="Categoría" :value="deviceCategoria" />
            <drawer-zone-row zone="zone-4" label="Grupo" :value="data.groupName || '—'" />
            <drawer-zone-row v-if="devicePhone" zone="zone-4" label="Teléfono" :value="devicePhone">
              <template #action>
                <drawer-cross-ref-button
                  icon="mdi-phone-outline"
                  tooltip="Llamar al chofer"
                  @click="onCallDevicePhone"
                />
              </template>
            </drawer-zone-row>
            <drawer-zone-row zone="zone-4" label="Última actualización" :value="deviceUltimaActualizacion" />
            <drawer-zone-row v-if="devicePosition" zone="zone-4" label="Precisión" :value="devicePrecision" />
            <drawer-zone-row v-if="devicePosition && devicePosition.protocol" zone="zone-4" label="Protocolo GPS" :value="devicePosition.protocol" />
            <drawer-zone-row v-if="deviceFixTimeRelative" zone="zone-4" label="Última fix GPS" :value="deviceFixTimeRelative" />
            <drawer-zone-row v-if="deviceDistancia" zone="zone-4" label="Distancia total" :value="deviceDistancia" />
            <drawer-zone-row v-if="devicePosition" zone="zone-4" label="Movimiento" :value="deviceMovimiento" />
            <drawer-zone-row zone="zone-4" label="Tipo de alerta" :value="deviceTipoAlerta" />
            <drawer-zone-row v-if="deviceDestinatarios" zone="zone-4" label="Destinatarios" :value="deviceDestinatarios" />
            <drawer-zone-row v-if="data.uniqueId" zone="zone-4" label="Identificador hardware" :value="data.uniqueId" />
          </div>
        </div>
      </template>

      <!-- Bus — DRAWERS-VARIANTS-SPEC §2 (4 zonas) -->
      <template v-else-if="entityType === 'bus'">
        <!-- ZONA 1 — Crítica. -->
        <div class="drawer-zone drawer-zone--1">
          <drawer-zone-row zone="zone-1" label="Unidad" :value="data.name || '—'" />
          <drawer-zone-row
            zone="zone-1"
            label="Estatus"
            :value="busStatusText"
            :value-color="busStatusColor"
          />
          <drawer-zone-row
            zone="zone-1"
            label="Última señal"
            :value="busLastUpdateRelative"
            :flash-key="data.lastUpdate || data.updatedAt || ''"
          />
          <drawer-zone-row
            v-if="data.longitude"
            zone="zone-1"
            label="Ubicación"
            :value="busUbicacion"
          >
            <template #action>
              <drawer-cross-ref-button
                icon="mdi-crosshairs-gps"
                tooltip="Centrar en mapa"
                @click="$emit('center', item)"
              />
            </template>
          </drawer-zone-row>
          <drawer-zone-row
            v-if="data.position && data.position.speed != null || data.speed != null"
            zone="zone-1"
            label="Velocidad"
            :value="busVelocidad"
            :flash-key="(data.position && data.position.speed) || data.speed"
          />
          <drawer-zone-row
            v-if="data.numberOfCameras"
            zone="zone-1"
            label="Cámaras a bordo"
            :value="String(data.numberOfCameras)"
          />
        </div>

        <!-- ZONA 2 — Acción (context solo; botones ya en drawer-actions). -->
        <div class="drawer-zone drawer-zone--2">
          <div class="drawer-zone__context" :class="{ 'drawer-zone__context--alert': busHasAlert }">
            <v-icon
              v-if="busHasAlert"
              x-small
              color="var(--kln-alert-danger)"
              class="mr-1"
            >mdi-alert</v-icon>
            {{ busActionContext }}
          </div>
        </div>

        <!-- ZONA 3 — Contextual. Las cámaras embebidas viven fuera de esta
             sección (bloque pmi-cameras compartido con PMIs al final del
             drawer). Recursos cercanos en caso alert los inyecta MapV2. -->

        <!-- ZONA 4 — Administrativa. -->
        <div class="drawer-zone drawer-zone--4">
          <drawer-section-header
            label="Datos administrativos"
            :collapsible="true"
            :expanded="zone4Expanded"
            @toggle="zone4Expanded = !zone4Expanded"
          />
          <div v-show="zone4Expanded" class="drawer-zone__content">
            <drawer-zone-row zone="zone-4" label="Grupo" :value="data.groupName || '—'" />
            <drawer-zone-row v-if="data.address" zone="zone-4" label="Dirección" :value="data.address" />
            <drawer-zone-row v-if="data.phone" zone="zone-4" label="Teléfono" :value="data.phone">
              <template #action>
                <drawer-cross-ref-button
                  icon="mdi-phone-outline"
                  tooltip="Llamar"
                  @click="onCallBusPhone"
                />
              </template>
            </drawer-zone-row>
            <drawer-zone-row v-if="data.contact" zone="zone-4" label="Contacto" :value="data.contact" />
            <drawer-zone-row v-if="data.uniqueId" zone="zone-4" label="Identificador" :value="data.uniqueId" />
            <drawer-zone-row zone="zone-4" label="Última actualización" :value="busLastUpdate" />
          </div>
        </div>
      </template>

      <!-- Camera / PMI -->
      <template v-else-if="entityType === 'camera'">
        <div class="drawer-zone drawer-zone--1">
          <drawer-zone-row v-if="getCameraOwnershipLabel()" zone="zone-1" label="Ámbito" :value="getCameraOwnershipLabel()" />
          <drawer-zone-row zone="zone-1" label="Grupo" :value="data.groupName || data.group || '—'" />
          <drawer-zone-row v-if="data.city" zone="zone-1" label="Municipio" :value="data.city" />
          <drawer-zone-row zone="zone-1" label="Dirección" :value="data.address || data.location || '—'" />
        </div>
      </template>

      <!-- LPR — incluye últimos hits del device (GET /v2/analytic/records/
           device/:id/lasthits), paridad con selectLpr del legacy. -->
      <template v-else-if="entityType === 'lpr'">
        <div class="drawer-zone drawer-zone--1">
          <drawer-zone-row zone="zone-1" label="Nombre" :value="data.name || '—'" />
          <drawer-zone-row v-if="data.identifier" zone="zone-1" label="Identificador" :value="data.identifier" />
          <drawer-zone-row v-if="data.groupName || data.group" zone="zone-1" label="Grupo" :value="data.groupName || data.group" />
          <drawer-zone-row v-if="data.address || data.location" zone="zone-1" label="Dirección" :value="data.address || data.location" />
          <drawer-zone-row v-if="item.subtitle || data.status" zone="zone-1" label="Estatus" :value="item.subtitle || lprStatusLabel" />
        </div>

        <div class="drawer-zone drawer-zone--3">
          <drawer-section-header label="Historial de hits" :count="lprHits.length" />
          <div v-if="lprHitsLoading" class="drawer-section-empty">Cargando…</div>
          <div v-else-if="!lprHits.length" class="drawer-section-empty">Sin detecciones recientes.</div>
          <div v-else class="drawer-lpr-hits">
            <div v-for="(hit, idx) in lprHits" :key="idx" class="drawer-lpr-hit-row">
              <span class="drawer-lpr-hit-plate-group">
                <span class="drawer-lpr-hit-plate">{{ hit.plate }}</span>
                <v-btn
                  v-if="puedeMurphy && hit.plate && hit.plate !== '—'"
                  icon
                  x-small
                  class="detail-row__action"
                  v-auto-tooltip="'Buscar placa en Murphy'"
                  @click.stop="onSearchPlacaMurphy(hit.plate)"
                >
                  <v-icon x-small>mdi-card-account-details-outline</v-icon>
                </v-btn>
              </span>
              <span class="drawer-lpr-hit-date">{{ hit.when }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- Incident — DRAWERS-VARIANTS-SPEC §5 (4 zonas con modulación por prioridad) -->
      <template v-else-if="entityType === 'incident'">
        <!-- ZONA 1A — Campos cortos en grid 2-col. Las coords se acceden
             desde el botón "Maps" en el header de acciones; aquí no las
             repetimos porque al operador solo le sirven funcionalmente. -->
        <div class="drawer-zone drawer-zone--1 drawer-zone--grid2">
          <drawer-zone-row
            zone="zone-1"
            label="Identificador"
            :value="data.uniqueId || data.name || String(data.id || '')"
          />
          <drawer-zone-row
            zone="zone-1"
            label="Categoría"
            :value="incidentCategoriaLabel"
            :value-color="incidentCategoriaColor"
          />
          <drawer-zone-row
            v-if="incidentPrioridad"
            zone="zone-1"
            label="Prioridad"
            :value="incidentPrioridad"
            :value-color="incidentPrioridadColor"
          />
          <drawer-zone-row
            zone="zone-1"
            label="Estado"
            :value="incidentEstado"
            :value-color="incidentEstadoColor"
          />
          <drawer-zone-row
            zone="zone-1"
            label="Hora del incidente"
            :value="incidentFechaRelativa"
          />
          <drawer-zone-row
            zone="zone-1"
            label="Catalogado"
            :value="incidentCatalogRelativa"
          />
        </div>

        <!-- ZONA 1B — Tipo full-width (texto largo, no entra cómodo en grid). -->
        <div class="drawer-zone drawer-zone--1">
          <drawer-zone-row
            zone="zone-1"
            label="Tipo"
            :value="incidentTipo"
          />
        </div>

        <!-- ZONA 1C — Datos del reportante embebidos en el bloque principal
             del incidente (antes vivían en una sección separada "REPORTANTE"
             en Zona 3, que generaba scroll innecesario). Orden:
             Descripción → Nombre (con cross-ref Murphy) → Teléfono (con
             botón llamar) → Dirección. -->
        <div
          v-if="incidentAttrs.descripcion || incidentAttrs.nombreCompletoCiudadano || incidentAttrs.numeroReportante"
          class="drawer-zone drawer-zone--1"
        >
          <drawer-zone-row
            v-if="incidentAttrs.descripcion"
            zone="zone-1"
            label="Descripción"
            :value="incidentAttrs.descripcion"
          />
          <drawer-zone-row
            v-if="incidentAttrs.nombreCompletoCiudadano"
            zone="zone-1"
            label="Nombre del reportante"
            :value="incidentAttrs.nombreCompletoCiudadano"
          >
            <template #action>
              <drawer-cross-ref-button
                v-if="puedeMurphy && incidentHasReportanteName"
                icon="mdi-card-account-details-outline"
                tooltip="Consultar en Murphy"
                @click="onSearchReportanteMurphy"
              />
            </template>
          </drawer-zone-row>
          <drawer-zone-row
            v-if="incidentAttrs.numeroReportante"
            zone="zone-1"
            label="Teléfono del reportante"
            :value="incidentAttrs.numeroReportante"
          >
            <template #action>
              <drawer-cross-ref-button
                icon="mdi-phone-outline"
                tooltip="Llamar al reportante"
                @click="onCallReportante"
              />
            </template>
          </drawer-zone-row>
        </div>

        <!-- ZONA 1D — Dirección al final del bloque (texto largo, calle +
             colonia + municipio). Lectura natural: metadatos del incidente
             → contexto del reportante → lugar físico. -->
        <div v-if="incidentAddress" class="drawer-zone drawer-zone--1">
          <drawer-zone-row
            zone="zone-1"
            label="Dirección"
            :value="incidentAddress"
          />
        </div>

        <!-- ZONA 2 eliminada — el banner contextual ocupaba demasiado
             vertical para info que en la mayoría de los casos es redundante
             (la prioridad ya tiene su chip en Zona 1, las unidades viven
             en el panel de Despacho). El contexto se sigue exponiendo
             como ícono i con tooltip en el header de acciones del drawer. -->


        <!-- ZONA 3 — Contextual: notas, razonamientos, reportante, Uber. -->
        <div class="drawer-zone drawer-zone--3">
          <!-- Reportante: los campos (Descripción, Nombre, Teléfono) se
               movieron al bloque principal del incidente arriba (Zona 1D)
               para evitar scroll innecesario y mantener el contexto visible. -->

          <!-- Interconexión externa (Carbyne / DiDi / Uber / RapidSOS).
               Aparece cuando el incidente fue enriquecido por el WS de
               trip data con positions del reportante (rapidSosLocations)
               o lleva un systemName explícito. Indica al operador que
               este incidente recibe GPS en tiempo real del proveedor. -->
          <template v-if="incidentPartner">
            <drawer-section-header label="Interconexión" />
            <div class="incident-partner-row">
              <img
                :src="incidentPartner.logo"
                :alt="incidentPartner.label"
                class="incident-partner-logo"
              />
              <div class="incident-partner-info">
                <div class="incident-partner-label">{{ incidentPartner.label }}</div>
                <div
                  v-if="incidentPartnerHint"
                  class="incident-partner-hint"
                >{{ incidentPartnerHint }}</div>
              </div>
            </div>
          </template>

          <!-- Uber -->
          <template v-if="incidentUberInfo">
            <drawer-section-header label="Vehículo (Uber)" />
            <drawer-zone-row
              v-if="incidentUberInfo.vehicle"
              zone="zone-2"
              label="Vehículo"
              :value="incidentUberInfo.vehicle"
            />
            <drawer-zone-row
              v-if="incidentUberInfo.plate"
              zone="zone-2"
              label="Placa"
              :value="incidentUberInfo.plate"
            >
              <template #action>
                <drawer-cross-ref-button
                  v-if="puedeMurphy"
                  icon="mdi-car"
                  tooltip="Buscar placa en Murphy"
                  @click="onSearchPlacaMurphy(incidentUberInfo.plate)"
                />
              </template>
            </drawer-zone-row>
            <drawer-zone-row
              v-if="incidentUberInfo.userName"
              zone="zone-2"
              label="Conductor Uber"
              :value="incidentUberInfo.userName"
            >
              <template #action>
                <drawer-cross-ref-button
                  v-if="puedeMurphy"
                  icon="mdi-card-account-details-outline"
                  tooltip="Consultar conductor en Murphy"
                  @click="onSearchUberDriverMurphy"
                />
              </template>
            </drawer-zone-row>
            <drawer-zone-row
              v-if="incidentUberInfo.userPhone"
              zone="zone-2"
              label="Tel. conductor"
              :value="incidentUberInfo.userPhone"
            >
              <template #action>
                <drawer-cross-ref-button
                  icon="mdi-phone-outline"
                  tooltip="Llamar al conductor"
                  @click="onCallUberDriver"
                />
              </template>
            </drawer-zone-row>
          </template>

          <!-- Notas: siempre visible el header (para el botón Agregar), y
               colapsable con chevron igual que Datos administrativos. -->
          <drawer-section-header
            label="Notas"
            :count="incidentNotas.length"
            collapsible
            :expanded="notasOpen"
            @toggle="notasOpen = !notasOpen"
          >
            <template #action v-if="canAddIncidentNote">
              <button
                type="button"
                class="incident-notes__add-btn"
                @click.stop="openAddNoteDialog"
              >
                <v-icon x-small class="mr-1">mdi-plus</v-icon>Agregar
              </button>
            </template>
          </drawer-section-header>
          <div v-if="notasOpen && incidentNotas.length" class="incident-notes__list">
            <div v-for="(nota, i) in incidentNotas" :key="'n' + i" class="incident-note">
              <div class="incident-note__meta">
                <strong v-if="nota.corporacion" class="incident-note__corp">{{ nota.corporacion }}</strong>
                <span v-if="nota.autor" class="incident-note__autor">·&nbsp;{{ nota.autor }}</span>
                <span v-if="nota.fecha" class="incident-note__time">{{ nota.fecha }}</span>
              </div>
              <div class="incident-note__msg">{{ nota.msg }}</div>
            </div>
          </div>
          <div v-else-if="notasOpen" class="incident-notes__empty">Sin notas registradas</div>

          <!-- Razonamientos. -->
          <template v-if="incidentRazonamientos.length">
            <drawer-section-header
              label="Razonamientos"
              :count="incidentRazonamientos.length"
            />
            <div class="incident-notes__list">
              <div v-for="(r, i) in incidentRazonamientos" :key="'r' + i" class="incident-note">
                <div class="incident-note__time">
                  <strong>{{ r.corporacion || 'Sin corporación' }}</strong>
                  <span v-if="r.fecha"> · {{ r.fecha }}</span>
                </div>
                <div v-if="r.motivo" class="incident-note__msg">{{ r.motivo }}</div>
                <div v-if="r.nota" class="incident-note__msg incident-note__msg--dim">Nota: {{ r.nota }}</div>
              </div>
            </div>
          </template>
        </div>

        <!-- ZONA 4 — Administrativa. -->
        <div class="drawer-zone drawer-zone--4">
          <drawer-section-header
            label="Datos administrativos"
            :collapsible="true"
            :expanded="zone4Expanded"
            @toggle="zone4Expanded = !zone4Expanded"
          />
          <div v-show="zone4Expanded" class="drawer-zone__content">
            <drawer-zone-row zone="zone-4" label="Grupo" :value="data.groupName || 'INCIDENTES'" />
            <drawer-zone-row
              v-if="data.categoryData && data.categoryData.name"
              zone="zone-4"
              label="Categoría"
              :value="data.categoryData.name"
            />
            <drawer-zone-row
              v-if="incidentCorporacionesText"
              zone="zone-4"
              label="Corporaciones asignadas"
              :value="incidentCorporacionesText"
            />
            <drawer-zone-row
              v-if="incidentAttrs.referencias"
              zone="zone-4"
              label="Referencias"
              :value="incidentAttrs.referencias"
            />
            <drawer-zone-row
              v-if="incidentAttrs.folio"
              zone="zone-4"
              label="Folio"
              :value="incidentAttrs.folio"
            />
            <drawer-zone-row
              v-if="incidentAttrs.operador"
              zone="zone-4"
              label="Operador catalogador"
              :value="incidentAttrs.operador"
            />
            <drawer-zone-row
              zone="zone-4"
              label="Fecha completa"
              :value="formatFullDate(incidentAttrs.fecha || data.createdAt)"
            />
            <drawer-zone-row
              zone="zone-4"
              label="Última actualización"
              :value="formatFullDate(data.lastUpdate || data.createdAt)"
            />
          </div>
        </div>
      </template>

      <!-- Sensor (Botón de Pánico) — DRAWERS-VARIANTS-SPEC §3 -->
      <template v-else-if="entityType === 'sensor'">
        <!-- ZONA 1 — Crítica. -->
        <div class="drawer-zone drawer-zone--1">
          <drawer-zone-row zone="zone-1" label="Identificador" :value="data.name || '—'" />
          <drawer-zone-row
            zone="zone-1"
            label="Estado"
            :value="sensorStatusText"
            :value-color="sensorStatusColor"
          />
          <drawer-zone-row
            v-if="sensorHasActiveAlert && sensorTiempoActivacion"
            zone="zone-1"
            label="Tiempo desde activación"
            :value="sensorTiempoActivacion"
            value-color="var(--kln-alert-danger)"
          />
          <drawer-zone-row
            v-if="data.longitude || (data.position && data.position.longitude)"
            zone="zone-1"
            label="Ubicación"
            :value="sensorUbicacion"
          >
            <template #action>
              <drawer-cross-ref-button
                icon="mdi-crosshairs-gps"
                tooltip="Centrar en mapa"
                @click="$emit('center', item)"
              />
            </template>
          </drawer-zone-row>
          <drawer-zone-row
            v-if="data.fiscalName"
            zone="zone-1"
            label="Establecimiento"
            :value="sensorEstablecimiento"
          />
        </div>

        <!-- ZONA 2 — Acción primaria modulada por estado. -->
        <div v-if="sensorPrimaryAction || sensorActionContext" class="drawer-zone drawer-zone--2">
          <div
            class="drawer-zone__context"
            :class="{ 'drawer-zone__context--alert': sensorHasActiveAlert }"
          >
            <v-icon
              v-if="sensorHasActiveAlert"
              x-small
              color="var(--kln-alert-danger)"
              class="mr-1"
            >mdi-alert</v-icon>
            {{ sensorActionContext }}
          </div>
          <drawer-action-button
            v-if="sensorPrimaryAction"
            variant="primary"
            :label="sensorPrimaryAction.label"
            :icon="sensorPrimaryAction.icon"
            :tone="sensorPrimaryAction.tone"
            @click="$emit(sensorPrimaryAction.event, item)"
          />
        </div>

        <!-- ZONA 3 — Contextual: recursos cercanos críticos para sensor.
             El bloque de Recursos Cercanos vive como sección compartida
             fuera de esta variante; MapV2 lo inyecta y el slider del radio
             se maneja por el mismo flujo nearby. -->

        <!-- ZONA 4 — Administrativa. -->
        <div class="drawer-zone drawer-zone--4">
          <drawer-section-header
            label="Datos administrativos"
            :collapsible="true"
            :expanded="zone4Expanded"
            @toggle="zone4Expanded = !zone4Expanded"
          />
          <div v-show="zone4Expanded" class="drawer-zone__content">
            <drawer-zone-row v-if="data.groupName" zone="zone-4" label="Grupo" :value="data.groupName">
              <template v-if="getSensorGroupColor()" #action>
                <span class="group-color-dot" :style="{ backgroundColor: getSensorGroupColor() }" />
              </template>
            </drawer-zone-row>
            <drawer-zone-row v-if="data.fiscalName" zone="zone-4" label="Nombre fiscal" :value="data.fiscalName" />
            <drawer-zone-row v-if="data.address" zone="zone-4" label="Dirección" :value="data.address" />
            <drawer-zone-row v-if="data.neighborhood" zone="zone-4" label="Colonia" :value="data.neighborhood" />
            <drawer-zone-row v-if="data.city" zone="zone-4" label="Municipio" :value="data.city" />
            <drawer-zone-row v-if="data.phone" zone="zone-4" label="Teléfono" :value="data.phone">
              <template #action>
                <drawer-cross-ref-button
                  icon="mdi-phone-outline"
                  tooltip="Llamar al establecimiento"
                  @click="onCallSensorPhone"
                />
              </template>
            </drawer-zone-row>
            <drawer-zone-row v-if="data.contact" zone="zone-4" label="Contacto" :value="data.contact" />
            <drawer-zone-row v-if="data.contactJob" zone="zone-4" label="Puesto del contacto" :value="data.contactJob" />
            <drawer-zone-row v-if="data.contactPhone" zone="zone-4" label="Tel. contacto" :value="data.contactPhone">
              <template #action>
                <drawer-cross-ref-button
                  icon="mdi-phone-outline"
                  tooltip="Llamar al contacto"
                  @click="onCallSensorContactPhone"
                />
              </template>
            </drawer-zone-row>
            <drawer-zone-row v-if="data.contactEmail" zone="zone-4" label="Correo contacto" :value="data.contactEmail" />
            <drawer-zone-row zone="zone-4" label="Última actualización" :value="lastUpdate" />
          </div>
        </div>
      </template>

      <!-- SOS Call — DRAWERS-VARIANTS-SPEC §4 (el drawer más crítico) -->
      <template v-else-if="entityType === 'sos_call'">
        <!-- ZONA 1 — Crítica. Énfasis en urgencia y tiempo. -->
        <div class="drawer-zone drawer-zone--1">
          <drawer-zone-row zone="zone-1" label="Llamante" :value="sosCallerName">
            <template #action>
              <drawer-cross-ref-button
                v-if="puedeMurphy && sosHasCallerName"
                icon="mdi-card-account-details-outline"
                tooltip="Consultar persona en Murphy"
                @click="onSearchSosCallerMurphy"
              />
            </template>
          </drawer-zone-row>
          <drawer-zone-row zone="zone-1" label="Tipo de llamada" :value="_sosType()" />
          <drawer-zone-row
            zone="zone-1"
            label="Estado"
            :value="sosStatusText"
            :value-color="sosStatusColor"
          />
          <drawer-zone-row
            v-if="sosTiempoTranscurrido"
            zone="zone-1"
            label="Tiempo transcurrido"
            :value="sosTiempoTranscurrido"
            :value-color="sosIsWaiting ? 'var(--kln-alert-danger)' : undefined"
          />
          <drawer-zone-row
            v-if="sosLastPosition"
            zone="zone-1"
            label="Posición actual"
            :value="sosPosicion"
            :flash-key="sosPosicionFlashKey"
          >
            <template #action>
              <drawer-cross-ref-button
                icon="mdi-crosshairs-gps"
                tooltip="Centrar en mapa"
                @click="$emit('center', item)"
              />
            </template>
          </drawer-zone-row>
          <drawer-zone-row
            v-if="sosDireccion"
            zone="zone-1"
            label="Dirección"
            :value="sosDireccion"
          />
        </div>

        <!-- ZONA 2 — Acción crítica según estado. -->
        <div v-if="sosPrimaryAction || sosActionContext" class="drawer-zone drawer-zone--2">
          <div
            class="drawer-zone__context"
            :class="{ 'drawer-zone__context--alert': sosIsWaiting }"
          >
            <v-icon
              v-if="sosIsWaiting"
              x-small
              color="var(--kln-alert-danger)"
              class="mr-1"
            >mdi-phone-alert</v-icon>
            {{ sosActionContext }}
          </div>
          <drawer-action-button
            v-if="sosPrimaryAction"
            variant="primary"
            :label="sosPrimaryAction.label"
            :icon="sosPrimaryAction.icon"
            :tone="sosPrimaryAction.tone"
            :class="['drawer-action-btn--white', { 'drawer-action-btn--big': sosPrimaryAction.big }]"
            @click="onSosPrimaryAction"
          />
        </div>

        <!-- ZONA 3 — Contextual: recursos cercanos (siempre relevante para
             SOS activo) + trayectoria del reportante si hay historial. -->
        <!-- Los recursos cercanos vienen como sección compartida de MapV2. -->

        <!-- ZONA 4 — Administrativa. -->
        <div class="drawer-zone drawer-zone--4">
          <drawer-section-header
            label="Datos administrativos"
            :collapsible="true"
            :expanded="zone4Expanded"
            @toggle="zone4Expanded = !zone4Expanded"
          />
          <div v-show="zone4Expanded" class="drawer-zone__content">
            <drawer-zone-row zone="zone-4" label="Sistema" :value="_sosSystem()" />
            <drawer-zone-row zone="zone-4" label="Teléfono" :value="_sosPhone()">
              <template #action>
                <drawer-cross-ref-button
                  v-if="_sosPhone() !== '—'"
                  icon="mdi-phone-outline"
                  tooltip="Llamar al número"
                  @click="onCallSosPhone"
                />
              </template>
            </drawer-zone-row>
            <drawer-zone-row
              v-if="_sosData().cadId"
              zone="zone-4"
              label="Folio CAD"
              :value="_sosData().cadId"
            />
            <drawer-zone-row
              v-if="_sosData().userData && _sosData().userData.email"
              zone="zone-4"
              label="Correo"
              :value="_sosData().userData.email"
            />
            <drawer-zone-row zone="zone-4" label="Inicio" :value="lastUpdate" />
          </div>
        </div>
      </template>

      <!-- Hit (iSentry, Acuseek, hitLpr, hitMandamiento, hitRoboVehiculo)
           Para hitLpr portamos los campos del fieldsMap.hitLpr legacy:
           FECHA, PLACA, TIPO DE VEHICULO, ARCO, DIRECCION, CARRIL, MOTIVO
           + la foto del vehiculo capturada por el ARCO. Para otros subtipos
           se mantiene el render generico con subtitulo/tipo. -->
      <template v-else-if="entityType === 'hit'">
        <!-- Medida cautelar: persona con brazalete GPS por orden judicial.
             Solo identificadores y el motivo — los generales de la persona
             (CURP, domicilio) nunca viajan al frontend (LFPDPPP). -->
        <template v-if="hitIsMedidaCautelar">
          <div class="drawer-zone drawer-zone--1">
            <drawer-zone-row zone="zone-1" label="Motivo" :value="mcMotivo" value-color="var(--kln-alert-danger)" />
            <drawer-zone-row zone="zone-1" label="Persona" :value="mcPersonaNombre" />
            <drawer-zone-row zone="zone-1" label="Estado" :value="mcEstadoAlerta" />
            <drawer-zone-row zone="zone-1" label="Fecha" :value="lastUpdate" />
          </div>
        </template>
        <template v-else-if="hitIsLpr">
          <div class="drawer-zone drawer-zone--1">
            <div v-if="hitLprFoto" class="hit-photo-row">
              <img
                :src="hitLprFoto"
                class="hit-photo"
                alt="Captura LPR"
                @click="openHitPhoto(hitLprFoto)"
              />
            </div>
            <drawer-zone-row zone="zone-1" label="Fecha" :value="hitLprFecha" />
            <drawer-zone-row zone="zone-1" label="Placa" :value="hitLprPlaca">
              <template #action>
                <drawer-cross-ref-button
                  v-if="puedeMurphy && hitLprPlaca && hitLprPlaca !== '—'"
                  icon="mdi-card-account-details-outline"
                  tooltip="Buscar placa en Murphy"
                  @click="onSearchPlacaMurphy(hitLprPlaca)"
                />
              </template>
            </drawer-zone-row>
            <drawer-zone-row zone="zone-1" label="Tipo de vehículo" :value="hitLprTipoVehiculo" />
            <drawer-zone-row zone="zone-1" label="Arco" :value="hitLprArco" />
            <drawer-zone-row zone="zone-1" label="Dirección" :value="hitLprDireccion" />
            <drawer-zone-row zone="zone-1" label="Carril" :value="hitLprCarril" />
            <drawer-zone-row zone="zone-1" label="Motivo" :value="hitLprMotivo" />
          </div>
          <!-- Lightbox simple: si el operador clickea la foto, la mostramos
               full-screen hasta que haga click fuera o presione Escape. -->
          <div v-if="hitPhotoZoomSrc" class="hit-photo-zoom" @click.self="closeHitPhoto">
            <v-btn icon small class="hit-photo-zoom__close" @click="closeHitPhoto">
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <img :src="hitPhotoZoomSrc" class="hit-photo-zoom__img" @click.stop="closeHitPhoto" />
          </div>
        </template>
        <template v-else>
          <div class="drawer-zone drawer-zone--1">
            <drawer-zone-row zone="zone-1" label="Tipo" :value="monitor.subtitle2 || monitor.typeOfItem || '—'" />
            <drawer-zone-row zone="zone-1" label="Detección" :value="monitor.subtitle || '—'" />
            <drawer-zone-row zone="zone-1" label="Dispositivo" :value="monitor.title || '—'" />
            <drawer-zone-row v-if="data.data && data.data.alertCategory" zone="zone-1" label="Categoría" :value="data.data.alertCategory" />
            <drawer-zone-row v-if="data.data && data.data.priority" zone="zone-1" label="Prioridad" :value="data.data.priority" />
            <drawer-zone-row v-if="data.deviceId" zone="zone-1" label="ID dispositivo" :value="data.deviceId" />
            <drawer-zone-row zone="zone-1" label="Fecha" :value="lastUpdate" />
          </div>
        </template>

        <!-- Lista negra — el backend adjunta extendedData.blacklist cuando la
             placa detectada coincide con una entrada activa. Va fuera del
             bloque LPR para cubrir también hits externos que traigan match.
             Header en variante `alert`: es la info que decide si el hit
             amerita despacho. -->
        <template v-if="hitBlacklist">
          <drawer-section-header label="Lista negra" alert />
          <div class="drawer-zone drawer-zone--1">
            <!-- Label "Categoría" (no "Motivo"): el bloque LPR de arriba ya
                 tiene su propia fila MOTIVO y dos labels iguales confunden. -->
            <drawer-zone-row
              zone="zone-1"
              label="Categoría"
              :value="hitBlacklistCategoria"
              value-color="var(--kln-alert-danger)"
            />
            <drawer-zone-row
              v-if="hitBlacklistPlacaDistinta"
              zone="zone-4"
              label="Placa en lista"
              :value="hitBlacklistPlacaDistinta"
            />
            <drawer-zone-row zone="zone-4" label="Vehículo" :value="hitBlacklistVehiculo" />
            <drawer-zone-row zone="zone-4" label="Color" :value="hitBlacklistColor" />
            <drawer-zone-row zone="zone-4-stacked" label="VIN" :value="hitBlacklistVin" />
            <drawer-zone-row
              v-if="hitBlacklistCoincidencias"
              zone="zone-4"
              label="Detecciones previas"
              :value="hitBlacklistCoincidencias"
            />
            <drawer-zone-row
              v-if="hitBlacklistJustificacion"
              zone="zone-4-stacked"
              label="Antecedente"
              :value="hitBlacklistJustificacion"
            />
          </div>
        </template>

        <!-- Razonamiento — el operador clasifica el hit (positivo / falso
             positivo / positivo expirado) sin salir del mapa. Misma acción
             y mismos tipos que la vista Analytics Monitor. -->
        <template v-if="hitRecordId">
          <drawer-section-header label="Razonamiento">
            <template #action>
              <button
                type="button"
                class="incident-notes__add-btn"
                @click.stop="openRazonamientoDialog"
              >
                <v-icon x-small class="mr-1">{{ hitRazonamiento ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
                {{ hitRazonamiento ? 'Editar' : 'Agregar' }}
              </button>
            </template>
          </drawer-section-header>
          <div v-if="hitRazonamiento" class="drawer-zone drawer-zone--1">
            <div class="hit-raz">
              <span
                v-if="hitRazonamientoTipoMeta"
                class="hit-raz__tipo"
                :style="{ color: hitRazonamientoTipoMeta.color, borderColor: hitRazonamientoTipoMeta.color }"
              >{{ hitRazonamientoTipoMeta.name }}</span>
              <p class="hit-raz__text">{{ hitRazonamiento.razonamiento }}</p>
              <span v-if="hitRazonamientoAutor" class="hit-raz__by">— {{ hitRazonamientoAutor }}</span>
            </div>
          </div>
          <div v-else class="drawer-section-empty">Sin razonamiento registrado.</div>
        </template>
      </template>

      <!-- Reportante (RAPID-SOS / DIDI / CARBYNE / uber) — replica los campos
           de fieldsMap.reportant en src/store/modules/map.js del mapa viejo.
           Lee directamente del payload crudo (positions, additionalData). -->
      <template v-else-if="entityType === 'reportant'">
        <div class="drawer-zone drawer-zone--1">
          <drawer-zone-row
            zone="zone-1"
            label="Teléfono"
            :value="reportantPhone"
            html
          >
            <template #action>
              <drawer-cross-ref-button
                v-if="reportantPhoneRaw"
                icon="mdi-phone-outline"
                tooltip="Llamar al reportante"
                @click="onCallReportantPhone"
              />
            </template>
          </drawer-zone-row>
          <drawer-zone-row
            zone="zone-1"
            label="Ubicación"
            :value="reportantCoordsLink"
            html
          >
            <template #action>
              <drawer-cross-ref-button
                icon="mdi-crosshairs-gps"
                tooltip="Centrar en mapa"
                @click="$emit('center', item)"
              />
            </template>
          </drawer-zone-row>
          <drawer-zone-row zone="zone-1" label="Dirección" :value="reportantDireccion" />
          <!-- Fuente con logo del proveedor (Carbyne / DiDi / Uber /
               RapidSOS). Replica la columna FUENTE del fieldsMap.reportant
               legacy que mostraba `<img src="rapidsos.png">` o el de
               Carbyne. Estilos locales (`.partner-fuente-row`) replican el
               look de zone-1 (label uppercase pequeño + valor a 14px). NO
               usamos .drawer-zone-row__* aquí porque esas son clases
               scoped del child component DrawerZoneRow y no aplican
               cross-component. -->
          <div v-if="reportantPartner" class="partner-fuente-row">
            <span class="partner-fuente-row__label">Fuente</span>
            <div class="partner-fuente-row__value">
              <img
                :src="reportantPartner.logo"
                :alt="reportantPartner.label"
                class="partner-fuente-row__logo"
              />
              <span class="partner-fuente-row__name">{{ reportantPartner.label }}</span>
            </div>
          </div>
          <drawer-zone-row zone="zone-1" label="Actualización" :value="reportantLastUpdate" />
        </div>

        <div v-if="reportantUber || reportantDidi" class="drawer-zone drawer-zone--3">
          <drawer-section-header label="Detalle del sistema" />
          <detail-row v-if="reportantUber" label="Uber" :value="reportantUber" icon="mdi-car" html />
          <detail-row v-if="reportantDidi" label="DIDI" :value="reportantDidi" icon="mdi-car" html />
        </div>

        <div class="drawer-zone drawer-zone--4">
          <drawer-section-header
            label="Datos administrativos"
            :collapsible="true"
            :expanded="zone4Expanded"
            @toggle="zone4Expanded = !zone4Expanded"
          />
          <div v-show="zone4Expanded" class="drawer-zone__content">
            <drawer-zone-row
              zone="zone-4"
              label="Fecha de llamada"
              :value="reportantCallStart"
              :fresh="reportantCallStartRelative"
            />
            <drawer-zone-row zone="zone-4" label="Folio CAD" :value="reportantCadId" />
            <drawer-zone-row zone="zone-4" label="Municipio" :value="reportantMunicipio" />
            <drawer-zone-row zone="zone-4" label="Población" :value="reportantPoblacion" />
            <drawer-zone-row zone="zone-4" label="Colonia" :value="reportantColonia" />
            <drawer-zone-row zone="zone-4" label="CP" :value="reportantCP" />
          </div>
        </div>
      </template>

      <!-- Sismo (Katrina / WS weather). El payload del backend usa campos
           en español: fecha, hora, magnitud, lugar, latitud, longitud. -->
      <template v-else-if="entityType === 'sismo'">
        <div class="drawer-zone drawer-zone--1">
          <drawer-zone-row
            zone="zone-1"
            label="Fecha"
            :value="(data.fecha || '') + (data.hora ? ' ' + data.hora : '') || '—'"
          />
          <drawer-zone-row zone="zone-1" label="Magnitud (Richter)" :value="data.magnitud != null ? String(data.magnitud) : '—'" />
          <drawer-zone-row zone="zone-1" label="Lugar" :value="data.lugar || '—'" />
          <drawer-zone-row zone="zone-1" label="Coordenadas" :value="sismoCoords">
            <template #action>
              <drawer-cross-ref-button
                icon="mdi-crosshairs-gps"
                tooltip="Centrar en mapa"
                @click="$emit('center', item)"
              />
            </template>
          </drawer-zone-row>
        </div>
      </template>

      <!-- Presa (Katrina / WS weather). Campos en inglés: municipality,
           elevation, fill, percentageNamofill, namoElev/Fill, nameElev/Fill, etc. -->
      <template v-else-if="entityType === 'presa'">
        <div class="drawer-zone drawer-zone--1">
          <drawer-zone-row zone="zone-1" label="Nombre" :value="data.name || '—'" />
          <drawer-zone-row zone="zone-1" label="Municipio" :value="data.municipality || '—'" />
          <drawer-zone-row v-if="data.elevation != null" zone="zone-1" label="Elevación actual" :value="data.elevation + ' msnm'" />
          <drawer-zone-row v-if="data.fill != null" zone="zone-1" label="Almacenamiento actual" :value="data.fill + ' hm³'" />
          <drawer-zone-row v-if="data.percentageNamofill != null" zone="zone-1" label="% de llenado (NAMO)" :value="Number(data.percentageNamofill).toFixed(0) + ' %'" />
        </div>
        <div class="drawer-zone drawer-zone--4">
          <drawer-section-header
            label="Datos administrativos"
            :collapsible="true"
            :expanded="zone4Expanded"
            @toggle="zone4Expanded = !zone4Expanded"
          />
          <div v-show="zone4Expanded" class="drawer-zone__content">
            <drawer-zone-row v-if="data.namoElev != null || data.namoFill != null" zone="zone-4" label="NAMO" :value="presaNamo" html />
            <drawer-zone-row v-if="data.nameElev != null || data.nameFill != null" zone="zone-4" label="NAME" :value="presaName" html />
            <drawer-zone-row v-if="data.spillwayType" zone="zone-4" label="Tipo vertedor" :value="data.spillwayType" />
            <drawer-zone-row v-if="data.opStart" zone="zone-4" label="Inicio de operación" :value="data.opStart" />
            <drawer-zone-row v-if="data.curtainHeight != null" zone="zone-4" label="Altura de cortina" :value="data.curtainHeight + ' m'" />
            <drawer-zone-row v-if="data.freeBoard != null" zone="zone-4" label="Bordo libre" :value="data.freeBoard + ' msnm'" />
            <drawer-zone-row v-if="data.crownElev != null" zone="zone-4" label="Elevación corona" :value="data.crownElev + ' msnm'" />
            <drawer-zone-row v-if="data.use" zone="zone-4" label="Uso" :value="data.use" />
          </div>
        </div>
      </template>

      <!-- Fire (Katrina / WS weather). Payload en inglés: region, state,
           status, municipality, classification, controlPercentage, etc. -->
      <template v-else-if="entityType === 'fire'">
        <div class="drawer-zone drawer-zone--1">
          <drawer-zone-row zone="zone-1" label="Status" :value="data.status || '—'" />
          <drawer-zone-row v-if="data.controlPercentage != null" zone="zone-1" label="% de control" :value="data.controlPercentage + ' %'" />
          <drawer-zone-row v-if="data.settlementPercentage != null" zone="zone-1" label="% de liquidación" :value="data.settlementPercentage + ' %'" />
          <drawer-zone-row v-if="data.startDate" zone="zone-1" label="Fecha de inicio" :value="formatFullDate(data.startDate)" />
          <drawer-zone-row zone="zone-1" label="Municipio" :value="data.municipality || '—'" />
          <drawer-zone-row zone="zone-1" label="Estado" :value="data.state || '—'" />
          <drawer-zone-row zone="zone-1" label="Región" :value="data.region || '—'" />
        </div>
        <div class="drawer-zone drawer-zone--4">
          <drawer-section-header
            label="Datos administrativos"
            :collapsible="true"
            :expanded="zone4Expanded"
            @toggle="zone4Expanded = !zone4Expanded"
          />
          <div v-show="zone4Expanded" class="drawer-zone__content">
            <drawer-zone-row v-if="data.predio" zone="zone-4" label="Predio" :value="data.predio" />
            <drawer-zone-row v-if="data.classification" zone="zone-4" label="Clasificación" :value="data.classification" />
            <drawer-zone-row v-if="data.classificationIFAE" zone="zone-4" label="Clasificación IFAE" :value="data.classificationIFAE" />
          </div>
        </div>
      </template>

      <!-- CPR / Centro Penitenciario — data estática del EdoMéx (22 centros
           hardcoded en data/cprs-data.js). Muestra población, distribución
           por sexo y presencia de grupos delictivos reportada en la zona. -->
      <template v-else-if="entityType === 'cpr'">
        <div class="drawer-zone drawer-zone--1">
          <drawer-zone-row zone="zone-1" label="Ubicación" :value="cprData.ubicacion || '—'">
            <template #action>
              <drawer-cross-ref-button
                icon="mdi-crosshairs-gps"
                tooltip="Centrar en mapa"
                @click="$emit('center', item)"
              />
            </template>
          </drawer-zone-row>
          <drawer-zone-row zone="zone-1" label="Población total" :value="cprPoblacion" />
          <drawer-zone-row v-if="cprData.hombres != null" zone="zone-1" label="Hombres" :value="formatNumber(cprData.hombres)" />
          <drawer-zone-row v-if="cprData.mujeres != null" zone="zone-1" label="Mujeres" :value="formatNumber(cprData.mujeres)" />
        </div>
        <div v-if="cprGruposDelictivos" class="drawer-zone drawer-zone--3">
          <drawer-section-header label="Grupos delictivos en la zona" :alert="true" />
          <drawer-zone-row zone="zone-2" label="Reportados" :value="cprGruposDelictivos" value-color="var(--kln-alert-danger)" />
        </div>
      </template>

      <!-- KPI / Dashboard por geocerca — KPI-DRAWER-REDISEÑO §4 -->
      <template v-else-if="entityType === 'kpi'">
        <!-- Estado de carga inicial (no refresh) -->
        <div v-if="kpiLoading" class="kpi-loading">
          <v-progress-circular indeterminate size="40" width="3" color="var(--kln-kpi-publico)" />
          <span class="kpi-loading__text">Calculando KPIs...</span>
        </div>
        <template v-if="!kpiLoading">

        <!-- Contexto: chip de alcance + frescura + refresh -->
        <div class="kpi-context">
          <div class="kpi-context__scope">
            <v-icon x-small class="mr-1" color="var(--kln-text-muted)">{{ kpiScopeIcon }}</v-icon>
            <span class="kpi-context__scope-label" :title="kpiScopeLabel">{{ kpiScopeLabel }}</span>
          </div>
          <div class="kpi-context__freshness">
            <span :class="kpiFreshnessStale ? 'kpi-freshness kpi-freshness--stale' : 'kpi-freshness'">
              {{ kpiFreshnessText }}
            </span>
            <v-btn
              icon
              x-small
              class="kpi-context__refresh"
              v-auto-tooltip="'Actualizar KPIs'"
              @click="onKpiRefresh"
            >
              <v-icon x-small>mdi-refresh</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Ficha del encargado (mando unico: region / sector / cuadrante) -->
        <div v-if="kpiEncargadoVisible" class="kpi-section kpi-encargado">
          <drawer-section-header :label="kpiEncargadoLabel" />
          <div class="kpi-encargado__body">
            <div class="kpi-encargado__photo-wrap">
              <img
                v-if="kpiEncargadoPhoto"
                :src="kpiEncargadoPhoto"
                :alt="kpiEncargadoNombre"
                class="kpi-encargado__photo"
                @error="onKpiEncargadoPhotoError"
              />
              <div v-else class="kpi-encargado__photo kpi-encargado__photo--fallback">
                <v-icon size="32" color="var(--kln-text-dim)">mdi-account</v-icon>
              </div>
            </div>
            <div class="kpi-encargado__info">
              <drawer-zone-row zone="zone-2" label="Comisario" :value="kpiEncargadoNombre" />
              <drawer-zone-row zone="zone-2" label="Unidad administrativa" :value="kpiEncargadoUnidadAdmin" />
              <drawer-zone-row zone="zone-2" label="Adscripción" :value="kpiEncargadoAdscripcion" />
              <drawer-zone-row zone="zone-2" label="Categoría" :value="kpiEncargadoCategoria" />
            </div>
          </div>
        </div>

        <!-- Sección Incidentes (4 cards accionables) -->
        <div class="kpi-section kpi-incidents">
          <div class="kpi-incidents__header">
            <span class="kpi-incidents__title">Incidentes — últimas 24 h</span>
          </div>
          <div class="kpi-incidents__grid">
            <button
              type="button"
              class="kpi-card kpi-card--seguridad"
              :class="{ 'kpi-card--active': kpiCategoryFilter === 'seguridad' }"
              @click="onKpiCardClick('seguridad')"
            >
              <v-icon size="20" class="kpi-card__icon">mdi-shield-alert</v-icon>
              <div class="kpi-card__value">{{ kpiIncidents.seguridad || 0 }}</div>
              <div class="kpi-card__label">Seguridad</div>
            </button>
            <button
              type="button"
              class="kpi-card kpi-card--medico"
              :class="{ 'kpi-card--active': kpiCategoryFilter === 'medico' }"
              @click="onKpiCardClick('medico')"
            >
              <v-icon size="20" class="kpi-card__icon">mdi-medical-bag</v-icon>
              <div class="kpi-card__value">{{ kpiIncidents.medico || 0 }}</div>
              <div class="kpi-card__label">Médicos</div>
            </button>
            <button
              type="button"
              class="kpi-card kpi-card--pcivil"
              :class="{ 'kpi-card--active': kpiCategoryFilter === 'proteccion_civil' }"
              @click="onKpiCardClick('proteccion_civil')"
            >
              <v-icon size="20" class="kpi-card__icon">mdi-home-alert</v-icon>
              <div class="kpi-card__value">{{ kpiIncidents.proteccion_civil || 0 }}</div>
              <div class="kpi-card__label">P. Civil</div>
            </button>
            <button
              type="button"
              class="kpi-card kpi-card--publico"
              :class="{ 'kpi-card--active': kpiCategoryFilter === 'servicios_publicos' }"
              @click="onKpiCardClick('servicios_publicos')"
            >
              <v-icon size="20" class="kpi-card__icon">mdi-wrench</v-icon>
              <div class="kpi-card__value">{{ kpiIncidents.servicios_publicos || 0 }}</div>
              <div class="kpi-card__label">Serv. Públicos</div>
            </button>
          </div>
          <div class="kpi-incidents__total">
            <span class="kpi-incidents__total-label">Total</span>
            <span class="kpi-incidents__total-value">{{ kpiIncidents.total || 0 }}</span>
          </div>
        </div>

        <!-- Sección Carga Operativa (colapsable, estados granulares) -->
        <div class="kpi-section kpi-load">
          <drawer-section-header
            :label="kpiLoadHeaderLabel"
            :collapsible="true"
            :expanded="kpiLoadExpanded"
            :alert="kpiLoadHasAlert"
            @toggle="kpiLoadExpanded = !kpiLoadExpanded"
          />
          <div v-show="kpiLoadExpanded" class="kpi-load__body">

          <!-- Unidades: 4 estados (online/offline/unknown/down) -->
          <div class="kpi-resource" :class="{ 'kpi-resource--alert': (kpiSensors.alert || 0) > 0 && false }">
            <div class="kpi-resource__header">
              <v-icon x-small class="kpi-resource__icon">mdi-car-emergency</v-icon>
              <span class="kpi-resource__name">Unidades</span>
              <span class="kpi-resource__primary">{{ (kpiDevices.online || 0).toLocaleString('es-MX') }} en línea</span>
            </div>
            <div class="kpi-stacked-bar" :title="kpiDevicesBarTitle">
              <span class="kpi-stacked-bar__seg kpi-stacked-bar__seg--online" :style="{ flexBasis: kpiDevicesPct.online + '%' }" />
              <span class="kpi-stacked-bar__seg kpi-stacked-bar__seg--alert" :style="{ flexBasis: kpiDevicesPct.offline + '%' }" />
              <span class="kpi-stacked-bar__seg kpi-stacked-bar__seg--muted" :style="{ flexBasis: kpiDevicesPct.unknown + '%' }" />
              <span class="kpi-stacked-bar__seg kpi-stacked-bar__seg--down" :style="{ flexBasis: kpiDevicesPct.down + '%' }" />
            </div>
            <div class="kpi-resource__legend">
              {{ (kpiDevices.online || 0).toLocaleString('es-MX') }} en línea ·
              {{ (kpiDevices.offline || 0).toLocaleString('es-MX') }} fuera ·
              {{ (kpiDevices.unknown || 0).toLocaleString('es-MX') }} sin señal ·
              {{ (kpiDevices.down || 0).toLocaleString('es-MX') }} inactivas
            </div>
          </div>

          <!-- Transporte: 3 estados (up/alert/down) con destaque si hay alert -->
          <div class="kpi-resource" :class="{ 'kpi-resource--alert': (kpiBuses.alert || 0) > 0 }">
            <div class="kpi-resource__header">
              <v-icon x-small class="kpi-resource__icon">mdi-bus</v-icon>
              <span class="kpi-resource__name">Transporte</span>
              <span class="kpi-resource__primary">{{ (kpiBuses.up || 0).toLocaleString('es-MX') }} operando</span>
            </div>
            <div class="kpi-stacked-bar" :title="kpiBusesBarTitle">
              <span class="kpi-stacked-bar__seg kpi-stacked-bar__seg--online" :style="{ flexBasis: kpiBusesPct.up + '%' }" />
              <span class="kpi-stacked-bar__seg kpi-stacked-bar__seg--warning" :style="{ flexBasis: kpiBusesPct.alert + '%' }" />
              <span class="kpi-stacked-bar__seg kpi-stacked-bar__seg--down" :style="{ flexBasis: kpiBusesPct.down + '%' }" />
            </div>
            <div class="kpi-resource__legend">
              {{ (kpiBuses.up || 0).toLocaleString('es-MX') }} operando ·
              <span v-if="(kpiBuses.alert || 0) > 0" class="kpi-resource__legend-alert">{{ (kpiBuses.alert || 0).toLocaleString('es-MX') }} en alerta</span>
              <span v-else>{{ (kpiBuses.alert || 0).toLocaleString('es-MX') }} en alerta</span>
              · {{ (kpiBuses.down || 0).toLocaleString('es-MX') }} fuera
            </div>
          </div>

          <!-- Cámaras: 2 estados (up/down) -->
          <div class="kpi-resource">
            <div class="kpi-resource__header">
              <v-icon x-small class="kpi-resource__icon">mdi-cctv</v-icon>
              <span class="kpi-resource__name">Cámaras</span>
              <span class="kpi-resource__primary">{{ (kpiCameras.up || 0).toLocaleString('es-MX') }} activas</span>
            </div>
            <div class="kpi-stacked-bar" :title="kpiCamerasBarTitle">
              <span class="kpi-stacked-bar__seg kpi-stacked-bar__seg--online" :style="{ flexBasis: kpiCamerasPct.up + '%' }" />
              <span class="kpi-stacked-bar__seg kpi-stacked-bar__seg--down" :style="{ flexBasis: kpiCamerasPct.down + '%' }" />
            </div>
            <div class="kpi-resource__legend">
              {{ (kpiCameras.up || 0).toLocaleString('es-MX') }} activas ·
              {{ (kpiCameras.down || 0).toLocaleString('es-MX') }} sin señal
              <span class="kpi-resource__legend-hint">(incluye sub-cámaras por ubicación)</span>
            </div>
          </div>

          <!-- Sensores: 4 estados (up/alert/reported/down) con destaque si alert>0 -->
          <div class="kpi-resource" :class="{ 'kpi-resource--alert': (kpiSensors.alert || 0) > 0 }">
            <div class="kpi-resource__header">
              <v-icon x-small class="kpi-resource__icon">mdi-alarm-light</v-icon>
              <span class="kpi-resource__name">Sensores</span>
              <span class="kpi-resource__primary">{{ (kpiSensors.up || 0).toLocaleString('es-MX') }} operativos</span>
            </div>
            <div class="kpi-stacked-bar" :title="kpiSensorsBarTitle">
              <span class="kpi-stacked-bar__seg kpi-stacked-bar__seg--online" :style="{ flexBasis: kpiSensorsPct.up + '%' }" />
              <span class="kpi-stacked-bar__seg kpi-stacked-bar__seg--danger" :style="{ flexBasis: kpiSensorsPct.alert + '%' }" />
              <span class="kpi-stacked-bar__seg kpi-stacked-bar__seg--warning" :style="{ flexBasis: kpiSensorsPct.reported + '%' }" />
              <span class="kpi-stacked-bar__seg kpi-stacked-bar__seg--down" :style="{ flexBasis: kpiSensorsPct.down + '%' }" />
            </div>
            <div class="kpi-resource__legend">
              {{ (kpiSensors.up || 0).toLocaleString('es-MX') }} operativos ·
              <span v-if="(kpiSensors.alert || 0) > 0" class="kpi-resource__legend-alert">{{ (kpiSensors.alert || 0).toLocaleString('es-MX') }} en alerta</span>
              <span v-else>{{ (kpiSensors.alert || 0).toLocaleString('es-MX') }} en alerta</span>
              ·
              {{ (kpiSensors.reported || 0).toLocaleString('es-MX') }} reportados ·
              {{ (kpiSensors.down || 0).toLocaleString('es-MX') }} sin señal
            </div>
          </div>

          <!-- LPR: 2 estados (up/down). -->
          <div class="kpi-resource">
            <div class="kpi-resource__header">
              <v-icon x-small class="kpi-resource__icon">mdi-camera-front</v-icon>
              <span class="kpi-resource__name">LPR</span>
              <span class="kpi-resource__primary">{{ (kpiLprs.up || 0).toLocaleString('es-MX') }} activos</span>
            </div>
            <div class="kpi-stacked-bar" :title="kpiLprsBarTitle">
              <span class="kpi-stacked-bar__seg kpi-stacked-bar__seg--online" :style="{ flexBasis: kpiLprsPct.up + '%' }" />
              <span class="kpi-stacked-bar__seg kpi-stacked-bar__seg--down" :style="{ flexBasis: kpiLprsPct.down + '%' }" />
            </div>
            <div class="kpi-resource__legend">
              {{ (kpiLprs.up || 0).toLocaleString('es-MX') }} activos ·
              {{ (kpiLprs.down || 0).toLocaleString('es-MX') }} sin señal
            </div>
          </div>

          <!-- PPLs: dato estático censal (sin barra) -->
          <div class="kpi-resource kpi-resource--static">
            <div class="kpi-resource__header">
              <v-icon x-small class="kpi-resource__icon">mdi-office-building</v-icon>
              <span class="kpi-resource__name">PPLs (censo)</span>
              <span
                class="kpi-resource__primary"
                v-auto-tooltip="'Población penitenciaria. Dato censal de referencia, no refleja estado en tiempo real.'"
              >
                {{ (kpiCprs.total || 0).toLocaleString('es-MX') }}
              </span>
            </div>
          </div>
          </div><!-- end kpi-load__body -->
        </div>

        <!-- Sección Eventos: tabs Críticos / Todos -->
        <div class="kpi-section kpi-events">
          <div class="kpi-events-tabs" role="tablist">
            <button
              type="button"
              class="kpi-events-tab"
              :class="{
                'kpi-events-tab--active': kpiEventsTab === 'critical',
                'kpi-events-tab--alert': kpiEventsTab === 'critical' && kpiCriticalCategories.length,
              }"
              role="tab"
              :aria-selected="String(kpiEventsTab === 'critical')"
              @click="kpiEventsTab = 'critical'"
            >
              <span class="kpi-events-tab__label">Eventos críticos</span>
              <span class="kpi-events-tab__count">{{ kpiCriticalCategories.length }}</span>
            </button>
            <button
              type="button"
              class="kpi-events-tab"
              :class="{ 'kpi-events-tab--active': kpiEventsTab === 'all' }"
              role="tab"
              :aria-selected="String(kpiEventsTab === 'all')"
              @click="kpiEventsTab = 'all'"
            >
              <span class="kpi-events-tab__label">Todos</span>
              <span class="kpi-events-tab__count">{{ kpiCategoriesSorted.length }}</span>
            </button>
          </div>

          <!-- Tab Críticos -->
          <template v-if="kpiEventsTab === 'critical'">
            <div v-if="kpiCriticalCategories.length" class="kpi-cat-list">
              <button
                v-for="cat in kpiCriticalCategories"
                :key="'crit-' + cat.motivo"
                type="button"
                class="kpi-cat-row kpi-cat-row--clickable kpi-cat-row--critical"
                @click="onKpiCategoryRowClick(cat.motivo)"
              >
                <span class="kpi-cat-row__count">{{ cat.count.toLocaleString('es-MX') }}</span>
                <div class="kpi-cat-row__body">
                  <span class="kpi-cat-row__label">{{ cat.motivo }}</span>
                  <span v-if="cat.priorityBreakdown" class="kpi-cat-row__priority">
                    {{ cat.priorityBreakdown }}
                  </span>
                </div>
              </button>
            </div>
            <div v-else class="kpi-empty">
              Sin eventos críticos en las últimas 24 horas.
            </div>
          </template>

          <!-- Tab Todos -->
          <template v-else-if="kpiEventsTab === 'all'">
            <div v-if="kpiCategoriesSorted.length" class="kpi-cat-list">
              <div
                v-for="cat in kpiCategoriesSorted"
                :key="'all-' + cat.motivo"
                class="kpi-cat-row"
                :class="{ 'kpi-cat-row--critical': isCriticalCatCached(cat.motivo) }"
              >
                <span class="kpi-cat-row__count">{{ cat.count.toLocaleString('es-MX') }}</span>
                <span class="kpi-cat-row__label">{{ cat.motivo }}</span>
              </div>
            </div>
            <div v-else class="kpi-empty">
              Sin incidentes en las últimas 24 horas.
            </div>
          </template>
        </div>

        <!-- Fuente SNSP con timestamp de captura -->
        <div class="kpi-fuente">
          <div><b>FUENTE:</b> Servicio de Atención de Llamadas de Emergencia 911, conforme al Catálogo Nacional de Incidentes de Emergencias ver. 3.0, SNSP.</div>
          <div v-if="kpiFetchedAtFormatted" class="kpi-fuente__timestamp">
            Datos obtenidos: {{ kpiFetchedAtFormatted }}
          </div>
        </div>

        </template>
      </template>

      <!-- Generic fallback — DRAWERS-VARIANTS-SPEC §8.3 (4 zonas minimal)
           Cubre entityTypes sin plantilla dedicada (external_sensor,
           external_sensor_route, route_lpr y cualquier futuro). Mantiene
           la misma anatomía de zonas que las variantes principales para
           consistencia visual. -->
      <!-- Persona con medida cautelar en seguimiento activo. El bloque
           genérico mostraba "medida_cautelar" crudo y todo lo demás en "—":
           lo que el operador necesita mientras la sigue es el motivo, el
           estado del brazalete y qué tan fresca es la última señal. -->
      <template v-else-if="entityType === 'medida_cautelar'">
        <div class="drawer-zone drawer-zone--1">
          <drawer-zone-row zone="zone-1" label="Persona" :value="mcPersonaNombre" />
          <drawer-zone-row
            zone="zone-1"
            label="Motivo del seguimiento"
            :value="(item && item.subtitle) || monitor.subtitle || '—'"
            value-color="var(--kln-alert-danger)"
          />
          <drawer-zone-row zone="zone-1" label="Batería del brazalete" :value="mcBateriaTexto" />
          <drawer-zone-row zone="zone-1" label="Última señal" :value="mcUltimaSenal" />
          <drawer-zone-row zone="zone-1" label="Puntos del recorrido" :value="mcPuntosRecorrido" />
          <drawer-zone-row zone="zone-1" label="Ubicación" :value="genericLocation || '—'" />
        </div>
      </template>

      <template v-else>
        <!-- ZONA 1 — Crítica: id, tipo, status, ubicación, frescura. -->
        <div class="drawer-zone drawer-zone--1">
          <drawer-zone-row
            v-if="genericId"
            zone="zone-1"
            label="Identificador"
            :value="genericId"
          />
          <drawer-zone-row
            v-if="entityType"
            zone="zone-1"
            label="Tipo"
            :value="entityType"
          />
          <drawer-zone-row
            v-if="genericStatus"
            zone="zone-1"
            label="Estado"
            :value="genericStatus"
          />
          <drawer-zone-row
            v-if="genericLocation"
            zone="zone-1"
            label="Ubicación"
            :value="genericLocation"
          >
            <template #action>
              <drawer-cross-ref-button
                icon="mdi-crosshairs-gps"
                tooltip="Centrar en mapa"
                @click="$emit('center', item)"
              />
            </template>
          </drawer-zone-row>
          <drawer-zone-row
            v-if="genericFreshness"
            zone="zone-1"
            label="Última actualización"
            :value="genericFreshness"
          />
        </div>

        <!-- ZONA 2 — Acción: contexto + acción primaria si aplica (close-alert). -->
        <div v-if="canCloseAlert" class="drawer-zone drawer-zone--2">
          <div class="drawer-zone__context">
            Alerta pendiente de cierre.
          </div>
          <drawer-action-button
            variant="primary"
            label="Cerrar alerta"
            icon="mdi-check-all"
            tone="danger"
            @click="$emit('close-alert', item)"
          />
        </div>

        <!-- ZONA 3 — Contextual: additionalData parseado como sub-tabla. -->
        <div v-if="genericAdditionalData && genericAdditionalData.length" class="drawer-zone drawer-zone--3">
          <drawer-section-header
            label="Información adicional"
            :count="genericAdditionalData.length"
          />
          <drawer-zone-row
            v-for="(row, i) in genericAdditionalData"
            :key="'add-' + i"
            zone="zone-2"
            :label="row.label"
            :value="row.value"
          />
        </div>

        <!-- ZONA 4 — Administrativa: resto de campos primitivos del raw. -->
        <div v-if="genericZone4Fields.length" class="drawer-zone drawer-zone--4">
          <drawer-section-header
            label="Datos administrativos"
            :collapsible="true"
            :expanded="zone4Expanded"
            @toggle="zone4Expanded = !zone4Expanded"
          />
          <div v-show="zone4Expanded" class="drawer-zone__content">
            <drawer-zone-row
              v-for="(row, i) in genericZone4Fields"
              :key="'z4-' + i"
              zone="zone-4"
              :label="row.label"
              :value="row.value"
            />
          </div>
        </div>
      </template>
      <!-- Universal: sub-cameras for ANY entity type with dev* attributes -->
      <div v-if="pmiCameras.length > 0" class="pmi-cameras">
        <div class="pmi-cameras-header">
          <button
            type="button"
            class="pmi-cameras-title-btn"
            :aria-expanded="String(!pmiCamerasCollapsed)"
            v-auto-tooltip="pmiCamerasCollapsed ? 'Expandir cámaras' : 'Colapsar cámaras'"
            @click="pmiCamerasCollapsed = !pmiCamerasCollapsed"
          >
            <v-icon x-small class="mr-1">{{ pmiCamerasCollapsed ? 'mdi-chevron-right' : 'mdi-chevron-down' }}</v-icon>
            <span class="pmi-cameras-title">Cámaras ({{ pmiCameras.length }})</span>
          </button>
          <div v-if="!pmiCamerasCollapsed" class="pmi-layout-toggle">
            <v-btn x-small icon v-auto-tooltip="'Fijar todas'" @click="pinAllPmiCameras"><v-icon x-small>mdi-pin</v-icon></v-btn>
            <v-btn x-small icon :class="{ 'pmi-layout-active': pmiColumns === 1 }" @click="pmiColumns = 1"><v-icon x-small>mdi-view-sequential</v-icon></v-btn>
            <v-btn x-small icon :class="{ 'pmi-layout-active': pmiColumns === 2 }" @click="pmiColumns = 2"><v-icon x-small>mdi-view-grid</v-icon></v-btn>
          </div>
        </div>
        <div v-if="!pmiCamerasCollapsed" class="pmi-cameras-grid" :style="{ gridTemplateColumns: 'repeat(' + pmiColumns + ', 1fr)' }">
          <div
            v-for="(cam, idx) in pmiCameras"
            :key="cam.devId"
            class="pmi-cam-card"
            :class="{ 'pmi-cam-card--collapsed': !!pmiCamCollapsed[cam.devId] }"
          >
            <div class="pmi-cam-header">
              <v-btn
                x-small
                icon
                class="pmi-cam-collapse-btn"
                v-auto-tooltip="pmiCamCollapsed[cam.devId] ? 'Restaurar video' : 'Minimizar (ocultar video)'"
                @click="togglePmiCamCollapsed(cam)"
              >
                <v-icon x-small>{{ pmiCamCollapsed[cam.devId] ? 'mdi-chevron-right' : 'mdi-chevron-down' }}</v-icon>
              </v-btn>
              <span class="pmi-cam-name">{{ cam.title }}</span>
              <v-btn
                x-small
                icon
                class="pin-btn"
                :class="{ 'pin-btn--active': isCameraPinned(data.id + '-' + cam.devId) }"
                v-auto-tooltip="isCameraPinned(data.id + '-' + cam.devId) ? 'Ya fijada en bandeja' : 'Fijar en bandeja'"
                @click="pinPmiCamera(cam)"
              >
                <v-icon x-small :color="isCameraPinned(data.id + '-' + cam.devId) ? 'var(--kln-accent)' : ''">
                  {{ isCameraPinned(data.id + '-' + cam.devId) ? 'mdi-pin' : 'mdi-pin-outline' }}
                </v-icon>
              </v-btn>
            </div>
            <div v-if="!pmiCamCollapsed[cam.devId]" class="ptz-cam-wrap">
              <CameraStream :url="cam.streamUrl" :autoplay="true" :camera="cam" :ptz-unique-id="data.uniqueId || ''" @maximize-change="onCameraMaximizeChange" />
              <!-- PTZ controls overlay (same pattern as CameraWindow.vue) -->
              <div v-if="cam.isPtz && puedePtz && data.uniqueId" class="ptz-controls">
                <div class="ptz-dpad">
                  <div class="d-flex justify-center"><v-btn color="secondary" fab x-small dark @click="ptzMove('moveup', cam)"><v-icon>mdi-chevron-up</v-icon></v-btn></div>
                  <div class="d-flex justify-space-between"><v-btn color="secondary" fab x-small dark @click="ptzMove('moveleft', cam)"><v-icon>mdi-chevron-left</v-icon></v-btn><v-btn color="secondary" fab x-small dark @click="ptzMove('moveright', cam)"><v-icon>mdi-chevron-right</v-icon></v-btn></div>
                  <div class="d-flex justify-center"><v-btn color="secondary" fab x-small dark @click="ptzMove('movedown', cam)"><v-icon>mdi-chevron-down</v-icon></v-btn></div>
                </div>
                <div class="ptz-zoom">
                  <v-btn color="secondary" fab x-small dark @click="ptzMove('zoomin', cam)"><v-icon>mdi-magnify-plus</v-icon></v-btn>
                  <v-btn color="secondary" fab x-small dark @click="ptzMove('zoomout', cam)"><v-icon>mdi-magnify-minus</v-icon></v-btn>
                </div>
              </div>
              <!-- Fallo PTZ in-situ: píldora discreta sobre ESTA cámara (el
                   éxito no necesita retro — la cámara se mueve). -->
              <div v-if="ptzErrorByDev[cam.devId]" class="ptz-error-flash">
                <v-icon x-small color="var(--kln-alert-danger)">mdi-lan-disconnect</v-icon>
                <span>Sin respuesta de la cámara</span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Nearby entities — collapsible, solo en tipos accionables.
         Usamos v-show en lugar de v-if porque SimpleBar (inicializado en
         mounted()) envuelve el contenido en .simplebar-content y pierde
         el comment anchor que Vue usa para re-insertar tras un segundo
         toggle. Con v-show el div permanece en el DOM y sólo se alterna
         display, evitando el bug. -->
    <div v-show="showNearbySection" class="drawer-nearby">
      <div v-if="!osrmAvailable" class="drawer-routing-degraded">
        <v-icon x-small color="var(--kln-alert-warning)" class="mr-2">mdi-alert-circle</v-icon>
        <span>Servicio de rutas no disponible. Distancias en línea recta.</span>
      </div>
      <button class="nearby-toggle" @click="toggleNearbyOpen">
        <v-icon x-small class="mr-1">{{ nearbyOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
        <span class="nearby-toggle-label">Recursos cercanos ({{ totalNearbyCount }})</span>
        <span v-if="nearbyOpen" class="nearby-toggle-radius">{{ formatRadius(nearbyRadius) }}</span>
      </button>

      <template v-if="nearbyOpen">
        <!-- Slider de radio configurable por llamada: 0-2000 m -->
        <div class="nearby-radius-control">
          <v-slider
            :value="nearbyRadius"
            min="0"
            max="2000"
            step="50"
            dense
            hide-details
            class="nearby-radius-slider"
            @input="onRadiusChange"
          />
        </div>

        <div v-if="totalNearbyCount === 0" class="nearby-empty">
          <v-icon x-small color="var(--kln-text-dim)" class="mr-1">mdi-magnify-close</v-icon>
          Sin recursos en este radio
        </div>

        <div v-if="nearby && nearby.units.length" class="nearby-section">
          <button class="nearby-group-toggle" @click="unitsOpen = !unitsOpen">
            <v-icon x-small class="nearby-group-chevron">{{ unitsOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
            <v-icon x-small color="var(--kln-alert-success)" class="mr-1">mdi-car-emergency</v-icon>
            <span>Unidades ({{ nearby.units.length }})</span>
          </button>
          <template v-if="unitsOpen">
          <div
            v-for="unit in nearby.units"
            :key="unit.id"
            class="nearby-item"
            :class="{
              'nearby-item--expanded': expandedNearby === unit.id,
              'nearby-item--active-route': activeRouteEntityId === unit.id,
            }"
            @click="toggleNearbyExpand(unit); $emit('click-nearby', unit)"
            @mouseenter="$emit('hover-nearby', unit)"
            @mouseleave="$emit('hover-nearby', null)"
          >
            <div class="nearby-item-row">
              <span class="nearby-name">{{ unit.title }}</span>
              <span class="nearby-dist">
                <template v-if="unit.routeDistance != null">
                  {{ formatMetersKm(unit.routeDistance) }} · {{ formatSecondsMin(unit.routeDuration) }}
                </template>
                <template v-else-if="unit.routeState === 'pending'">
                  <v-progress-circular indeterminate size="12" width="2" color="var(--kln-alert-warning)" />
                </template>
                <template v-else-if="unit.routeState === 'failed'">
                  <v-icon x-small color="var(--kln-alert-warning)">mdi-alert-circle-outline</v-icon>
                  {{ formatDist(unit.distance) }}
                </template>
                <template v-else>
                  {{ formatDist(unit.distance) }}
                </template>
                <v-icon
                  v-if="activeRouteEntityId === unit.id"
                  x-small
                  class="nearby-route-clear"
                  v-auto-tooltip="'Quitar ruta'"
                  @click.stop="$emit('click-nearby', unit)"
                >
                  mdi-close-circle
                </v-icon>
              </span>
            </div>
            <div class="nearby-status" :class="'nearby-status--' + unit.status">
              {{ unit.status === 'online' ? 'En línea' : 'Fuera de línea' }}
            </div>
            <!-- Expanded: unit details -->
            <div v-if="expandedNearby === unit.id" class="nearby-expanded">
              <div class="nearby-exp-row" v-if="unit.data && unit.data.groupName">
                <span class="nearby-exp-label">Grupo</span>
                <span>{{ unit.data.groupName }}</span>
              </div>
              <div class="nearby-exp-row" v-if="unit.data && unit.data.uniqueId">
                <span class="nearby-exp-label">ID</span>
                <span>{{ unit.data.uniqueId }}</span>
              </div>
              <div class="nearby-exp-actions">
                <v-btn x-small outlined @click.stop="$emit('select-nearby', unit)">
                  <v-icon x-small left>mdi-crosshairs-gps</v-icon>Ir a unidad
                </v-btn>
              </div>
            </div>
          </div>
          </template>
        </div>

        <div v-if="nearby && nearby.cameras.length" class="nearby-section">
          <button class="nearby-group-toggle" @click="camerasOpen = !camerasOpen">
            <v-icon x-small class="nearby-group-chevron">{{ camerasOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
            <v-icon x-small color="var(--kln-entity-teal)" class="mr-1">mdi-cctv</v-icon>
            <span>Cámaras ({{ nearby.cameras.length }})</span>
          </button>
          <template v-if="camerasOpen">
          <div
            v-for="cam in nearby.cameras"
            :key="cam.id"
            class="nearby-item"
            :class="{ 'nearby-item--expanded': expandedNearby === cam.id }"
            @click="toggleNearbyExpand(cam)"
            @mouseenter="$emit('hover-nearby', cam)"
            @mouseleave="$emit('hover-nearby', null)"
          >
            <div class="nearby-item-row">
              <span class="nearby-name">{{ cam.title }}</span>
              <span class="nearby-dist">{{ formatDist(cam.distance) }}</span>
            </div>
            <!-- Expanded: show all sub-cameras of this PMI -->
            <div v-if="expandedNearby === cam.id" class="nearby-expanded">
              <template v-if="expandedNearbyCameras.length > 0">
                <div v-for="sub in expandedNearbyCameras" :key="sub.devId" class="nearby-sub-cam">
                  <div class="pmi-cam-header">
                    <span class="pmi-cam-name">{{ sub.title }}</span>
                    <v-btn
                      x-small
                      icon
                      class="pin-btn"
                      :class="{ 'pin-btn--active': isCameraPinned((cam.data && cam.data.id || cam.id) + '-' + sub.devId) }"
                      v-auto-tooltip="isCameraPinned((cam.data && cam.data.id || cam.id) + '-' + sub.devId) ? 'Ya fijada' : 'Fijar'"
                      @click.stop="pinSubCamera(cam, sub)"
                    >
                      <v-icon x-small :color="isCameraPinned((cam.data && cam.data.id || cam.id) + '-' + sub.devId) ? 'var(--kln-accent)' : ''">
                        {{ isCameraPinned((cam.data && cam.data.id || cam.id) + '-' + sub.devId) ? 'mdi-pin' : 'mdi-pin-outline' }}
                      </v-icon>
                    </v-btn>
                  </div>
                  <CameraStream :url="sub.streamUrl" :autoplay="true" :camera="sub" @maximize-change="onCameraMaximizeChange" />
                </div>
              </template>
              <div v-else class="nearby-video-wrap">
                <div class="nearby-video-offline">
                  <v-icon small color="rgba(255,255,255,0.3)">mdi-video-off</v-icon>
                  <span>Sin cámaras disponibles</span>
                </div>
              </div>
              <div class="nearby-exp-actions">
                <v-btn x-small outlined @click.stop="$emit('select-nearby', cam)">
                  <v-icon x-small left>mdi-crosshairs-gps</v-icon>Ir al PMI
                </v-btn>
              </div>
            </div>
          </div>
          </template>
        </div>
      </template>
    </div>
    </div><!-- end drawer-scroll-content -->

    <!-- Modal Editar incidente (solo para incidentes) -->
    <v-dialog
      v-model="editDialogOpen"
      max-width="560"
      persistent
    >
      <v-card class="edit-incident-dialog">
        <v-card-title class="edit-incident-dialog__title">
          <v-icon left>mdi-pencil-outline</v-icon>
          <span>Editar incidente</span>
          <v-spacer />
          <v-btn icon small :disabled="editSaving" @click="closeEditIncidentDialog">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="edit-incident-dialog__body">
          <div class="edit-incident-dialog__field">
            <label class="edit-incident-dialog__label">Tipo de incidente</label>
            <v-autocomplete
              v-model="editForm.tipoIncidente"
              :items="editIncidentTypes"
              :loading="editTypesLoading"
              item-text="incidentCNIName"
              item-value="incidentCNIId"
              outlined dense hide-details
              clearable
              no-data-text="Cargando catálogo..."
              :menu-props="{ contentClass: 'kln-dense-menu', maxHeight: 260 }"
            />
          </div>
          <div class="edit-incident-dialog__field">
            <label class="edit-incident-dialog__label">Descripción</label>
            <v-textarea
              v-model="editForm.descripcion"
              outlined dense hide-details
              rows="2"
              auto-grow
            />
          </div>
          <div class="edit-incident-dialog__row">
            <div class="edit-incident-dialog__field edit-incident-dialog__field--grow">
              <label class="edit-incident-dialog__label">Nombre del reportante</label>
              <v-text-field v-model="editForm.nombreCiudadano" outlined dense hide-details />
            </div>
            <div class="edit-incident-dialog__field edit-incident-dialog__field--grow">
              <label class="edit-incident-dialog__label">Apellido paterno</label>
              <v-text-field v-model="editForm.paternoCiudadano" outlined dense hide-details />
            </div>
            <div class="edit-incident-dialog__field edit-incident-dialog__field--grow">
              <label class="edit-incident-dialog__label">Apellido materno</label>
              <v-text-field v-model="editForm.maternoCiudadano" outlined dense hide-details />
            </div>
          </div>
          <div class="edit-incident-dialog__row edit-incident-dialog__row--align-end">
            <div class="edit-incident-dialog__field edit-incident-dialog__field--grow">
              <label class="edit-incident-dialog__label">Latitud</label>
              <v-text-field
                v-model.number="editForm.latitude"
                type="number"
                step="0.000001"
                outlined dense hide-details
              />
            </div>
            <div class="edit-incident-dialog__field edit-incident-dialog__field--grow">
              <label class="edit-incident-dialog__label">Longitud</label>
              <v-text-field
                v-model.number="editForm.longitude"
                type="number"
                step="0.000001"
                outlined dense hide-details
              />
            </div>
            <v-btn
              x-small
              outlined
              color="var(--kln-accent)"
              class="edit-incident-dialog__pick-btn"
              v-auto-tooltip="'Seleccionar la nueva ubicación con un clic en el mapa'"
              @click="startEditLocationPicker"
            >
              <v-icon x-small left>mdi-crosshairs-gps</v-icon>
              Mapa
            </v-btn>
          </div>
          <div v-if="editError" class="edit-incident-dialog__error">
            <v-icon x-small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
            {{ editError }}
          </div>
        </v-card-text>
        <v-card-actions class="edit-incident-dialog__footer">
          <v-btn text small :disabled="editSaving" @click="closeEditIncidentDialog">Cancelar</v-btn>
          <v-spacer />
          <v-btn
            color="var(--kln-accent)"
            small
            :loading="editSaving"
            :disabled="editSaving || !editHasChanges"
            @click="saveEditIncidentDialog"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Guardar cambios
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal Razonamiento del hit — mismos tipos que Analytics Monitor. -->
    <v-dialog
      v-model="razDialogOpen"
      max-width="480"
      persistent
    >
      <v-card class="add-note-dialog">
        <v-card-title class="add-note-dialog__title">
          <v-icon left>mdi-comment-plus-outline</v-icon>
          <span>{{ hitRazonamiento ? 'Editar razonamiento' : 'Agregar razonamiento' }}</span>
          <v-spacer />
          <v-btn icon small @click="closeRazonamientoDialog" :disabled="razSaving">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="add-note-dialog__body">
          <div class="add-note-dialog__meta">
            Placa <b>{{ hitLprPlaca }}</b> · {{ hitLprArco }}
          </div>
          <div class="add-note-dialog__field">
            <label class="add-note-dialog__label">Tipo</label>
            <v-select
              v-model="razTipo"
              :items="razTipoOptions"
              item-text="name"
              item-value="id"
              outlined dense hide-details
              :disabled="razSaving"
              :menu-props="{ contentClass: 'kln-dense-menu', offsetY: true }"
            />
          </div>
          <div class="add-note-dialog__field">
            <v-textarea
              v-model="razText"
              placeholder="Describa el razonamiento..."
              outlined dense hide-details
              rows="4"
              counter="500"
              maxlength="500"
              auto-grow
              :disabled="razSaving"
            />
            <div class="add-note-dialog__counter">{{ (razText || '').length }} / 500</div>
          </div>
          <div v-if="razError" class="add-note-dialog__error">
            <v-icon x-small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
            {{ razError }}
          </div>
        </v-card-text>
        <v-card-actions class="add-note-dialog__footer">
          <v-btn text small @click="closeRazonamientoDialog" :disabled="razSaving">Cancelar</v-btn>
          <v-spacer />
          <v-btn
            color="var(--kln-accent)"
            small
            :loading="razSaving"
            :disabled="razSaving || !canSaveRazonamiento"
            @click="saveRazonamiento"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal Agregar nota (solo para incidentes) -->
    <v-dialog
      v-model="addNoteDialogOpen"
      max-width="480"
      persistent
    >
      <v-card class="add-note-dialog">
        <v-card-title class="add-note-dialog__title">
          <v-icon left>mdi-note-plus-outline</v-icon>
          <span>Agregar nota</span>
          <v-spacer />
          <v-btn icon small @click="closeAddNoteDialog" :disabled="addNoteSaving">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="add-note-dialog__body">
          <div class="add-note-dialog__field">
            <label class="add-note-dialog__label">Corporación</label>
            <v-autocomplete
              v-model="addNoteCorp"
              :items="incidentCorporaciones"
              outlined dense hide-details
              clearable
              :menu-props="{ contentClass: 'kln-dense-menu', maxHeight: 220 }"
            />
          </div>
          <div class="add-note-dialog__field">
            <v-textarea
              v-model="addNoteText"
              placeholder="Escriba su nota aquí..."
              outlined dense hide-details
              rows="4"
              counter="500"
              maxlength="500"
              auto-grow
            />
            <div class="add-note-dialog__counter">{{ (addNoteText || '').length }} / 500</div>
          </div>
          <div v-if="addNoteError" class="add-note-dialog__error">
            <v-icon x-small color="error" class="mr-1">mdi-alert-circle-outline</v-icon>
            {{ addNoteError }}
          </div>
        </v-card-text>
        <v-card-actions class="add-note-dialog__footer">
          <v-btn text small @click="closeAddNoteDialog" :disabled="addNoteSaving">Cancelar</v-btn>
          <v-spacer />
          <v-btn
            color="var(--kln-accent)"
            small
            :loading="addNoteSaving"
            :disabled="addNoteSaving || !canSaveAddNote"
            @click="saveAddNote"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Guardar nota
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import SimpleBar from 'simplebar'
import CameraStream from './CameraStream.vue'
import ApiService from '@/util/api.service'
import { canAccess } from '@/router/access'
import { canUsePtz, canUseMurphy, canUseRadio } from '@/util/user-permissions'
import { extractPmiCameras, toTrayCamera } from '../utils/pmi-cameras'
import { sendPtzCommand } from '../utils/ptz-service'
import { ENTITY_CONFIGS } from '../data/entity-configs.js'
import {
  resolveCameraOwnershipByGroupId,
  resolveSensorGroupColorByGroupId,
  CAMERA_OWNERSHIP_LABELS,
} from '../data/camera-ownership.js'
import { resolveDeviceType, deviceTypeLabel, deviceTypeMdiIcon } from '../data/device-type-resolver.js'
import { loadDeviceMarkerSvgUrl } from '../utils/icon-registry.js'
import { STATUS_COLORS, ENTITY_COLORS, timeAgo, courseToCardinal } from '../utils/map-constants.js'
import { isMcAlert, mcAlertLabel } from '../data/mc-alert-types.js'
import { parsePhone } from '@/util/phone-format'
import { splitMexicanFullName } from '../data/murphy-helpers'
import { getIncidentMdiIcon, getIncidentColorVar, getIncidentCategoryLabel } from '../data/incident-category-resolver'
import {
  ensureDriverDirectory, isDriverDirectoryLoaded, getDriverInfoForRaw, driverPhotoUrl,
} from '../data/driver-directory.js'
import { resolverRumbo } from '../data/course-resolver.js'
import { isCriticalMotivo } from '../data/critical-motivos'
import { detectPartnerSystem, getPartnerMeta } from '../data/partner-system'
import { isRadioDevice } from '../data/radio-device.js'
import { findConductorLinks } from '../data/conductor-links.js'
import { hitIcon } from '../data/hit-icons.js'
import {
  DrawerSectionHeader,
  DrawerZoneRow,
  DrawerCrossRefButton,
  DrawerActionButton,
} from './drawer-ui'

// Razonamiento de un hit — mismos valores que la vista Analytics Monitor
// (components/analyticsMonitor/ReadingsPanel.vue) para que el operador
// clasifique igual desde el mapa. El backend los guarda en
// record.data.razonamientoTipo.
const RAZONAMIENTO_TIPOS = [
  { id: 'positivo', name: 'Positivo', color: 'var(--kln-alert-danger)' },
  { id: 'falso_positivo', name: 'Falso positivo', color: 'var(--kln-text-muted)' },
  { id: 'positivo_expirado', name: 'Positivo expirado', color: 'var(--kln-alert-warning)' },
]
const RAZONAMIENTO_TIPOS_BY_ID = RAZONAMIENTO_TIPOS.reduce(function(acc, t) {
  acc[t.id] = t
  return acc
}, {})

// Functional DetailRow component
const DetailRow = {
  name: 'DetailRow',
  functional: true,
  props: {
    label: String,
    value: [String, Number, Function],
    icon: { type: String, default: 'mdi-chevron-right' },
    // Cuando true, el value se inserta como innerHTML (permite <br>, <a>,
    // <img>, etc.). Usado por reportant (UBER, DIDI, UBICACIÓN link Maps).
    html: { type: Boolean, default: false },
  },
  render(h, ctx) {
    const { label, value, icon, html } = ctx.props
    const display = typeof value === 'function' ? '—' : (value != null ? String(value) : '—')
    const valueNode = html
      ? h('span', { class: 'detail-row__value', domProps: { innerHTML: display } })
      : h('span', { class: 'detail-row__value' }, [display])
    return h('div', { class: 'detail-row' }, [
      h('v-icon', { props: { xSmall: true }, class: 'detail-row__icon' }, [icon]),
      h('span', { class: 'detail-row__label' }, [label]),
      valueNode,
    ])
  },
}

const SOS_STATUS_MAP = {
  CREATED: 'Nueva',
  WAITING_OPERATOR: 'Esperando operador',
  IN_PROGRESS: 'En progreso',
  RECONNECTING: 'Reconectando',
  FINISHED: 'Finalizada',
}

const GENERIC_SKIP = new Set(['id', '_id', 'tenantId', '__v', 'createdAt', 'updatedAt', 'serverTime'])

export default {
  name: 'MapDrawer',

  components: {
    DetailRow,
    CameraStream,
    DrawerSectionHeader,
    DrawerZoneRow,
    DrawerCrossRefButton,
    DrawerActionButton,
  },

  // NOTA: el watch de este componente vive en el bloque `watch` de abajo
  // (junto a deviceSvgKey / item.monitorId / locationPicker.active). Antes
  // había un segundo bloque `watch: { item }` aquí — al ser llave duplicada
  // del options object, Vue solo registraba el último y este quedaba MUERTO
  // (no-dupe-keys): el colapso de cámaras y el estado de Recursos cercanos
  // no se reseteaban al cambiar de item. El cuerpo se fusionó en el handler
  // de 'item.monitorId'.

  data: function() {
    return {
      // URL del SVG del marker (mismo asset que el mapa) para el avatar del
      // header cuando el item es un device — así el ícono del drawer coincide
      // exactamente con el del mapa (glyph + color + tema). Se resuelve async
      // (ver watcher deviceSvgKey). Vacío → fallback a v-icon (entityIcon).
      deviceSvgUrl: '',
      // Una cámara del drawer está maximizada (CSS-promote en CameraStream).
      // Cuando es true, .map-drawer neutraliza su backdrop-filter para que el
      // contenedor fixed del stream pueda escapar al viewport completo.
      cameraMaximized: false,
      // devId → true mientras se muestra la píldora de fallo PTZ in-situ.
      ptzErrorByDev: {},
      // Sección Notas del incidente — colapsable con chevron en el header.
      notasOpen: true,
      // Modal Agregar nota (solo incidentes)
      addNoteDialogOpen: false,
      addNoteCorp: null,
      addNoteText: '',
      addNoteSaving: false,
      addNoteError: '',
      // Modal Editar incidente (Tipo, Descripción, Nombre reportante, Coords)
      editDialogOpen: false,
      editSaving: false,
      editError: '',
      editIncidentTypes: [],
      editTypesLoading: false,
      editForm: {
        tipoIncidente: null,
        descripcion: '',
        nombreCiudadano: '',
        paternoCiudadano: '',
        maternoCiudadano: '',
        latitude: null,
        longitude: null,
      },
      editFormOriginal: null,
      nearbyOpen: true,
      unitsOpen: true,
      camerasOpen: true,
      expandedNearby: null,
      // Empate radio ↔ unidad por conductor compartido (attributes.conductor).
      // null = sin empate; ver _computeConductorLinks / conductor-links.js.
      conductorLinks: null,
      equipoOpen: true,
      pmiColumns: 1,
      pmiCamerasCollapsed: false,
      // Mapa devId → bool de sub-cámaras del item actual que el operador
      // ha colapsado individualmente: la card mantiene su slot pero el
      // <CameraStream> se desmonta (libera ancho de banda y CPU del iframe).
      // Se resetea cuando cambia el item seleccionado.
      pmiCamCollapsed: {},
      hitPhotoZoomSrc: '',
      // Razonamiento del hit (paridad con Analytics Monitor).
      razDialogOpen: false,
      razTipo: null,
      razText: '',
      razSaving: false,
      razError: '',
      razTipoOptions: RAZONAMIENTO_TIPOS,
      // Razonamientos guardados durante esta sesión, por recordId. El payload
      // crudo del hit llega por socket y no es reactivo (los alerts se
      // congelan al ingerirse), así que no podemos mutarlo y esperar re-render:
      // guardamos aquí y el computed lee este overlay primero.
      razLocal: {},
      // Historial de hits del LPR seleccionado (paridad selectLpr legacy).
      lprHits: [],
      lprHitsLoading: false,
      // Tick reactivo que se incrementa cada segundo para recalcular
      // frescura viva en los computed de Zona 1 (ej. deviceLastUpdateFresh,
      // sosElapsed). El timer se arranca en mounted y se limpia en
      // beforeDestroy. Sin este tick, la frescura mostrada quedaría
      // congelada al valor de cuando se abrió el drawer.
      drawerFreshnessTick: 0,
      zone4Expanded: true,
      kpiLoadExpanded: true,
      // Opciones del menú "Historial". El reporte se genera automático
      // cuando el operador elige uno de estos. La opción "Personalizado"
      // del menú abre el dialog de filtros sin periodo preseleccionado.
      // Subset estable de los periodos típicos para revisión operativa.
      historyPeriodOptions: [
        { id: 'today',     label: 'Hoy' },
        { id: 'yesterday', label: 'Ayer' },
        { id: 'week',      label: 'Semana actual' },
        { id: 'lastWeek',  label: 'Semana anterior' },
        { id: 'mounth',    label: 'Mes actual' },
        { id: 'lastMonth', label: 'Mes anterior' },
      ],
      // Tab activo en la sección de eventos del drawer KPI. 'critical'
      // muestra solo eventos filtrados por isCriticalMotivo; 'all'
      // muestra todos los motivos sin filtro. Se persiste en el pin a
      // futuro si es necesario; por ahora es estado local del drawer.
      kpiEventsTab: 'critical',
      // Contador que despierta a los computeds que dependen del directorio de
      // elementos: la carga es async y el módulo no es reactivo, así que sin
      // esto la foto resuelta después del montaje no llegaría a pintarse.
      driverDirTick: 0,
    }
  },

  created: function() {
    // Non-reactive cache for pmiCameras array (prevents iframe remount on WS updates)
    this._pmiCamerasCache = null
    // Flag non-reactive para caer al dummy si la foto del device 404ea
    this._devicePhotoBroken = false
    // Flag para la foto del encargado (comisario) en KPI de mando unico.
    this._kpiEncargadoPhotoBroken = false
  },

  watch: {
    // Resuelve (async) el SVG del marker para el avatar del header cuando el
    // item es un device, así el ícono del drawer coincide EXACTO con el del
    // mapa (glyph + color + tema). Re-chequea el key al resolver para evitar
    // pisar otra selección hecha mientras cargaba.
    deviceSvgKey: {
      immediate: true,
      handler: function(key) {
        var self = this
        if (!key) { this.deviceSvgUrl = ''; return }
        var parts = key.split('|')
        loadDeviceMarkerSvgUrl(parts[0], parts[1], 'day').then(function(url) {
          if (self.deviceSvgKey === key) self.deviceSvgUrl = url || ''
        })
      },
    },

    // Resetear el flag de foto rota al cambiar de item seleccionado para
    // que el dummy no se pegue si el nuevo item tiene foto valida. Ademas
    // para devices disparamos el fetch lazy del conductor (paridad con
    // Map.vue legacy: selectDevice() hace GET /v2/drivers/{conductor}).
    'item.monitorId': {
      immediate: true,
      handler: function onItemChange() {
        this._devicePhotoBroken = false
        this._kpiEncargadoPhotoBroken = false
        this._maybeFetchDeviceDriver()
        this._ensureDriverDirForPhoto()
        this._maybeFetchLprHits()
        this._computeConductorLinks()
        // Reset del estado de UI por-item (fusionado del watcher `item` que
        // era llave duplicada y nunca corría): sin esto, el colapso de una
        // sub-cámara (keyado solo por devId) o de la sección "Cámaras" se
        // hereda al siguiente item con cámaras y parece que "no cargan".
        this.expandedNearby = null
        this.pmiCamerasCollapsed = false
        this.pmiCamCollapsed = {}
        // Forzar Recursos cercanos expandido y notificar al padre SIEMPRE.
        // El emit es incondicional: un guard (if !nearbyOpen) dejaría al
        // padre con _nearbyExpanded=false → no se dibujaría el círculo.
        this.nearbyOpen = true
        this.$emit('nearby-open-change', true)
      },
    },

    // Cuando el location-picker resuelve (active: true → false), si este
    // componente estaba esperando un resultado para el modal de edición,
    // lo aplica al form y reabre el modal. Si no estaba esperando, no toca.
    'locationPicker.active': function(newActive, oldActive) {
      // Solo nos interesa la transición true → false (resolve o cancel).
      if (newActive || !oldActive) return
      if (!this._pickerWaitingForResult) return
      var lp = this.locationPicker
      // El requestId debe coincidir con el que pedimos: evita race con
      // otro consumer que haya iniciado/resuelto su propio picker.
      if (lp.requestId !== this._pickerWaitingForResult) return
      this._pickerWaitingForResult = null
      if (lp.result && typeof lp.result.lat === 'number' && typeof lp.result.lng === 'number') {
        this.editForm.latitude = Number(lp.result.lat.toFixed(6))
        this.editForm.longitude = Number(lp.result.lng.toFixed(6))
      }
      // Reabre el modal manteniendo el resto del editForm intacto.
      this.editDialogOpen = true
    },
  },

  props: {
    item: {
      type: Object,
      default: null,
    },
    nearby: {
      type: Object,
      default: null,
    },
    isFollowing: {
      type: Boolean,
      default: false,
    },
    isPinned: {
      type: Boolean,
      default: false,
    },
    osrmAvailable: {
      type: Boolean,
      default: true,
    },
    // ID de la unidad con ruta OSRM activa (clic persistente).
    // null si no hay ruta activa. Usado para destacar la fila y
    // mostrar el botón "limpiar ruta" en el item correspondiente.
    activeRouteEntityId: {
      type: String,
      default: null,
    },
    // Cuando el device tiene sus geocercas cargadas en el mapa, el botón
    // del drawer cambia a "Ocultar" — paridad con el toggle del legacy.
    deviceGeofencesVisible: {
      type: Boolean,
      default: false,
    },
  },

  inject: {
    sosCallServiceGetter: { default: null },
  },

  computed: {
    lprStatusLabel: function() {
      var st = this.data && this.data.status
      return st === 'active' ? 'Activo' : st === 'partial' ? 'Parcial' : st ? 'Inactivo' : ''
    },
    ...mapState('map-v2', {
      pinnedCameras: state => state.tray.cameras,
      nearbyRadius: state => state.nearbyRadius,
    }),

    ...mapGetters('map-v2', ['locationPicker']),
    ...mapGetters('auth', ['getUser']),
    puedeMurphy() {
      return canUseMurphy(this.getUser)
    },
    puedePtz() {
      return canUsePtz(this.getUser)
    },

    // Tipos accionables donde tiene sentido mostrar "Recursos cercanos".
    // Debe coincidir con NEARBY_ACTIONABLE_TYPES en MapV2.vue.
    showNearbySection() {
      const actionable = ['incident', 'sensor', 'sos_call', 'reportant', 'hit', 'mapPoint']
      return actionable.indexOf(this.entityType) !== -1
    },

    totalNearbyCount() {
      if (!this.nearby) return 0
      return (this.nearby.cameras ? this.nearby.cameras.length : 0)
        + (this.nearby.units ? this.nearby.units.length : 0)
    },

    entityType() {
      return this.item?.entityType || 'generic'
    },

    // The item from the store is { monitorId, entityType, data: monitor }
    // The monitor has { id, entityType, title, ..., data: rawApiData }
    // We need the raw API data for detail fields
    data() {
      var monitor = this.item?.data || {}
      return monitor.data || monitor || {}
    },

    entityConfig() {
      return ENTITY_CONFIGS[this.entityType] || null
    },

    entityColor() {
      if (this.entityType === 'kpi') return 'var(--kln-kpi-publico)'
      if (this.entityType === 'hit') return 'var(--kln-entity-teal)'
      // Incidentes: el header del drawer hereda el color de la categoría CNIE
      // para que coincida visualmente con el marker en el mapa, el chip del
      // sidebar y el chip de "Categoría" en zone-1. Antes era el color
      // genérico de la entity (rojo de incident) para todos los grupos.
      if (this.entityType === 'incident') return getIncidentColorVar(this.data || {})
      return this.entityConfig?.color || 'var(--kln-status-idle)'
    },

    entityIcon() {
      if (this.entityType === 'kpi') return 'mdi-chart-donut-variant'
      // Mismo mapa que el sidebar de alertas (ver hit-icons.js): antes cada
      // uno tenía su copia y hitLpr apuntaba a un ícono inexistente.
      if (this.entityType === 'hit') {
        return hitIcon(this.monitor.typeOfItem, this.monitor.hitType)
      }
      // Incidentes: el ícono del header refleja la categoría CNIE específica
      // (médico, seguridad, PC, servicios públicos, asistencia, otros, mal
      // uso) en lugar del shield-alert genérico hardcoded en entityConfig.
      if (this.entityType === 'incident') return getIncidentMdiIcon(this.data || {})
      // Devices: ícono por tipo operativo (dron, ambulancia, etc.) — el config
      // genérico de device es mdi-car-emergency para todos, pero un dron debe
      // verse como dron (igual que el marker del mapa, que ya usa el slug).
      if (this.entityType === 'device') return deviceTypeMdiIcon(resolveDeviceType(this.data || {}))
      return this.entityConfig?.mdiIcon || 'mdi-map-marker'
    },

    // Clave que identifica qué SVG de marker debe mostrar el avatar para un
    // device: slug (tipo) + status. Al cambiar, el watcher resuelve el asset.
    deviceSvgKey() {
      if (this.entityType !== 'device') return ''
      var slug = resolveDeviceType(this.data || {})
      var status = (this.item && this.item.data && this.item.data.status) || 'offline'
      return slug + '|' + status
    },

    monitor() {
      return this.item?.data || {}
    },

    entityTitle() {
      if (!this.item) return ''
      if (this.entityType === 'sos_call') {
        var d = this.data || {}
        var ud = d.userData || {}
        var n = [ud.name, ud.lastName1, ud.lastName2].filter(Boolean).join(' ').trim()
        return n || d.name || d.callbackNumber || ud.phoneNumber || d.phone || 'SOS'
      }
      // Reportante: priorizamos el teléfono formateado con su bandera
      // de país (paridad con la card del sidebar). Si el parser no
      // reconoce el formato, cae al title raw del MapMonitor.
      if (this.entityType === 'reportant') {
        var parsed = this.reportantPhoneParsed
        if (parsed && parsed.formatted) {
          return (parsed.flag ? parsed.flag + ' ' : '') + parsed.formatted
        }
      }
      // Mismo desajuste que en mcMonitorData: cuando el item ES el monitor,
      // el título vive un nivel arriba de donde lo busca `this.monitor`.
      if (this.entityType === 'medida_cautelar') {
        return this.mcPersonaNombre
      }
      return this.monitor.title || this.data.name || '—'
    },

    entitySubtitle() {
      if (!this.item) return ''
      if (this.entityType === 'kpi') return 'KPIs'
      if (this.entityType === 'hit') return this.monitor.subtitle2 || 'Hit'
      if (this.entityType === 'sos_call') {
        var t = this.data && this.data.type
        var label = 'SOS'
        if (t === 'VIDEO_CALL') label = 'Videollamada'
        else if (t === 'VOICE_CALL' || t === 'CALL') label = 'Llamada de voz'
        else if (t === 'CHAT') label = 'Chat'
        return 'SOS — ' + label
      }
      // Devices: en lugar del generico "Unidades", mostrar el tipo
      // operativo concreto (Ambulancia / Policía Municipal / Tránsito /
      // etc.) calculado por el resolver. Si cae a "Sin clasificar", al
      // menos el operador sabe que la inferencia no encontro el tipo.
      if (this.entityType === 'device') {
        var slug = resolveDeviceType(this.data || {})
        return deviceTypeLabel(slug)
      }
      return this.entityConfig?.label || this.entityType
    },

    // Oculta el id raw del header para entidades donde el título ya ES el
    // identificador humano (sos_call: nombre/teléfono; reportant: teléfono)
    // y el id del backend es un UUID/ObjectId que genera ruido visual.
    showDrawerId() {
      if (this.entityType === 'sos_call' || this.entityType === 'reportant') return false
      return !!(this.data.id || this.data._id)
    },

    drawerIdText() {
      const id = String(this.data.id || this.data._id || '')
      if (id.length > 12) return id.slice(-6)
      return id
    },

    entityStatus() {
      return this.monitor.status || this.item?.status || 'idle'
    },

    statusChipColor() {
      return STATUS_COLORS[this.entityStatus] || 'var(--kln-status-idle)'
    },

    statusChipIcon() {
      const icons = {
        online: 'mdi-circle',
        offline: 'mdi-circle-outline',
        alert: 'mdi-alert',
        idle: 'mdi-minus-circle',
      }
      return icons[this.entityStatus] || 'mdi-circle'
    },

    statusLabel() {
      const labels = {
        online: 'En línea',
        offline: 'Fuera de línea',
        alert: 'Alerta',
        idle: 'Inactivo',
      }
      return labels[this.entityStatus] || this.entityStatus
    },

    canFollow() {
      // Pseudo-monitores (alertas que no tienen monitor real en _monitors)
      // no pueden ser seguidos — el follow requiere updates de posicion
      // continuas. Se marcan con isAlertOnly desde _openAlertDrawer.
      if (this.monitor && this.monitor.isAlertOnly) return false
      // Medida cautelar NO: a esa persona se llega precisamente por haber
      // abierto su alerta, que ya enciende el seguimiento. El botón ofrecía
      // activar algo que ya estaba activo y lo único que conseguía era
      // cerrar el panel.
      return ['device', 'bus', 'sos_call', 'reportant'].includes(this.entityType)
    },

    canViewHistory() {
      // Solo device — /reports/bus sigue comentado en el router. Cuando se
      // reactive el de buses, sumar 'bus' aquí.
      if (this.entityType !== 'device') return false
      // Y solo si el guard dejaría entrar: ofrecer un botón que el guard
      // rebota es peor que no ofrecerlo. Se pregunta con canAccess, nunca
      // leyendo user.attributes a mano (regla 19 de CLAUDE.md).
      const { route } = this.$router.resolve({ name: 'historial-unidades-reports' })
      return canAccess(route, this.getUser)
    },

    // Descartar alerta (sin crear reporte) — aplica a bus y sensor pánico
    // cuando están en estado 'alert'. Paridad con legacy:
    //   - bus: action.discardBusAlerts (Map.vue:3443-3449)
    //   - sensor: action.discardSensorAlerts (Map.vue:3712-3716)
    canDiscardAlert() {
      const s = this.data.status
      if (this.entityType === 'bus' && s === 'alert') return true
      if (this.entityType === 'sensor' && s === 'alert') return true
      return false
    },

    // Cerrar alerta (con reporte) — aplica a external_sensor cuando el
    // additionalData.status es 'alert' o 'reported'. Paridad con legacy
    // Map.vue:3745-3753.
    canCloseAlert() {
      if (this.entityType !== 'external_sensor') return false
      const s = (this.data.additionalData && this.data.additionalData.status) || this.data.status
      return s === 'alert' || s === 'reported'
    },

    // Despachar incidente — abre el dialog de IncidentDispatch (CAD).
    // Solo para incidentes en estado ACTIVO (cerrados no se despachan).
    // Paridad con legacy Map.vue:3626-3632.
    canDispatchIncident() {
      if (this.entityType !== 'incident') return false
      return this.data.status === 'ACTIVO' || this.data.status === 'alert'
    },

    // Comunicacion de radio — el boton aparece si el device es de categoria
    // radio (radioType tetra|mcx), no por el nombre del uniqueId.
    // El criterio NO depende de status: el operador puede querer iniciar
    // comunicacion incluso si el equipo esta offline (paging por radio).
    canOpenTetraCommunication() {
      if (this.entityType !== 'device') return false
      if (!canUseRadio(this.getUser)) return false
      return isRadioDevice(this.data)
    },

    // Generar alerta en sensor pánico — acción manual. Restringida a
    // administradores que además tengan permiso `alarm_sensor`, sobre
    // sensor en status 'up' (si ya está en alerta no tiene sentido
    // generar otra).
    canGenerateSensorAlert() {
      if (this.entityType !== 'sensor') return false
      if (this.data.status !== 'up') return false
      // Usuario efectivo: alarm_sensor es un acceso del catálogo de perfiles.
      if (!this.getUser || !this.getUser.administrator) return false
      return !!(this.getUser.attributes && this.getUser.attributes.alarm_sensor)
    },

    // Reporte al CAD — paridad con legacy Map.vue:3229-3282 (cámara) y
    // Map.vue:3345-3441 (bus). Cámara: siempre disponible. Bus: solo en
    // status 'alert' (reporta la alerta activa al sistema CAD).
    canReportToCad() {
      if (this.entityType === 'camera') return true
      if (this.entityType === 'bus' && this.data.status === 'alert') return true
      // Botón de pánico: solo con alerta viva. No es una restricción de la
      // interfaz, es la del backend — `KalanSensorResource:165` rechaza con 400
      // «Sensor is not alerted» antes de mirar siquiera el cuerpo.
      if (this.entityType === 'sensor' && this.data.status === 'alert') return true
      return false
    },

    // El botón de la barra se calla cuando la acción primaria ya reporta: un
    // botón de pánico alertado ofrecía «Reporte» y «Reportar al CAD», los dos
    // con el mismo efecto. Dos caminos al mismo sitio no son el doble de
    // acceso, son una duda.
    showReportInActionBar() {
      if (!this.canReportToCad) return false
      const p = this.sensorPrimaryAction
      return !(p && p.event === 'report-cad')
    },

    // Ver geocercas asignadas al device — paridad con legacy
    // Map.vue:3545-3563. El botón también ocuta (toggle) si ya hay
    // geocercas del device cargadas.
    canViewDeviceGeofences() {
      return this.entityType === 'device'
    },

    // Abrir panel IRP externo del reportante (UBER/DIDI/otros). Solo se
    // muestra si el backend incluyó la URL de seguimiento. Paridad con
    // legacy Map.vue:3128-3142.
    canOpenIrp() {
      if (this.entityType !== 'reportant') return false
      return !!(this.data && this.data.urlIRP)
    },

    speedText() {
      const speed = this.data.position?.speed ?? this.data.speed
      if (speed == null) return '—'
      return Math.round(speed) + ' km/h'
    },

    courseText() {
      const course = this.data.position?.course ?? this.data.course
      if (course == null) return '—'
      return course + '° ' + courseToCardinal(course)
    },

    lastUpdate() {
      const d = this.data
      const ts = d.updatedAt || d.serverTime || d.lastUpdate || d.createdAt || d.created_at
      if (!ts) return '—'
      return timeAgo(ts)
    },

    // ── Device computed helpers (paridad con fieldsMap.devices legacy) ──
    // data es el payload crudo del device tal cual lo recibe el WS principal.
    devicePosition() {
      return this.data && this.data.position
    },

    // URL de la foto del usuario del device. Si no hay foto, devuelve el
    // dummy user2.png (mismo asset que el legacy, servido desde /img/).
    // El backend suele guardar la foto como path relativo; tomamos el
    // basename y lo pegamos al VITE_APP_IMAGES_PATH (default /api/v2/public/images/).
    deviceUserPhoto() {
      // driverDirTick se lee para que el computed vuelva a correr cuando el
      // directorio termina de cargar (es async y no reactivo por sí mismo).
      this.driverDirTick // eslint-disable-line no-unused-expressions
      if (this._devicePhotoBroken) return '/img/user2.png'

      // 1) Foto del `user` joined, cuando el payload la trae.
      const u = this.data && this.data.user
      let photo = u && u.photo

      // 2) Fallback por el elemento asignado. En la practica este es el camino
      //    real: NINGUN device que llega al mapa trae el objeto `user` —ni los
      //    radios TETRA ni las patrullas—, solo attributes.conductor con el id
      //    del elemento. Sin esto el drawer mostraba el avatar dummy para
      //    todos, y se notaba sobre todo en los radios, que es donde el
      //    operador espera ver a la persona.
      if (!photo) {
        const info = getDriverInfoForRaw(this.data)
        photo = info && info.photo
      }

      return driverPhotoUrl(photo) || '/img/user2.png'
    },

    // Icono LTE/TETRA/Kenwood/GPS a la derecha del retrato. Mismas reglas
    // del fieldsMap legacy: TETRA > kenwood > noLTE (arrow/dif/stj) > default LTE.
    deviceLteIcon() {
      const d = this.data || {}
      const uniqueId = String(d.uniqueId || '')
      const category = d.category || ''
      const NO_LTE = ['arrow', 'dif', 'stj']
      if (uniqueId.includes('TETRA')) return '/img/tetra.png'
      if (uniqueId.includes('kenwood')) return '/img/kenwood.png'
      if (NO_LTE.indexOf(category) !== -1) return '/img/gps.png'
      return '/img/lte.png'
    },

    // Clase especial para el badge TETRA que es mas ancho (64x40) que el
    // resto (32x32). Permite al CSS adaptar el tamano.
    deviceLteClass() {
      const uniqueId = String((this.data && this.data.uniqueId) || '')
      return uniqueId.includes('TETRA') ? 'device-badge--wide' : ''
    },

    // Icono de tipo (portatil/crp). TETRA y categoria "persona" usan
    // port.png; categorias noLTE no tienen este indicador; resto usa crp.png.
    deviceTypeIcon() {
      const d = this.data || {}
      const uniqueId = String(d.uniqueId || '')
      const category = d.category || ''
      const PERSON = ['ayudantia', 'bombero', 'estatalp', 'ministerialp', 'militar', 'municipalp', 'elementoPcivil']
      const NO_LTE = ['arrow', 'dif', 'stj']
      if (uniqueId.includes('TETRA')) return '/img/port.png'
      if (NO_LTE.indexOf(category) !== -1) return null
      if (PERSON.indexOf(category) !== -1) return '/img/port.png'
      return '/img/crp.png'
    },

    deviceUsuario() {
      const u = this.data && this.data.user
      return (u && (u.name || u.nombre)) || 'Desconocido'
    },

    // Grupos de la sección "Equipo del elemento". Cap de 6 filas por grupo:
    // hay conductores basura asignados a decenas de radios en producción y
    // sin el cap la sección se comería el drawer completo.
    conductorLinkGroups() {
      const links = this.conductorLinks
      if (!links) return []
      const CAP = 6
      const groups = []
      const push = (key, label, icon, items) => {
        if (!items.length) return
        groups.push({
          key, label, icon, items: items.slice(0, CAP), total: items.length, extra: Math.max(0, items.length - CAP),
        })
      }
      push('radios', 'Radios', 'mdi-radio-handheld', links.radios)
      push('unidades', 'Unidades', 'mdi-shield-account-outline', links.unidades)
      return groups
    },

    conductorLinkCount() {
      const links = this.conductorLinks
      if (!links) return 0
      return links.radios.length + links.unidades.length
    },

    deviceUnidadAdmin() {
      const u = this.data && this.data.user
      return (u && (u.unidad_administrativa || u.dependencia)) || this.data.groupName || 'Desconocido'
    },

    deviceAdscripcion() {
      const u = this.data && this.data.user
      return (u && (u.adscripcion || u.corporacion)) || this.data.groupName || 'Desconocido'
    },

    deviceCategoria() {
      const u = this.data && this.data.user
      return (u && u.categoria) || this.data.groupName || 'Desconocido'
    },

    deviceUltimaActualizacion() {
      const ts = this.data && (this.data.lastUpdate || this.data.updatedAt || this.data.serverTime)
      if (!ts) return '—'
      return this.formatFullDate(ts)
    },

    // Los estados llegan como claves del backend. Traducir todas las que el
    // normalizer puede producir —no solo las tres frecuentes—: el `return s`
    // de respaldo mostraba la clave cruda en pantalla, y un operador veia
    // "unknown" en una ficha por lo demas en español. Los terminos son los
    // mismos que usa el mapa anterior (Util.statusMap) para que un equipo no
    // aparezca "Desconocido" en una vista y de otra forma en la otra.
    deviceEstatus() {
      const s = this.data && this.data.status
      if (s === 'online' || s === 'up' || s === 'active') return 'En línea'
      if (s === 'offline' || s === 'down' || s === 'inactive') return 'Fuera de línea'
      if (s === 'alert') return 'En alerta'
      if (s === 'unknown') return 'Desconocido'
      if (s === 'idle') return 'Sin movimiento'
      if (s === 'partial' || s === 'parcial') return 'Parcialmente en línea'
      if (s === 'maintenance') return 'En mantenimiento'
      return s || '—'
    },

    deviceUbicacion() {
      const p = this.devicePosition
      if (!p || p.latitude == null || p.longitude == null) return '—'
      return (p.latitude + '0000000000').substring(0, 10)
        + ', ' + (p.longitude + '0000000000').substring(0, 10)
    },

    devicePrecision() {
      const p = this.devicePosition
      if (!p || p.accuracy == null) return '—'
      return p.accuracy + ' KM'
    },

    // Legacy: parseFloat(speed) * 1.852 para convertir de nudos a km/h. El
    // WS legacy enviaba la velocidad en nudos (protocolo osmand). Mantenemos
    // la misma conversion para paridad exacta con lo que el operador ve en
    // el mapa viejo.
    deviceVelocidad() {
      const p = this.devicePosition
      if (!p) return '—'
      const speed = p.speed || 0
      return (parseFloat(speed) * 1.852).toFixed(2) + ' Km/h'
    },

    deviceDistancia() {
      const p = this.devicePosition
      if (!p || !p.attributes || p.attributes.distance == null) return ''
      return p.attributes.distance + ' KM'
    },

    deviceMovimiento() {
      const p = this.devicePosition
      if (!p || !p.attributes) return '—'
      return p.attributes.motion ? 'Sí' : 'No'
    },

    deviceTipoAlerta() {
      const p = this.devicePosition
      const opts = { None: 'Ninguna', Whatsapp: 'Whatsapp', Telegram: 'Telegram' }
      const val = p && p.attributes && p.attributes.instantMessenger
      return opts[val] || 'Ninguna'
    },

    deviceDestinatarios() {
      const p = this.devicePosition
      const list = p && p.attributes && p.attributes.phoneList
      if (!list || !list.length) return ''
      return Array.isArray(list) ? list.join(', ') : String(list)
    },

    // ── Helpers del sistema de zonas para device (DRAWERS-VARIANTS-SPEC §1)

    // Frescura relativa del lastUpdate. Se recalcula cada segundo vía
    // drawerFreshnessTick (data) para que la lectura del operador sea viva.
    deviceLastUpdateFresh() {
      // eslint-disable-next-line no-unused-expressions
      this.drawerFreshnessTick
      const ts = this.data && (this.data.lastUpdate || this.data.updatedAt || this.data.serverTime)
      return ts ? timeAgo(ts) : ''
    },

    // Rumbo cardinal desde data.position.course (campo derivable, antes no usado).
    // Rumbo mostrado en la ficha.
    //
    // Antes se pintaba `course` tal cual, y como el 85% de las unidades en
    // movimiento lo manda en 0, la ficha decia "0° N" de unidades que iban al
    // sur. No era un dato faltante presentado como faltante: era un cero
    // presentado como una medicion.
    //
    // Ahora se resuelve con course-resolver: el valor del equipo cuando existe,
    // derivado del recorrido cuando no, y nada cuando la unidad esta detenida
    // o no hay desplazamiento suficiente para afirmar una direccion.
    deviceRumbo() {
      const p = this.devicePosition
      if (!p) return ''
      const kmh = p.speed != null ? parseFloat(p.speed) * 1.852 : null
      const buffer = (typeof window !== 'undefined' && window.__mapV2PositionBuffer) || {}
      const coords = buffer[this.item && this.item.monitorId]
      const grados = resolverRumbo(p.course, coords, kmh)
      if (grados == null) return ''
      return (Math.round(grados) + '° ' + courseToCardinal(grados)).trim()
    },

    // Formato relativo del fixTime GPS (antes no renderizado).
    deviceFixTimeRelative() {
      // eslint-disable-next-line no-unused-expressions
      this.drawerFreshnessTick
      const p = this.devicePosition
      if (!p || !p.fixTime) return ''
      return timeAgo(p.fixTime)
    },

    deviceHasActiveEvents() {
      const ev = this.data && this.data.events
      return Array.isArray(ev) && ev.length > 0
    },

    // Status normalizado para modular la acción primaria.
    deviceNormalizedStatus() {
      const s = this.data && this.data.status
      if (s === 'online' || s === 'up') return 'online'
      if (s === 'alert') return 'alert'
      return 'offline'
    },

    devicePrimaryAction() {
      if (this.deviceHasActiveEvents || this.deviceNormalizedStatus === 'alert') {
        return {
          label: 'Despachar unidad cercana',
          icon: 'mdi-shield-alert',
          tone: 'danger',
          event: 'request-dispatch',
        }
      }
      if (this.deviceNormalizedStatus === 'online') {
        return {
          label: this.isFollowing ? 'Dejar de seguir' : 'Seguir unidad',
          icon: 'mdi-navigation',
          tone: this.isFollowing ? 'muted' : 'accent',
          event: 'follow',
        }
      }
      // offline/idle → sin acción primaria
      return null
    },

    deviceActionContext() {
      if (this.deviceHasActiveEvents) {
        const ev = (this.data.events || [])[0] || {}
        return 'Evento activo: ' + (ev.type || ev.name || 'sin detalle') + '. Unidad sigue reportando.'
      }
      if (this.deviceNormalizedStatus === 'alert') {
        return 'Unidad en alerta. Última señal: ' + (this.deviceLastUpdateFresh || 'sin reporte') + '.'
      }
      if (this.deviceNormalizedStatus === 'online') {
        return 'Unidad operando normal. Última señal: ' + (this.deviceLastUpdateFresh || 'ahora') + '.'
      }
      return 'Sin reporte desde hace ' + (this.deviceLastUpdateFresh || 'tiempo desconocido') + '.'
    },

    // Murphy: consultable si hay nombre de usuario reconocible.
    deviceCanSearchMurphy() {
      const u = this.data && this.data.user
      const name = u && (u.name || u.nombre)
      return !!(name && name.trim().length > 3)
    },

    // Telefono para cross-ref "Llamar al chofer" (tel: link).
    devicePhone() {
      return (this.data && this.data.phone) || ''
    },

    // Color del estado (para el valor "Estatus" en Zona 1 con tratamiento vivo).
    deviceStatusColor() {
      const s = this.deviceNormalizedStatus
      if (s === 'online') return 'var(--kln-status-online)'
      if (s === 'alert') return 'var(--kln-alert-danger)'
      return 'var(--kln-status-offline)'
    },

    // ── Hit LPR helpers (paridad con fieldsMap.hitLpr legacy) ──
    // IMPORTANTE sobre el shape:
    //   this.item.data  = monitor wrapper (con typeOfItem/hitType/data/etc)
    //   this.data       = monitor.data desenvuelto por el computed data() de
    //                      arriba — para un hit, eso ES el hit crudo con
    //                      {analyticDevice, extendedData, data:{placa,foto,
    //                      tipo_vehiculo}, createdAt, typeOfItem, hitType,...}.
    //   this.data.data  = el subobjeto interno con placa/foto/tipo_vehiculo.
    // Por eso hitRaw = this.data (NO this.data.data ni this.data.raw, que nos
    // metian un nivel de mas y devolvian undefined en analyticDevice/extendedData).
    hitRaw() {
      return this.data || {}
    },

    hitIsLpr() {
      if (this.entityType !== 'hit') return false
      var mon = this.monitor || {}
      var raw = this.hitRaw || {}
      var t = mon.typeOfItem || mon.hitType || raw.typeOfItem || raw.hitType
      return t === 'hitLpr'
    },

    // ─── Medidas cautelares ───
    hitIsMedidaCautelar() {
      if (this.entityType !== 'hit') return false
      var mon = this.monitor || {}
      var raw = this.hitRaw || {}
      return isMcAlert(mon.typeOfItem || raw.typeOfItem)
    },

    // El personaId solo llega en alertas de medida cautelar; su presencia es
    // lo que habilita el botón de seguimiento.
    // Vale tanto desde la alerta (item tipo hit) como desde la persona ya
    // seguida (item tipo medida_cautelar). Antes solo contemplaba el primero,
    // así que al enganchar el seguimiento al abrir la alerta el botón de
    // expediente desaparecía justo cuando más se necesita.
    mcPersonaId() {
      if (!this.hitIsMedidaCautelar && this.entityType !== 'medida_cautelar') return null
      var raw = this.hitRaw || {}
      var d = this.data || {}
      return raw.personaId || d.personaId || null
    },

    mcMotivo() {
      var raw = this.hitRaw || {}
      var mon = this.monitor || {}
      return mcAlertLabel(mon.typeOfItem || raw.typeOfItem)
    },

    mcPersonaNombre() {
      var raw = this.hitRaw || {}
      return raw.personaNombre
        || this.mcMonitorData.personaNombre
        || (this.item && this.item.title)
        || (this.monitor && this.monitor.title)
        || '—'
    },

    mcEstadoAlerta() {
      var raw = this.hitRaw || {}
      var map = { pendiente: 'Pendiente', atendida: 'Atendida', descartada: 'Descartada' }
      return map[raw.status] || '—'
    },

    // Datos del monitor en seguimiento (no de la alerta): los alimenta
    // _onMcPosition con cada posición que llega por WebSocket.
    // El item de una persona seguida ES el monitor ({title, subtitle, data}),
    // mientras que el resto del drawer asume que el monitor viene envuelto en
    // item.data. Ese desajuste dejaba el panel entero en "—" con los datos
    // cargados y visibles en el mapa.
    mcMonitorData() {
      var d = this.data || {}
      if (d.positionsHistory || d.lastSeenAt || d.battery != null) return d
      return d.data || (this.monitor && this.monitor.data) || {}
    },

    mcBateriaTexto() {
      var b = this.mcMonitorData.battery
      return typeof b === 'number' ? Math.round(b) + '%' : '—'
    },

    mcUltimaSenal() {
      var ts = this.mcMonitorData.lastSeenAt
      return ts ? timeAgo(ts) : '—'
    },

    mcPuntosRecorrido() {
      var h = this.mcMonitorData.positionsHistory
      return Array.isArray(h) ? String(h.length) : '0'
    },

    hitLprFecha() {
      const ts = this.hitRaw.createdAt || this.data.createdAt
      if (!ts) return '—'
      return this.formatFullDate(ts)
    },

    hitLprPlaca() {
      return (this.hitRaw.data && this.hitRaw.data.placa) || this.hitRaw.plate || '—'
    },

    hitLprTipoVehiculo() {
      return (this.hitRaw.data && this.hitRaw.data.tipo_vehiculo) || 'Desconocido'
    },

    hitLprArco() {
      const ad = this.hitRaw.analyticDevice
        || (this.hitRaw.data && this.hitRaw.data.analyticDevice)
      return (ad && ad.name) || '—'
    },

    hitLprDireccion() {
      const ad = this.hitRaw.analyticDevice
        || (this.hitRaw.data && this.hitRaw.data.analyticDevice)
      return (ad && ad.address) || '—'
    },

    hitLprCarril() {
      return (this.hitRaw.extendedData && this.hitRaw.extendedData.sensor) || '—'
    },

    // El backend rara vez manda `extendedData.descripcion`, así que la fila
    // quedaba en "—" incluso en hits con match. Si el hit viene de la lista
    // negra, el motivo real es el de la lista: su `reason` (corto, ej. "robo")
    // y, en su defecto, la justificación —que suele traer la predenuncia o el
    // expediente—. Cuando sube la justificación, la sección Lista negra deja
    // de repetirla como "Antecedente" (ver hitBlacklistJustificacion).
    hitLprMotivo() {
      var ed = this.hitRaw.extendedData || {}
      var bl = this.hitBlacklist
      return ed.descripcion
        || (bl && (bl.reason || bl.justification))
        || '—'
    },

    // ── Lista negra (extendedData.blacklist) ────────────────────────────
    // El backend adjunta el match cuando la placa detectada coincide con una
    // entrada de la lista negra. Es lo más accionable del hit: distingue una
    // lectura LPR rutinaria de un vehículo con reporte de robo confirmado.
    hitBlacklist() {
      var ed = this.hitRaw.extendedData
      var bl = ed && ed.blacklist
      return (bl && typeof bl === 'object') ? bl : null
    },

    hitBlacklistCategoria() {
      var bl = this.hitBlacklist
      return (bl && (bl.categoryName || bl.categoryCode)) || '—'
    },

    // brand y model suelen venir iguales (ej. ITALIKA/ITALIKA) — no repetirlo.
    hitBlacklistVehiculo() {
      var bl = this.hitBlacklist
      if (!bl) return '—'
      var partes = [bl.brand, bl.model].filter(Boolean)
      if (partes.length === 2 && partes[0] === partes[1]) partes = [partes[0]]
      return partes.join(' ') || '—'
    },

    hitBlacklistColor() {
      var bl = this.hitBlacklist
      return (bl && bl.vehicleColor) || '—'
    },

    hitBlacklistVin() {
      var bl = this.hitBlacklist
      return (bl && bl.vin) || '—'
    },

    hitBlacklistJustificacion() {
      var bl = this.hitBlacklist
      var j = (bl && bl.justification) || ''
      // Si ya subió a la fila "Motivo" del bloque LPR, no repetirla aquí.
      if (j && this.hitIsLpr && j === this.hitLprMotivo) return ''
      return j
    },

    // Solo se muestra si NO coincide con la placa leída por el arco: en ese
    // caso el operador necesita ver contra qué placa se hizo el match.
    hitBlacklistPlacaDistinta() {
      var bl = this.hitBlacklist
      if (!bl || !bl.plate) return ''
      var detectada = (this.hitRaw.data && this.hitRaw.data.placa) || this.hitRaw.plate || ''
      return String(bl.plate).toUpperCase() === String(detectada).toUpperCase() ? '' : bl.plate
    },

    // Reincidencia: solo aporta si el vehículo ya fue detectado antes.
    hitBlacklistCoincidencias() {
      var bl = this.hitBlacklist
      var n = bl && Number(bl.matchCount)
      return (n && n > 1) ? String(n) : ''
    },

    // ── Razonamiento del hit ────────────────────────────────────────────
    // Id del AnalyticRecord — lo pide el PATCH del razonamiento.
    hitRecordId() {
      var raw = this.hitRaw
      return raw._id || raw.id || ''
    },

    // Overlay local primero (lo recién guardado), luego el payload del socket.
    hitRazonamiento() {
      var local = this.razLocal[this.hitRecordId]
      if (local) return local
      var d = this.hitRaw.data || {}
      if (!d.razonamiento) return null
      return {
        razonamiento: d.razonamiento,
        razonamientoTipo: d.razonamientoTipo,
        razonamientoBy: d.razonamientoBy,
      }
    },

    hitRazonamientoTipoMeta() {
      var r = this.hitRazonamiento
      return (r && RAZONAMIENTO_TIPOS_BY_ID[r.razonamientoTipo]) || null
    },

    hitRazonamientoAutor() {
      var r = this.hitRazonamiento
      var by = r && r.razonamientoBy
      return (by && (by.name || by.email)) || ''
    },

    canSaveRazonamiento() {
      return !!(this.razTipo && (this.razText || '').trim() && this.hitRecordId)
    },

    hitLprFoto() {
      var raw = this.hitRaw.data && this.hitRaw.data.foto
      if (!raw) return null
      var v = String(raw).trim()
      // Ya es data URI o URL absoluta → usarlo tal cual.
      if (v.indexOf('data:image') === 0) return v
      if (v.indexOf('http') === 0 || v.indexOf('//') === 0) return v
      if (v.charAt(0) === '/') return v
      // Asumir base64 JPEG (formato mas comun en capturas LPR). Igual
      // patron que MurphyDrawer.buildLprImageSrc.
      return 'data:image/jpeg;base64,' + v
    },

    // ── Reportant computed helpers (replican fieldsMap.reportant del viejo)
    // this.data es el payload crudo del reportant (persons[0] o equivalente).
    reportantLastPosition() {
      const d = this.data
      if (!d) return null
      if (Array.isArray(d.positions) && d.positions.length > 0) {
        return d.positions[d.positions.length - 1]
      }
      return d.lastPosition || null
    },
    reportantFirstPosition() {
      const d = this.data
      if (d && Array.isArray(d.positions) && d.positions.length > 0) {
        return d.positions[0]
      }
      return null
    },
    reportantAddress() {
      const pos = this.reportantLastPosition
      return (pos && pos.additionalData && pos.additionalData.address) || null
    },
    // Phone raw (sin formato, exactamente como llega del backend). Lo
    // necesita el handler de "Llamar" para invocar el tel: link y los
    // computeds de formato (parsePhone hace el resto).
    reportantPhoneRaw() {
      return (this.data && this.data.additionalData && this.data.additionalData.phone)
        || this.monitor.phone
        || ''
    },
    // Phone parseado: { iso, prefix, national, formatted, flag }.
    // Util para el header (entityTitle), el row "Teléfono" (HTML con
    // bandera) y cualquier otro consumidor que necesite el formato MX o
    // la bandera del país detectado.
    reportantPhoneParsed() {
      return parsePhone(this.reportantPhoneRaw)
    },
    // Valor del row "Teléfono" del drawer. Renderiza la bandera del país
    // detectado + el número en formato nacional (mismo patrón que la
    // card del sidebar). Devuelve HTML para que DrawerZoneRow lo inyecte
    // con `html=true` y respete la bandera como caracter (no como texto
    // escapado). El raw y el formato vienen del backend / parsePhone,
    // ninguno es input del usuario — sin riesgo de XSS.
    reportantPhone() {
      var parsed = this.reportantPhoneParsed
      if (!parsed || !parsed.formatted) return this.reportantPhoneRaw || '—'
      var flag = parsed.flag
        ? '<span class="phone-flag">' + parsed.flag + '</span> '
        : ''
      return flag + parsed.formatted
    },
    reportantCallStart() {
      const pos = this.reportantFirstPosition
      const ts = pos && pos.additionalData && pos.additionalData.call_start_time
      return this.formatLongDate(ts)
    },
    // Tiempo relativo desde la fecha de llamada ("hace 5 min"). Se enlaza
    // a drawerFreshnessTick para que dayjs.fromNow() reactive y el chip
    // se actualice solo cada cierto intervalo sin necesidad de cambiar
    // selectedItem.
    reportantCallStartRelative() {
      // eslint-disable-next-line no-unused-expressions
      this.drawerFreshnessTick
      const pos = this.reportantFirstPosition
      const ts = pos && pos.additionalData && pos.additionalData.call_start_time
      if (!ts) return ''
      // call_start_time viene como string de ms epoch (RAPID-SOS / Carbyne).
      const num = typeof ts === 'string' ? parseInt(ts, 10) : ts
      if (!num || isNaN(num)) return ''
      return timeAgo(num)
    },
    reportantLastUpdate() {
      const pos = this.reportantLastPosition
      const ts = pos && pos.additionalData && pos.additionalData.location_time
      return this.formatLongDate(ts)
    },
    reportantCadId() {
      return (this.data && this.data.cadId) || 'DESCONOCIDO'
    },
    reportantCoordsLink() {
      const pos = this.reportantLastPosition
      if (!pos || pos.latitude == null || pos.longitude == null) return '—'
      const lat = (pos.latitude + '0000000000').substring(0, 10)
      const lng = (pos.longitude + '0000000000').substring(0, 10)
      return '<a href="https://maps.google.com/maps?q=' + pos.latitude + '%2C' + pos.longitude
        + '&z=17&hl=es" target="_blank" rel="noopener">' + lat + ', ' + lng + '</a>'
    },
    reportantDireccion() {
      const addr = this.reportantAddress
      return (addr && addr.direccion) || 'Desconocido'
    },
    reportantMunicipio() {
      const addr = this.reportantAddress
      return (addr && addr.municipio) || 'Desconocido'
    },
    reportantPoblacion() {
      const addr = this.reportantAddress
      return (addr && addr.poblacion) || 'Desconocido'
    },
    reportantColonia() {
      const addr = this.reportantAddress
      return (addr && addr.colonia) || 'Desconocido'
    },
    reportantCP() {
      const addr = this.reportantAddress
      return (addr && addr.cp) || 'Desconocido'
    },
    reportantFuente() {
      const sys = (this.data && this.data.additionalData && this.data.additionalData.systemName) || ''
      if (sys === 'CARBYNE') return 'CARBYNE'
      if (sys === 'DIDI') return 'DIDI'
      if (this.data && this.data.additionalData && this.data.additionalData.uber) return 'Uber'
      return sys || 'RapidSOS'
    },

    // Metadata completa del proveedor de interconexión externa
    // (logo + label) para el bloque "Fuente" del drawer del reportante.
    // Reemplaza el row plano con texto por uno con logo del proveedor —
    // identificación visual instantánea, paridad con la columna FUENTE
    // del fieldsMap.reportant legacy. Pasamos 'reportant' como
    // entityType para que el detector defaultee a 'rapidsos' cuando
    // additionalData.systemName no esté presente (todo reportante usa
    // la red RapidSOS por definición).
    reportantPartner() {
      var id = detectPartnerSystem(this.data, 'reportant')
      return id ? getPartnerMeta(id) : null
    },

    // Idem para el incident: solo retorna proveedor cuando el incident
    // fue enriquecido con info del trip socket (rapidSosLocations o
    // systemName adjunto). Si retorna null la sección "Interconexión"
    // del drawer queda oculta.
    incidentPartner() {
      if (this.entityType !== 'incident') return null
      var id = detectPartnerSystem(this.data, 'incident')
      return id ? getPartnerMeta(id) : null
    },

    // Texto auxiliar bajo el label del incidentPartner — informa que
    // hay GPS del reportante disponible y cuántos puntos llegaron.
    incidentPartnerHint() {
      var locations = this.data && this.data.rapidSosLocations
      if (Array.isArray(locations) && locations.length > 0) {
        return 'GPS del reportante en tiempo real (' + locations.length + ' punto'
          + (locations.length === 1 ? '' : 's') + ')'
      }
      return ''
    },
    reportantUber() {
      const uber = this.data && this.data.additionalData && this.data.additionalData.uber
      if (!uber) return null
      const vehicle = uber.vehicle_info
      const user = uber.user_info
      let str = ''
      if (vehicle) {
        str += (vehicle.vehicle_make || '') + ' ' + (vehicle.vehicle_model || '') + ' ' + (vehicle.vehicle_color || '') + '<br>'
        str += 'Placa: ' + (vehicle.license_plate || '') + ' [' + (vehicle.license_plate_state || '') + ']<br>'
      }
      if (user) {
        str += '<b>REPORTANTE</b><br>'
        str += (user.user_name || '') + '<br>'
        str += 'TEL: ' + (user.user_phone || '') + '<br>'
      }
      return str || null
    },
    reportantDidi() {
      const add = this.data && this.data.additionalData
      if (!add || add.systemName !== 'DIDI') return null
      const didi = add.didiInfo
      if (!didi) return null
      let str = ''
      str += 'NOMBRE: ' + (didi.nombre || '') + ' ' + (didi.apellido || '') + '<br>'
      str += 'TIPO: ' + (didi.rol || '') + '<br>'
      str += 'TEL: ' + (this.data.telefono || add.phone || 'DESCONOCIDO') + '<br>'
      str += 'EN VIAJE: ' + (didi.enViaje != null ? didi.enViaje : '—') + '<br>'
      if (didi.vehiculo) {
        str += 'VEHICULO: ' + (didi.vehiculo.marca || '') + ' ' + (didi.vehiculo.color || '') + '<br>'
        str += 'PLACA: ' + (didi.vehiculo.placas || '') + '<br>'
      }
      return str
    },

    cameraStatusText() {
      const s = this.data.status
      if (s === 'up') return 'En línea'
      if (s === 'down') return 'Fuera de línea'
      return s || '—'
    },

    // Cached sub-cameras for the currently expanded nearby item
    expandedNearbyCameras() {
      if (!this.expandedNearby || !this.nearby) return []
      var allItems = (this.nearby.cameras || []).concat(this.nearby.units || [])
      var expanded = null
      for (var i = 0; i < allItems.length; i++) {
        if (allItems[i].id === this.expandedNearby) { expanded = allItems[i]; break }
      }
      return expanded ? this.getSubCameras(expanded) : []
    },

    busLastUpdate() {
      if (!this.data || !this.data.lastUpdate) return '—'
      return this.formatFullDate(this.data.lastUpdate)
    },

    // ── SOS Call computed helpers (DRAWERS-VARIANTS-SPEC §4) ──
    // La unidad más crítica del sistema; énfasis en urgencia y tiempo.
    sosCallerName() {
      var d = this._sosData()
      var ud = d.userData || {}
      var name = ud.name || ud.displayName || d.name
      if (name && String(name).trim()) return String(name).trim()
      return this._sosPhone() !== '—' ? this._sosPhone() : 'Sin identificar'
    },

    sosStatusText() {
      var s = (this._sosData() && this._sosData().status) || ''
      if (s === 'WAITING_OPERATOR') return 'Esperando atención'
      if (s === 'IN_PROGRESS') return 'En atención'
      if (s === 'CLIENT_END_CALL') return 'Finalizada por usuario'
      if (s === 'OPERATOR_END_CALL') return 'Finalizada por operador'
      if (s === 'ENDED') return 'Finalizada'
      return s || '—'
    },

    sosStatusColor() {
      var s = (this._sosData() && this._sosData().status) || ''
      if (s === 'WAITING_OPERATOR') return 'var(--kln-alert-danger)'
      if (s === 'IN_PROGRESS') return 'var(--kln-status-online)'
      if (s === 'CLIENT_END_CALL' || s === 'OPERATOR_END_CALL' || s === 'ENDED') return 'var(--kln-text-dim)'
      return 'var(--kln-text-muted)'
    },

    sosIsWaiting() {
      var s = (this._sosData() && this._sosData().status) || ''
      return s === 'WAITING_OPERATOR'
    },

    sosIsInProgress() {
      var s = (this._sosData() && this._sosData().status) || ''
      return s === 'IN_PROGRESS'
    },

    // El reportante colgó (CLIENT_END_CALL) pero la llamada sigue en la cola del
    // operador: NO es terminal. Mientras sea la llamada que este operador
    // atiende, debe poder finalizarla (end_call) igual que el legacy Map.vue.
    sosIsClientEnded() {
      var s = (this._sosData() && this._sosData().status) || ''
      return s === 'CLIENT_END_CALL'
    },

    // Llamada SOS que ESTE operador ya está atendiendo (store.map.sosActiveCall).
    // Permite distinguir "mi llamada en curso" (botón Finalizar) de "llamada de
    // otro operador" (botón Unirse). Habilita el multi-operador con paridad al
    // legacy Map.vue:2037, que permite tomar una llamada IN_PROGRESS si el
    // operador no tiene ya una llamada activa propia.
    sosActiveCall() {
      return (this.$store && this.$store.state.map && this.$store.state.map.sosActiveCall) || null
    },

    sosCallId() {
      var d = this._sosData()
      return (d && (d._id || d.id || d.callId)) || null
    },

    sosHasActiveCall() {
      var a = this.sosActiveCall
      return !!(a && a._id)
    },

    sosIsMine() {
      var a = this.sosActiveCall
      return !!(a && a._id && this.sosCallId && a._id === this.sosCallId)
    },

    sosIsCreated() {
      var s = (this._sosData() && this._sosData().status) || ''
      return s === 'CREATED'
    },

    sosIsEnded() {
      var s = (this._sosData() && this._sosData().status) || ''
      return s === 'CLIENT_END_CALL' || s === 'OPERATOR_END_CALL' || s === 'ENDED'
    },

    sosStartedAt() {
      var d = this._sosData()
      return d.startedAt || d.createdAt || d.initiatedAt || null
    },

    // Tiempo de FIN de la llamada — para congelar la duración de llamadas
    // finalizadas (si no, el counter cuenta contra Date.now() y crece para
    // siempre). El backend COLIBRI no manda un campo de fin explícito; el
    // cliente estampa endedAtClient cuando observa la transición a finalizada
    // en esta sesión (MapV2.onSOSCall). Para llamadas ya-finalizadas del
    // snapshot active_calls no hay fin conocido → null → no se inventa duración.
    sosEndedAt() {
      var d = this._sosData()
      if (!d) return null
      return d.endedAtClient || d.endedAt || d.endDate || d.finishedAt
        || d.endTime || d.dateEnd || null
    },

    sosTiempoTranscurrido() {
      // eslint-disable-next-line no-unused-expressions
      this.drawerFreshnessTick
      var ts = this.sosStartedAt
      if (!ts) return ''
      var n = typeof ts === 'number' ? ts : Date.parse(ts)
      if (isNaN(n)) return ''
      // Llamada finalizada: congelar en (fin - inicio). Sin tiempo de fin
      // conocido devolvemos '' (la fila se oculta) en vez de un número engañoso
      // y creciente. Llamada activa: corre contra Date.now().
      var endRef
      if (this.sosIsEnded) {
        var e = this.sosEndedAt
        if (!e) return ''
        endRef = typeof e === 'number' ? e : Date.parse(e)
        if (isNaN(endRef) || endRef < n) return ''
      } else {
        endRef = Date.now()
      }
      var diff = Math.max(0, Math.floor((endRef - n) / 1000))
      var h = Math.floor(diff / 3600)
      var m = Math.floor((diff % 3600) / 60)
      var s = diff % 60
      if (h) return h + 'h ' + m + 'm ' + s + 's'
      if (m) return m + 'm ' + s + 's'
      return s + 's'
    },

    sosLastPosition() {
      var d = this._sosData()
      return d.lastPosition || (Array.isArray(d.positionsHistory) && d.positionsHistory.length
        ? d.positionsHistory[d.positionsHistory.length - 1]
        : null)
    },

    sosPosicion() {
      var p = this.sosLastPosition
      if (!p) return '—'
      var lat = p.latitude != null ? p.latitude : p.lat
      var lng = p.longitude != null ? p.longitude : p.lng
      if (lat == null || lng == null) return '—'
      return Number(lat).toFixed(5) + ', ' + Number(lng).toFixed(5)
    },

    sosPosicionFlashKey() {
      var p = this.sosLastPosition
      if (!p) return ''
      return (p.latitude || p.lat || '') + ',' + (p.longitude || p.lng || '')
    },

    sosDireccion() {
      var p = this.sosLastPosition
      if (!p) return ''
      var ad = (p.additionalData && p.additionalData.address) || p.address || {}
      return ad.direccion || ad.formatted || ''
    },

    sosPrimaryAction() {
      // "Finalizar llamada" (emite end_call). Se ofrece en dos casos:
      //  - Mi llamada EN CURSO (IN_PROGRESS y mía): la termina para todos los
      //    participantes vía finalizarLlamada() (meeting.end + teardown).
      //  - CUALQUIER llamada cuyo reportante ya colgó (CLIENT_END_CALL), sea mía
      //    o no: el operador puede finalizarla por su id, igual que el legacy
      //    Map.vue (selectSOSCall → emit('end_call', item.id)). El handler
      //    onDrawerSOSFinalizar distingue por id: si es la activa hace teardown
      //    completo; si no, solo emite end_call sobre esa llamada sin tocar la
      //    activa del operador.
      if ((this.sosIsInProgress && this.sosIsMine) || this.sosIsClientEnded) {
        return {
          event: 'sos-finalizar',
          label: 'Finalizar llamada',
          icon: 'mdi-phone-hangup',
          tone: 'danger',
          big: false,
        }
      }
      // Llamada esperando atención → atender (comportamiento original).
      if (this.sosIsWaiting) {
        return {
          event: 'sos-atender',
          label: 'ATENDER LLAMADA',
          icon: 'mdi-phone',
          tone: 'success',
          big: true,
        }
      }
      // Llamada en curso por OTRO operador y este operador está libre → puede
      // UNIRSE. Paridad con legacy Map.vue:2037 (permite take_call sobre una
      // llamada IN_PROGRESS cuando !sosActiveCall). El join real es vía la sala
      // VideoSDK (multi-participante), reusando la misma ruta que "Atender".
      if (this.sosIsInProgress && !this.sosHasActiveCall) {
        return {
          event: 'sos-atender',
          label: 'UNIRSE A LLAMADA',
          icon: 'mdi-phone-plus',
          tone: 'success',
          big: true,
        }
      }
      // CREATED: llamada recién creada (aún sin enrutar a operador). No hay
      // flujo de "atender" para este estado; el operador puede descartarla
      // (emite end_call) para que no quede atascada en el mapa.
      if (this.sosIsCreated) {
        return {
          event: 'sos-descartar',
          label: 'Descartar llamada',
          icon: 'mdi-phone-cancel',
          tone: 'danger',
          big: false,
        }
      }
      return null
    },

    sosActionContext() {
      if (this.sosIsWaiting) {
        var elapsed = this.sosTiempoTranscurrido
        return elapsed
          ? 'Llamada esperando atención desde hace ' + elapsed + '.'
          : 'Llamada esperando atención.'
      }
      if (this.sosIsInProgress) {
        var op = this._sosData() && this._sosData().operatorName
        var base = 'En atención.' + (op ? ' Operador: ' + op + '.' : '')
        // Llamada de OTRO operador y este está libre → puede sumarse a la
        // atención (multi-operador). Lo indicamos para que el botón "Unirse"
        // tenga contexto.
        if (!this.sosIsMine && !this.sosHasActiveCall) {
          return base + ' Puedes unirte a la atención.'
        }
        return base
      }
      if (this.sosIsEnded) {
        var dur = this.sosTiempoTranscurrido
        // Sin duración conocida (backend sin tiempo de fin) NO mostramos
        // "Duración: —" — solo "Llamada finalizada." para no parecer incompleto.
        return dur ? 'Llamada finalizada. Duración: ' + dur + '.' : 'Llamada finalizada.'
      }
      return ''
    },

    sosHasCallerName() {
      var d = this._sosData()
      var ud = d.userData || {}
      return !!(ud.name || ud.displayName || d.name)
    },

    // ── Incident computed helpers (DRAWERS-VARIANTS-SPEC §5) ──
    incidentPrioridad() {
      return this.incidentAttrs.prioridad || ''
    },

    incidentPrioridadColor() {
      var p = (this.incidentPrioridad || '').toUpperCase()
      if (p === 'ALTA') return 'var(--kln-alert-danger)'
      if (p === 'MEDIA') return 'var(--kln-status-alert)'
      if (p === 'BAJA') return 'var(--kln-text-dim)'
      return 'var(--kln-text-muted)'
    },

    incidentPrioridadIsAlta() {
      return (this.incidentPrioridad || '').toUpperCase() === 'ALTA'
    },

    incidentEstado() {
      return (this.data && this.data.status) || '—'
    },

    incidentEstadoColor() {
      var s = this.incidentEstado
      if (s === 'ACTIVO' || s === 'alert') return 'var(--kln-alert-danger)'
      if (s === 'RESUELTO' || s === 'CERRADO') return 'var(--kln-status-online)'
      if (s === 'DESCARTADO' || s === 'CANCELADO') return 'var(--kln-text-dim)'
      return 'var(--kln-text-muted)'
    },

    incidentFechaRelativa() {
      // eslint-disable-next-line no-unused-expressions
      this.drawerFreshnessTick
      var ts = this.incidentAttrs.fecha || (this.data && this.data.createdAt)
      return ts ? timeAgo(ts) : '—'
    },

    incidentCatalogRelativa() {
      // eslint-disable-next-line no-unused-expressions
      this.drawerFreshnessTick
      var ts = this.data && (this.data.createdAt || this.data.created_at)
      return ts ? timeAgo(ts) : '—'
    },

    incidentHasAssignment() {
      var corps = this.incidentAttrs.corporaciones
      return Array.isArray(corps) && corps.length > 0
    },

    // Botón "Editar" del header de acciones del incidente. CAD no tiene
    // un permiso específico para "editar" — los 4 permisos formales son
    // create/assign_units/add_note/close. Usamos cad_permission_create_incident
    // como proxy semántico ("quien puede crear puede editar"), alineado a
    // que ambos endpoints viven bajo /cad-incidents/.
    canEditIncident() {
      if (this.entityType !== 'incident') return false
      var u = (this.$store && this.$store.state && this.$store.state.auth)
        ? this.$store.state.auth.user : null
      if (!u || !u.attributes) return false
      if (!u.attributes.cad_user) return false
      return !!u.attributes.cad_permission_create_incident
    },

    // Cantidad real de unidades despachadas (no de corporaciones, que son
    // el nivel organizacional). attrs.corporaciones puede tener 3 items
    // sin que haya ni una sola unidad asignada todavía. attrs.unidades
    // es el array de unidades concretas (cada una con .corporacion, .unit,
    // .status, .statusHistory).
    incidentUnidadesCount() {
      var arr = this.incidentAttrs.unidades
      return Array.isArray(arr) ? arr.length : 0
    },

    incidentCorporacionesCount() {
      var arr = this.incidentAttrs.corporaciones
      return Array.isArray(arr) ? arr.length : 0
    },

    incidentIsActive() {
      var s = this.incidentEstado
      return s === 'ACTIVO' || s === 'alert'
    },

    incidentTipo() {
      var d = this.data
      return (d && (d.tipoIncidente || (d.categoryData && d.categoryData.name) || d.type)) || '—'
    },

    // Grupo CNIE del incidente — comparte resolver con el marker del
    // mapa para que header del drawer, chip de categoría y marker se
    // perciban con el mismo color y label.
    incidentCategoriaLabel() {
      return getIncidentCategoryLabel(this.data || {})
    },

    incidentCategoriaColor() {
      return getIncidentColorVar(this.data || {})
    },

    incidentPrimaryAction() {
      if (!this.incidentIsActive) return null
      if (!this.incidentHasAssignment && this.canDispatchIncident) {
        return {
          event: 'dispatch-incident',
          label: 'Despachar incidente',
          icon: 'mdi-alert-circle',
          tone: this.incidentPrioridadIsAlta ? 'danger' : 'accent',
        }
      }
      return null
    },

    incidentActionContext() {
      var p = this.incidentPrioridad
      if (!this.incidentIsActive) {
        return 'Incidente en estado: ' + this.incidentEstado + '.'
      }
      var prText = p ? 'prioridad ' + p + '. ' : ''
      var nUnits = this.incidentUnidadesCount
      var nCorps = this.incidentCorporacionesCount
      // Caso 1: ni siquiera tiene corporaciones (incidente recién creado).
      if (nCorps === 0) {
        return 'Incidente ' + prText + 'Reportado ' + this.incidentFechaRelativa + '. Sin corporaciones asignadas.'
      }
      // Caso 2: tiene corporaciones pero todavía cero unidades despachadas.
      if (nUnits === 0) {
        return 'Incidente ' + prText + 'Asignado a ' + nCorps + ' corporación'
          + (nCorps === 1 ? '' : 'es') + '. Sin unidades despachadas todavía.'
      }
      // Caso 3: con unidades despachadas reales.
      return 'Incidente despachado. ' + nUnits + ' unidad'
        + (nUnits === 1 ? '' : 'es') + ' asignada' + (nUnits === 1 ? '' : 's')
        + ' en ' + nCorps + ' corporación' + (nCorps === 1 ? '' : 'es') + '.'
    },

    incidentHasReportanteName() {
      return !!(this.incidentAttrs.nombreCompletoCiudadano &&
        String(this.incidentAttrs.nombreCompletoCiudadano).trim().length > 0)
    },

    // ── Sensor computed helpers (DRAWERS-VARIANTS-SPEC §3) ──
    // Estado textualizado, con reactividad por drawerFreshnessTick para que
    // el tick de tiempo transcurrido dispare re-render sin watchers caros.
    sensorStatusText() {
      // eslint-disable-next-line no-unused-expressions
      this.drawerFreshnessTick
      return this.getSensorEstado()
    },

    sensorStatusColor() {
      var s = this.data && this.data.status
      if (s === 'alert') return 'var(--kln-alert-danger)'
      if (s === 'reported') return 'var(--kln-status-alert)'
      if (s === 'up') return 'var(--kln-status-online)'
      if (s === 'down') return 'var(--kln-text-dim)'
      return 'var(--kln-text-muted)'
    },

    sensorHasActiveAlert() {
      return !!(this.data && this.data.status === 'alert')
    },

    sensorActivatedAt() {
      var d = this.data
      if (!d) return null
      return d.activatedAt || d.alertStartedAt || d.lastUpdate || null
    },

    sensorTiempoActivacion() {
      // eslint-disable-next-line no-unused-expressions
      this.drawerFreshnessTick
      if (!this.sensorHasActiveAlert) return ''
      var ts = this.sensorActivatedAt
      return ts ? timeAgo(ts) : ''
    },

    sensorUbicacion() {
      var d = this.data
      if (!d) return '—'
      var lat = d.latitude || (d.position && d.position.latitude)
      var lng = d.longitude || (d.position && d.position.longitude)
      if (lat == null || lng == null) return '—'
      return Number(lat).toFixed(5) + ', ' + Number(lng).toFixed(5)
    },

    sensorLastUpdateRelative() {
      // eslint-disable-next-line no-unused-expressions
      this.drawerFreshnessTick
      var d = this.data
      var ts = d && (d.lastUpdate || d.updatedAt || d.serverTime)
      return ts ? timeAgo(ts) : '—'
    },

    sensorEstablecimiento() {
      var d = this.data
      return (d && (d.fiscalName || d.establecimiento || '—')) || '—'
    },

    // Acción primaria según estado del sensor. Paridad con spec §3.3.
    sensorPrimaryAction() {
      var s = this.data && this.data.status
      if (s === 'alert') {
        // Reportar, no descartar. Durante todo el tiempo en que la única salida
        // ofrecida fue descartar, el resultado medido fueron 0 reportadas y 0
        // `folioCad` en 2,690 alertas de un año: descartar es la excepción y
        // estaba ocupando el sitio de la regla. Descartar sigue disponible, en
        // el botón `Descartar` de la barra de acciones.
        return {
          event: 'report-cad',
          label: 'Reportar al CAD',
          icon: 'mdi-file-document-edit-outline',
          tone: 'warning',
        }
      }
      if (s === 'up' && this.canGenerateSensorAlert) {
        return {
          event: 'generate-sensor-alert',
          label: 'Generar alerta manual',
          icon: 'mdi-alert-plus',
          tone: 'warning',
        }
      }
      return null
    },

    sensorActionContext() {
      var s = this.data && this.data.status
      if (s === 'alert') {
        var elapsed = this.sensorTiempoActivacion
        return elapsed
          ? 'Alerta activa desde ' + elapsed + '. Sin atención asignada.'
          : 'Alerta activa. Sin atención asignada.'
      }
      if (s === 'reported') {
        return 'Alerta reportada ' + this.sensorLastUpdateRelative + '. Pendiente de cierre.'
      }
      if (s === 'up') {
        return 'Sensor operativo. Última señal ' + this.sensorLastUpdateRelative + '.'
      }
      if (s === 'down') return 'Sensor fuera de línea.'
      return ''
    },

    // ── Bus computed helpers (DRAWERS-VARIANTS-SPEC §2) ──
    busLastUpdateRelative() {
      // eslint-disable-next-line no-unused-expressions
      this.drawerFreshnessTick
      var d = this.data
      var ts = d && (d.lastUpdate || d.updatedAt || d.serverTime)
      return ts ? timeAgo(ts) : '—'
    },

    busStatusText() {
      var s = (this.data && this.data.status) || ''
      if (s === 'alert') return 'Alerta'
      if (s === 'up' || s === 'online') return 'En línea'
      if (s === 'down' || s === 'offline') return 'Fuera de línea'
      return s || '—'
    },

    busStatusColor() {
      var s = (this.data && this.data.status) || ''
      if (s === 'alert') return 'var(--kln-alert-danger)'
      if (s === 'up' || s === 'online') return 'var(--kln-status-online)'
      if (s === 'down' || s === 'offline') return 'var(--kln-text-dim)'
      return 'var(--kln-text-muted)'
    },

    busVelocidad() {
      var p = this.data && this.data.position
      var s = (p && p.speed) != null ? p.speed : (this.data && this.data.speed)
      if (s == null) return '—'
      var kmh = typeof s === 'number' && s < 50 ? s * 1.852 : s
      return Math.round(kmh) + ' km/h'
    },

    busUbicacion() {
      var d = this.data
      if (!d) return '—'
      var lat = d.latitude || (d.position && d.position.latitude)
      var lng = d.longitude || (d.position && d.position.longitude)
      if (lat == null || lng == null) return '—'
      return Number(lat).toFixed(5) + ', ' + Number(lng).toFixed(5)
    },

    busHasAlert() {
      return !!(this.data && this.data.status === 'alert')
    },

    // Tiempo transcurrido desde que entró en alerta (si el backend lo marca
    // en data.alertStartedAt; si no, usamos lastUpdate como aproximación).
    busAlertElapsed() {
      // eslint-disable-next-line no-unused-expressions
      this.drawerFreshnessTick
      if (!this.busHasAlert) return ''
      var ts = this.data.alertStartedAt || this.data.lastUpdate
      return ts ? timeAgo(ts) : ''
    },

    busActionContext() {
      if (this.busHasAlert) {
        var elapsed = this.busAlertElapsed
        return elapsed
          ? 'Alerta activa desde ' + elapsed + '. Verificar cámaras embebidas.'
          : 'Alerta activa. Verificar cámaras embebidas.'
      }
      return 'Bus operativo. Última señal ' + this.busLastUpdateRelative + '.'
    },

    // Incident computed helpers
    /**
     * Cache key estable para pmiCameras — solo cambia cuando cambia la
     * config de cámaras (no la posición). Tres formatos (ver pmi-cameras.js):
     *   PMI: attributes.dev0, dev1, ... = base64(JSON)
     *   Device/Bus: attributes.cameras = array
     *   LPR: cameras top-level del analytic-device
     * Importante: el id/uniqueId/identifier SIEMPRE forma parte del key —
     * un key vacío compartido entre items haría que el caché sirviera las
     * cámaras de un LPR al seleccionar otro.
     */
    pmiCamerasKey() {
      if (!this.data) return ''
      var attrs = this.data.attributes || {}
      var parts = [
        this.data.id || '',
        this.data.uniqueId || this.data.identifier || '',
      ]
      // PMI style
      var devKeys = Object.keys(attrs).filter(function(k) {
        return k.slice(0, 3) === 'dev' && !isNaN(parseInt(k.slice(3)))
      }).sort()
      for (var i = 0; i < devKeys.length; i++) {
        parts.push(devKeys[i] + ':' + (attrs[devKeys[i]] || '').slice(0, 20))
      }
      // Device/Bus style (attributes.cameras) y LPR (cameras top-level).
      // Se hashean ambas fuentes; extractPmiCameras decide la prioridad.
      var sources = [attrs.cameras, this.data.cameras]
      for (var s = 0; s < sources.length; s++) {
        var arr = Array.isArray(sources[s]) ? sources[s] : []
        for (var j = 0; j < arr.length; j++) {
          var c = arr[j]
          parts.push((c && c.server) + ':' + (c && c.name))
        }
      }
      return parts.join('|')
    },

    pmiCameras() {
      // Caché por pmiCamerasKey: estabiliza las refs de los iframes a través de
      // los updates de WS (sin esto, cada update recrearía el array y el iframe
      // recargaría). La extracción vive en el helper compartido pmi-cameras.js
      // (misma fuente que el pre-warm del tray en MapV2).
      if (this._pmiCamerasCache && this._pmiCamerasCache.key === this.pmiCamerasKey) {
        return this._pmiCamerasCache.cameras
      }
      var cameras = extractPmiCameras(this.data)
      this._pmiCamerasCache = { key: this.pmiCamerasKey, cameras: cameras }
      return cameras
    },

    incidentAttrs() {
      return (this.data && this.data.attributes) || {}
    },

    // ── Sismo ── (payload con campos en español latitud/longitud)
    sismoCoords() {
      var lat = this.data && (this.data.latitud != null ? this.data.latitud : this.data.latitude)
      var lng = this.data && (this.data.longitud != null ? this.data.longitud : this.data.longitude)
      if (lat == null || lng == null) return '—'
      return Number(lat).toFixed(6) + ', ' + Number(lng).toFixed(6)
    },

    // ── Presa ── (render compuesto de elevación + almacenamiento)
    presaNamo() {
      var e = this.data.namoElev != null ? this.data.namoElev : '—'
      var f = this.data.namoFill != null ? this.data.namoFill : '—'
      return '<strong>Elevación:</strong> ' + e + ' msnm · <strong>Almacenamiento:</strong> ' + f + ' hm³'
    },

    presaName() {
      var e = this.data.nameElev != null ? this.data.nameElev : '—'
      var f = this.data.nameFill != null ? this.data.nameFill : '—'
      return '<strong>Elevación:</strong> ' + e + ' msnm · <strong>Almacenamiento:</strong> ' + f + ' hm³'
    },

    // ── CPR (Centro Penitenciario) ──
    // this.data apunta al monitor.data (payload crudo del item en
    // cprs-data.js). Fields: ubicacion, poblacion, hombres, mujeres,
    // gruposDelictivosZona, latitude, longitude.
    cprData() {
      return this.data || {}
    },

    cprPoblacion() {
      var p = this.cprData.poblacion
      return (p != null) ? this.formatNumber(p) + ' internos' : '—'
    },

    // Lista de grupos delictivos como string separado por bullets para
    // que quepa legible en el drawer (en lugar de una comma-list larga).
    cprGruposDelictivos() {
      var arr = this.cprData.gruposDelictivosZona
      if (!Array.isArray(arr) || !arr.length) return ''
      return arr.join(' · ')
    },

    incidentCoords() {
      if (!this.data || !this.data.latitude) return '—'
      return this.data.latitude.toFixed(6) + ', ' + this.data.longitude.toFixed(6)
    },

    // URL de Google Maps para el botón "Maps" del header de acciones.
    // Reemplaza el link de la fila Ubicación (que se eliminó del listado
    // porque las coords no aportaban información legible al operador).
    incidentMapsUrl() {
      if (!this.data || this.data.latitude == null || this.data.longitude == null) return ''
      return 'https://maps.google.com/maps?q='
        + this.data.latitude + ',' + this.data.longitude
        + '&z=17&hl=es'
    },

    // Tooltip del botón "Maps" — al hacer hover muestra las coords del
    // incidente sin necesidad de label en el botón. Al hacer click se abre
    // Google Maps (acción auto-explicativa por el icono).
    incidentMapsTooltip() {
      if (!this.data || this.data.latitude == null || this.data.longitude == null) return ''
      var lat = Number(this.data.latitude).toFixed(6)
      var lng = Number(this.data.longitude).toFixed(6)
      return lat + ', ' + lng
    },

    // Lista normalizada de corporaciones asignadas al incidente (array
    // de strings) para el dropdown del modal "Agregar nota". La API puede
    // devolver attrs.corporaciones como array de strings o de objetos
    // {corporacion: '...'}; aplanamos a strings para que el VAutocomplete
    // las muestre como opciones separadas.
    incidentCorporaciones() {
      var a = this.incidentAttrs
      if (!Array.isArray(a.corporaciones)) return []
      return a.corporaciones.map(function(c) { return c && c.corporacion ? c.corporacion : c })
    },

    // Permiso CAD del usuario para escribir notas en incidentes. Si la API
    // de session no lo devuelve, mostramos el botón igual y dejamos que el
    // backend valide (mismo comportamiento defensivo que IncidentDispatch
    // legacy, que oculta el botón solo si la flag está explícitamente false).
    canAddIncidentNote() {
      if (this.entityType !== 'incident') return false
      var user = (this.$store && this.$store.state && this.$store.state.auth)
        ? this.$store.state.auth.user : null
      if (!user || !user.attributes) return true
      return user.attributes.cad_permission_add_note !== false
    },

    canSaveAddNote() {
      return !!(this.addNoteText && this.addNoteText.trim() && this.addNoteCorp)
    },

    // True si el operador modificó al menos un campo respecto al snapshot
    // tomado al abrir el modal. Evita POSTs vacíos al backend.
    editHasChanges() {
      if (!this.editFormOriginal) return false
      var keys = Object.keys(this.editForm)
      for (var i = 0; i < keys.length; i++) {
        var k = keys[i]
        if (this.editForm[k] !== this.editFormOriginal[k]) return true
      }
      return false
    },

    incidentAddress() {
      var a = this.incidentAttrs
      if (a.lugar) return a.lugar.trim()
      return [a.calle, a.colonia, a.municipio].filter(Boolean).map(function(s) { return s.trim() }).join(', ') || ''
    },

    // Versión texto plano para mostrar en el header del drawer.
    // Antes este computed tambien se llamaba incidentCorporaciones y
    // pisaba al de arriba (en object literals, la 2da clave gana), por
    // eso el VAutocomplete de "Agregar nota" recibia un string y
    // mostraba "CRUM, CMMTOL, PE" como una sola opcion.
    incidentCorporacionesText() {
      return this.incidentCorporaciones.join(', ')
    },

    incidentCoordsHtml() {
      if (!this.data) return '—'
      var lat = parseFloat(this.data.latitude)
      var lng = parseFloat(this.data.longitude)
      if (isNaN(lat) || isNaN(lng)) return '—'
      var text = lat.toFixed(6) + ', ' + lng.toFixed(6)
      return '<a href="https://maps.google.com/maps?q=' + lat + ',' + lng + '&z=17&hl=es" target="_blank" rel="noopener noreferrer">' + text + '</a>'
    },

    incidentUberInfo() {
      var uber = this.data && this.data.uber
      if (!uber) return null
      var vehicle = uber.vehicle_info || {}
      var user = uber.user_info || {}
      var parts = [vehicle.vehicle_make, vehicle.vehicle_model, vehicle.vehicle_color].filter(Boolean)
      var plate = vehicle.license_plate
        ? vehicle.license_plate + (vehicle.license_plate_state ? ' [' + vehicle.license_plate_state + ']' : '')
        : null
      return {
        vehicle: parts.length ? parts.join(' ') : null,
        plate: plate,
        userName: user.user_name || null,
        userPhone: user.user_phone || null,
      }
    },

    incidentNotas() {
      // Todas las notas de todas las corporaciones, ordenadas por fecha
      // descendente (más recientes primero). Paridad con el legacy
      // IncidentDispatch.vue que es el viewer canónico de notas.
      //
      // Antes filtrábamos por corporaciones[0] (paridad con un Map.vue
      // viejo) y eso perdía las notas de CMMNAU/PE cuando el incidente
      // tenía múltiples corps asignadas. Cada nota lleva su corporación
      // y autor visibles en el template para distinguir el origen.
      var attrs = this.incidentAttrs
      var notas = attrs.notas
      if (!notas || !notas.length) return []
      var self = this
      var list = notas.map(function(n) {
        return {
          fecha: n.fecha ? self.formatNotaDate(n.fecha) : '',
          fechaRaw: n.fecha || null,
          corporacion: n.corporacion || '',
          autor: n.user || n.usuario || n.autor || '',
          msg: self._decodeHtml(n.msg || ''),
        }
      })
      // Sort por fecha desc (fechaRaw es ISO/parseable por Date)
      list.sort(function(a, b) {
        var ta = a.fechaRaw ? new Date(a.fechaRaw).getTime() : 0
        var tb = b.fechaRaw ? new Date(b.fechaRaw).getTime() : 0
        return tb - ta
      })
      return list
    },

    incidentRazonamientos() {
      var rs = this.incidentAttrs.razonamientos
      if (!rs || typeof rs !== 'object') return []
      var self = this
      if (Array.isArray(rs)) {
        return rs.map(function(r) {
          return {
            corporacion: r.corporacion || '',
            fecha: r.fecha ? self.formatNotaDate(r.fecha) : '',
            motivo: r.motivoCierre || '',
            nota: r.notaCierre || '',
          }
        })
      }
      return Object.keys(rs).map(function(k) {
        var v = rs[k]
        return {
          corporacion: k,
          fecha: '',
          motivo: v === 'None' ? 'Sin razonamiento' : String(v == null ? '' : v),
          nota: '',
        }
      })
    },

    // ── KPI computed — KPI-DRAWER-REDISEÑO §4 ─────────────────
    kpiLoading() {
      return this.monitor && this.monitor.kpiLoading
    },
    kpiSummary() {
      if (this.kpiLoading) return {}
      return (this.entityType === 'kpi' && this.data) ? this.data : {}
    },
    kpiIncidents() {
      return (this.kpiSummary.incidents && this.kpiSummary.incidents.summary) || { total: 0, seguridad: 0, medico: 0, proteccion_civil: 0, servicios_publicos: 0 }
    },
    // Array crudo de incidentes dentro de la geocerca (o globales si all).
    // Habilita el breakdown por prioridad agrupando por (motivo, prioridad).
    kpiIncidentsArray() {
      return (this.kpiSummary.incidents && this.kpiSummary.incidents.incidents) || []
    },
    kpiCategories() {
      return (this.kpiSummary.incidents && this.kpiSummary.incidents.categories) || {}
    },
    kpiCategoriesSorted() {
      const cats = this.kpiCategories
      return Object.keys(cats)
        .map((k) => ({ motivo: k, count: Number(cats[k]) || 0 }))
        .sort((a, b) => b.count - a.count)
    },

    // Eventos críticos: categorías que matchean la lista hardcoded de
    // motivos operativamente críticos. Con breakdown por prioridad cuando
    // el array crudo de incidentes está disponible.
    kpiCriticalCategories() {
      var self = this
      var out = []
      for (var i = 0; i < this.kpiCategoriesSorted.length; i++) {
        var c = this.kpiCategoriesSorted[i]
        if (!isCriticalMotivo(c.motivo)) continue
        out.push({
          motivo: c.motivo,
          count: c.count,
          priorityBreakdown: self._kpiPriorityBreakdownFor(c.motivo),
        })
      }
      return out
    },

    // Actividad operativa: lo restante (no crítico) — llamadas mudas,
    // colgadas, otras alarmas, etc. Mismo orden descendente por conteo.
    kpiActivityCategories() {
      var out = []
      for (var i = 0; i < this.kpiCategoriesSorted.length; i++) {
        var c = this.kpiCategoriesSorted[i]
        if (isCriticalMotivo(c.motivo)) continue
        out.push(c)
      }
      return out
    },

    kpiCameras() {
      return (this.kpiSummary.cameras && this.kpiSummary.cameras.summary) || { total: 0, up: 0, down: 0 }
    },
    kpiDevices() {
      return (this.kpiSummary.devices && this.kpiSummary.devices.summary) || { total: 0, online: 0, offline: 0, unknown: 0, down: 0 }
    },
    kpiBuses() {
      return (this.kpiSummary.buses && this.kpiSummary.buses.summary) || { total: 0, up: 0, alert: 0, down: 0 }
    },
    kpiSensors() {
      return (this.kpiSummary.sensors && this.kpiSummary.sensors.summary) || { total: 0, up: 0, alert: 0, reported: 0, down: 0 }
    },
    kpiLprs() {
      return (this.kpiSummary.lprs && this.kpiSummary.lprs.summary) || { total: 0, up: 0, down: 0 }
    },
    kpiCprs() {
      return (this.kpiSummary.cprs && this.kpiSummary.cprs.summary) || { total: 0 }
    },

    // Porcentajes por estado para las barras apiladas. Garantizan que la
    // suma sea 100 (normalización contra el total) y evitan divisiones
    // por cero cuando el summary aún no llegó.
    kpiDevicesPct() { return this._pctFromSummary(this.kpiDevices, ['online', 'offline', 'unknown', 'down']) },
    kpiBusesPct() { return this._pctFromSummary(this.kpiBuses, ['up', 'alert', 'down']) },
    kpiCamerasPct() { return this._pctFromSummary(this.kpiCameras, ['up', 'down']) },
    kpiSensorsPct() { return this._pctFromSummary(this.kpiSensors, ['up', 'alert', 'reported', 'down']) },
    kpiLprsPct() { return this._pctFromSummary(this.kpiLprs, ['up', 'down']) },

    // Tooltips para cada barra apilada (mejor UX en hover vs text-only).
    kpiDevicesBarTitle() {
      var s = this.kpiDevices
      return 'En línea ' + (s.online || 0) + ' · Fuera ' + (s.offline || 0)
        + ' · Sin señal ' + (s.unknown || 0) + ' · Inactivas ' + (s.down || 0)
    },
    kpiBusesBarTitle() {
      var s = this.kpiBuses
      return 'Operando ' + (s.up || 0) + ' · Alerta ' + (s.alert || 0) + ' · Fuera ' + (s.down || 0)
    },
    kpiCamerasBarTitle() {
      var s = this.kpiCameras
      return 'Activas ' + (s.up || 0) + ' · Sin señal ' + (s.down || 0)
    },
    kpiSensorsBarTitle() {
      var s = this.kpiSensors
      return 'Operativos ' + (s.up || 0) + ' · Alerta ' + (s.alert || 0)
        + ' · Reportados ' + (s.reported || 0) + ' · Sin señal ' + (s.down || 0)
    },
    kpiLprsBarTitle() {
      var s = this.kpiLprs
      return 'Activos ' + (s.up || 0) + ' · Sin señal ' + (s.down || 0)
    },

    // Alcance (scope) del KPI — global vs geocerca específica.
    kpiScope() {
      return (this.monitor && this.monitor.geofenceScope) || (this.monitor && this.monitor.data && this.monitor.data.geofenceScope) || 'global'
    },
    kpiScopeLabel() {
      if (this.kpiScope === 'geofence') {
        var name = (this.monitor && this.monitor.geofenceName) || 'Geocerca'
        return 'Geocerca: ' + name
      }
      return 'Estado de México'
    },
    kpiScopeIcon() {
      return this.kpiScope === 'geofence' ? 'mdi-vector-polygon' : 'mdi-map-outline'
    },

    // Ficha del encargado (comisario) para geocercas de mando unico.
    // MapV2 fetchea el driver al hacer click y lo inyecta en item.data.encargado.
    kpiEncargado() {
      return (this.monitor && this.monitor.encargado) || null
    },
    kpiEncargadoVisible() {
      // Solo se muestra para region / sector / cuadrante. Si el operador
      // abre el KPI global (Estado de Mexico) o una geocerca regular sin
      // encargado asignado, la seccion no aparece.
      var mu = this.monitor && this.monitor.mandoUnico
      return !!(mu && this.kpiEncargado)
    },
    kpiEncargadoLabel() {
      var mu = this.monitor && this.monitor.mandoUnico
      if (mu === 'region') return 'Encargado — Región'
      if (mu === 'sector') return 'Encargado — Sector'
      if (mu === 'cuadrante') return 'Encargado — Cuadrante'
      return 'Encargado'
    },
    kpiEncargadoPhoto() {
      // eslint-disable-next-line no-unused-expressions
      this._kpiEncargadoPhotoBroken  // lee el flag para que Vue reactue al 404
      if (this._kpiEncargadoPhotoBroken) return ''
      var e = this.kpiEncargado || {}
      if (!e.photo) return ''
      var base = import.meta.env.VITE_APP_IMAGES_PATH || '/api/v2/public/images/'
      var name = String(e.photo).split('/').pop()
      return base + name
    },
    kpiEncargadoNombre() {
      var e = this.kpiEncargado || {}
      return e.name || e.nombre || '—'
    },
    kpiEncargadoUnidadAdmin() {
      var e = this.kpiEncargado || {}
      return e.unidad_administrativa || e.dependencia || '—'
    },
    kpiEncargadoAdscripcion() {
      var e = this.kpiEncargado || {}
      return e.adscripcion || e.corporacion || '—'
    },
    kpiEncargadoCategoria() {
      var e = this.kpiEncargado || {}
      return e.categoria || '—'
    },

    // Frescura del dato cliente-side. fetchedAt viene del payload del KPI;
    // el tick de drawerFreshnessTick (cada 1s) asegura re-cálculo reactivo.
    kpiFetchedAt() {
      if (!this.monitor || !this.monitor.fetchedAt) return null
      return this.monitor.fetchedAt
    },
    kpiFreshnessText() {
      // eslint-disable-next-line no-unused-expressions
      this.drawerFreshnessTick
      if (!this.kpiFetchedAt) return ''
      var diff = Math.max(0, Date.now() - this.kpiFetchedAt)
      if (diff < 15000) return 'Actualizado ahora'
      if (diff < 60000) return 'hace ' + Math.floor(diff / 1000) + 's'
      if (diff < 3600000) return 'hace ' + Math.floor(diff / 60000) + 'm'
      var hours = Math.floor(diff / 3600000)
      return 'hace ' + hours + 'h'
    },
    kpiFreshnessStale() {
      // eslint-disable-next-line no-unused-expressions
      this.drawerFreshnessTick
      if (!this.kpiFetchedAt) return false
      return (Date.now() - this.kpiFetchedAt) > 5 * 60 * 1000
    },
    kpiFetchedAtFormatted() {
      if (!this.kpiFetchedAt) return ''
      var d = new Date(this.kpiFetchedAt)
      var pad = function(n) { return n < 10 ? '0' + n : String(n) }
      return pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + ' '
        + pad(d.getHours()) + ':' + pad(d.getMinutes())
    },

    // Filtro activo de categoría (propagado al sidebar/mapa). Se lee del
    // store para mantener consistencia con MapV2.
    kpiCategoryFilter() {
      return this.$store.state['map-v2'].kpiCategoryFilter
    },

    // Header dinámico de Carga Operativa: agrega conteo de recursos con
    // alerta si los hay, para que el operador vea "hay algo raro" incluso
    // con la sección colapsada.
    kpiLoadHeaderLabel() {
      var parts = ['Carga operativa']
      var alerts = (this.kpiSensors.alert || 0) + (this.kpiBuses.alert || 0)
      if (alerts > 0) {
        parts.push('· ' + alerts + ' en alerta')
      }
      return parts.join(' ')
    },

    kpiLoadHasAlert() {
      return (this.kpiSensors.alert || 0) > 0 || (this.kpiBuses.alert || 0) > 0
    },

    // Set memoizado de motivos críticos (para el tab "Todos" — evita
    // recalcular isCriticalMotivo en cada render). Se recomputa cuando
    // cambia la lista de categorías.
    _kpiCriticalSet() {
      var set = Object.create(null)
      for (var i = 0; i < this.kpiCriticalCategories.length; i++) {
        set[this.kpiCriticalCategories[i].motivo] = true
      }
      return set
    },

  },

  mounted: function() {
    // Inicializar SimpleBar en el contenedor scrolleable.
    // SimpleBar reemplaza el scrollbar nativo del browser por uno
    // div-based completamente custom: always-visible cross-platform
    // (macOS/Windows/Linux), agarrable, consistente en todos los
    // navegadores. La opcion autoHide: false fuerza visibilidad
    // permanente cuando hay contenido scrolleable.
    if (this.$refs.scrollContent) {
      this._simplebar = new SimpleBar(this.$refs.scrollContent, {
        autoHide: false,
      })
    }
    // ResizeObserver sobre el root del drawer: cuando el grid-row de
    // abajo cambia (por el tray) el drawer shrinka/crece y SimpleBar
    // necesita recalcular para mostrar/actualizar el scrollbar.
    if (typeof ResizeObserver !== 'undefined' && this.$el) {
      this._drawerResizeObserver = new ResizeObserver(() => {
        if (this._simplebar && typeof this._simplebar.recalculate === 'function') {
          this._simplebar.recalculate()
        }
      })
      this._drawerResizeObserver.observe(this.$el)
    }
    // Timer de frescura (DRAWERS-DESIGN-SPEC §10.1): incrementa
    // drawerFreshnessTick cada segundo para que los computed relativos
    // (ej. deviceLastUpdateFresh, sosElapsed) recalculen sin esperar un
    // update del WebSocket. Barato — solo dispara re-render de los
    // nodos que leen drawerFreshnessTick.
    var self = this
    this._freshnessTimer = setInterval(function() {
      self.drawerFreshnessTick++
    }, 1000)
    // Sincronizar estado inicial de Recursos cercanos con el padre. El
    // watch de `item` solo dispara en cambios subsiguientes (no en el
    // primer mount, sin immediate: true). Sin este emit, MapV2 mantiene
    // _nearbyExpanded=false y el círculo del radio no aparece al primer
    // clic en un item desde el mapa.
    this.$emit('nearby-open-change', this.nearbyOpen)
    // Recalcular el empate radio↔unidad cuando el catálogo de devices se
    // (re)puebla. En el boot con selección persistida el drawer monta y
    // calcula antes de que GET /devices llegue; sin esto la sección
    // "Equipo del elemento" queda vacía hasta cambiar de item.
    this._onConductorCatalogUpdate = function() {
      // Diferir un tick: el evento se dispara sincrónicamente dentro del
      // ciclo de ingesta de MapV2 y necesitamos leer el estado POSTERIOR
      // (refreshSelectedItem puede reemplazar this.data en ese mismo ciclo).
      setTimeout(function() {
        self._computeConductorLinks()
        // Reintenta también el fetch del conductor (nombre del Operador).
        // Con selección persistida el watcher de item.monitorId no refire
        // al boot y el fetch original pudo correr con data incompleta; el
        // guard interno (raw.user ya presente) evita fetches redundantes.
        self._maybeFetchDeviceDriver()
      }, 0)
    }
    window.addEventListener('kalan:conductor-catalog-updated', this._onConductorCatalogUpdate)
  },

  beforeDestroy: function() {
    if (this._simplebar) {
      this._simplebar.unMount()
      this._simplebar = null
    }
    if (this._drawerResizeObserver) {
      this._drawerResizeObserver.disconnect()
      this._drawerResizeObserver = null
    }
    if (this._freshnessTimer) {
      clearInterval(this._freshnessTimer)
      this._freshnessTimer = null
    }
    // Clean up hit photo lightbox listener si quedo abierto al destruir
    window.removeEventListener('keydown', this._onHitPhotoKey)
    if (this._onConductorCatalogUpdate) {
      window.removeEventListener('kalan:conductor-catalog-updated', this._onConductorCatalogUpdate)
      this._onConductorCatalogUpdate = null
    }
  },

  methods: {
    // Una CameraStream del drawer entró/salió de modo maximizado. Togglear
    // .map-drawer--maximized neutraliza el backdrop-filter del drawer (que de
    // otro modo confinaría el contenedor fixed del stream a la caja del drawer).
    onCameraMaximizeChange(val) {
      this.cameraMaximized = !!val
    },

    // ── Editar incidente (modal con Tipo, Descripción, Nombre, Coords) ──
    async openEditIncidentDialog() {
      var attrs = this.incidentAttrs || {}
      var snapshot = {
        tipoIncidente: attrs.tipoEmergenciaIdCNI || null,
        descripcion: attrs.descripcion || '',
        nombreCiudadano: attrs.nombreCiudadano || '',
        paternoCiudadano: attrs.paternoCiudadano || '',
        maternoCiudadano: attrs.maternoCiudadano || '',
        latitude: this.data.latitude != null ? this.data.latitude : null,
        longitude: this.data.longitude != null ? this.data.longitude : null,
      }
      this.editForm = { ...snapshot }
      this.editFormOriginal = { ...snapshot }
      this.editError = ''
      this.editSaving = false
      this.editDialogOpen = true
      // Carga el catálogo CNI si aún no está cargado
      if (this.editIncidentTypes.length === 0 && attrs.subcentro) {
        this.editTypesLoading = true
        try {
          var cadCatalogs = (await import('@/util/cad-catalogs.service')).default
          this.editIncidentTypes = await cadCatalogs.getIncidentTypes(attrs.subcentro)
        } finally {
          this.editTypesLoading = false
        }
      }
    },

    closeEditIncidentDialog() {
      if (this.editSaving) return
      this.editDialogOpen = false
    },

    // Cierra temporalmente el modal y activa el location-picker del mapa
    // para que el operador clickee la nueva ubicación. El watcher de
    // locationPicker.result reabre el modal con las coords aplicadas.
    startEditLocationPicker() {
      this._pickerWaitingForResult = this.$store.state['map-v2'].locationPicker.requestId + 1
      this.editDialogOpen = false
      this.$store.dispatch('map-v2/startLocationPicker', {
        prompt: 'Haz clic en el mapa para fijar la nueva ubicación del incidente',
      })
    },

    async saveEditIncidentDialog() {
      if (this.editSaving || !this.editHasChanges) return
      this.editError = ''
      this.editSaving = true
      var attrs = this.incidentAttrs || {}
      var user = (this.$store && this.$store.state && this.$store.state.auth)
        ? this.$store.state.auth.user : null
      var cadUser = (user && user.attributes && user.attributes.cad_user) || ''
      // Construir el payload `data` solo con los campos modificados.
      var changes = {}
      var keys = Object.keys(this.editForm)
      for (var i = 0; i < keys.length; i++) {
        var k = keys[i]
        if (this.editForm[k] !== this.editFormOriginal[k]) {
          // Trim de strings antes de enviar
          var v = this.editForm[k]
          changes[k] = (typeof v === 'string') ? v.trim() : v
        }
      }
      try {
        await ApiService({
          url: '/v2/cad-incidents/update-incident',
          method: 'post',
          data: {
            center: attrs.subcentro,
            incidentId: this.data.id,
            user: cadUser,
            data: changes,
          },
        })
        // Push optimista a attrs (el WS confirmará al final).
        var newAttrs = { ...attrs }
        Object.keys(changes).forEach(function(k) {
          if (k === 'latitude' || k === 'longitude') return
          newAttrs[k] = changes[k]
        })
        this.$set(this.data, 'attributes', newAttrs)
        if (changes.latitude != null) this.$set(this.data, 'latitude', changes.latitude)
        if (changes.longitude != null) this.$set(this.data, 'longitude', changes.longitude)
        // Si cambió el nombre, también el campo concatenado de display
        if (changes.nombreCiudadano != null || changes.paternoCiudadano != null
            || changes.maternoCiudadano != null) {
          var full = [
            this.data.attributes.nombreCiudadano,
            this.data.attributes.paternoCiudadano,
            this.data.attributes.maternoCiudadano,
          ].filter(Boolean).join(' ')
          this.$set(this.data.attributes, 'nombreCompletoCiudadano', full)
        }
        if (window.VMA && window.VMA.showMessage) {
          window.VMA.showMessage({
            title: 'Incidente actualizado',
            color: 'success',
            icon: 'mdi-check-circle-outline',
          })
        }
        this.editDialogOpen = false
      } catch (err) {
        var msg = (err && err.response && err.response.data
          && (err.response.data.message || err.response.data.error))
          || 'No se pudo actualizar el incidente. Intenta de nuevo.'
        this.editError = msg
      } finally {
        this.editSaving = false
      }
    },

    // ── Agregar nota al incidente ──────────────────────────────────────
    // ── Razonamiento del hit (paridad Analytics Monitor) ────────────────
    openRazonamientoDialog() {
      var actual = this.hitRazonamiento || {}
      this.razTipo = actual.razonamientoTipo || null
      this.razText = actual.razonamiento || ''
      this.razError = ''
      this.razSaving = false
      this.razDialogOpen = true
    },

    closeRazonamientoDialog() {
      if (this.razSaving) return
      this.razDialogOpen = false
    },

    async saveRazonamiento() {
      if (!this.canSaveRazonamiento || this.razSaving) return
      var recordId = this.hitRecordId
      var texto = this.razText.trim()
      var tipo = this.razTipo
      this.razError = ''
      this.razSaving = true
      try {
        var updated = await ApiService({
          url: '/v2/analytic/records/' + recordId + '/razonamiento',
          method: 'patch',
          data: { razonamiento: texto, tipo: tipo },
        })
        // El backend devuelve el record actualizado; si no trae `data`,
        // construimos el overlay con lo que acabamos de enviar.
        var d = (updated && updated.data) || {}
        this.$set(this.razLocal, recordId, {
          razonamiento: d.razonamiento || texto,
          razonamientoTipo: d.razonamientoTipo || tipo,
          razonamientoBy: d.razonamientoBy || null,
        })
        this.razDialogOpen = false
        if (window.VMA && window.VMA.showMessage) {
          window.VMA.showMessage({
            title: 'Razonamiento guardado',
            color: 'success',
            icon: 'mdi-check-circle-outline',
          })
        }
      } catch (err) {
        console.error({ err })
        var msg = (err && err.response && err.response.data
          && (err.response.data.message || err.response.data.error))
          || (err && err.message)
          || 'No se pudo guardar el razonamiento'
        this.razError = String(msg)
      } finally {
        this.razSaving = false
      }
    },

    openAddNoteDialog() {
      this.addNoteText = ''
      this.addNoteError = ''
      this.addNoteSaving = false
      // Pre-seleccionar la corporación si solo hay una asignada.
      var corps = this.incidentCorporaciones
      this.addNoteCorp = corps.length === 1 ? corps[0] : null
      this.addNoteDialogOpen = true
    },

    closeAddNoteDialog() {
      if (this.addNoteSaving) return
      this.addNoteDialogOpen = false
    },

    async saveAddNote() {
      if (!this.canSaveAddNote || this.addNoteSaving) return
      this.addNoteError = ''
      this.addNoteSaving = true
      var attrs = this.incidentAttrs || {}
      var payload = {
        incidentId: this.data.id,
        remark: {
          corporacion: this.addNoteCorp,
          msg: this.addNoteText.trim(),
          center: attrs.subcentro || '',
        },
      }
      try {
        await ApiService({
          url: '/v2/cad-incidents/add-remark',
          method: 'post',
          data: payload,
        })
        // Push optimista en la lista de notas locales para que el operador
        // vea su nota inmediatamente (sin esperar el round-trip por WS).
        var nuevaNota = {
          corporacion: this.addNoteCorp,
          msg: this.addNoteText.trim(),
          fecha: new Date().toLocaleString('es-MX'),
        }
        if (!Array.isArray(attrs.notas)) {
          this.$set(this.data, 'attributes', { ...attrs, notas: [] })
        }
        this.data.attributes.notas.unshift(nuevaNota)
        if (window.VMA && window.VMA.showMessage) {
          window.VMA.showMessage({
            title: 'Nota agregada al incidente',
            color: 'success',
            icon: 'mdi-check-circle-outline',
          })
        }
        this.addNoteDialogOpen = false
      } catch (err) {
        var msg = (err && err.response && err.response.data
          && (err.response.data.message || err.response.data.error))
          || 'No se pudo guardar la nota. Intenta de nuevo.'
        this.addNoteError = msg
      } finally {
        this.addNoteSaving = false
      }
    },

    // Si la foto del usuario del device falla al cargar (404, permisos,
    // etc), caer al dummy user2.png en lugar de icono roto.
    onDevicePhotoError() {
      this._devicePhotoBroken = true
      this.$forceUpdate()
    },

    // Fallback a placeholder (icono) si la foto del encargado 404ea.
    onKpiEncargadoPhotoError() {
      this._kpiEncargadoPhotoBroken = true
      this.$forceUpdate()
    },

    // Accion rapida: al hacer click en el icono Murphy al lado de la placa
    // del hit LPR, disparamos la consulta via store action. Esta opera
    // igual que el buscador MurphyPanel y abre el panel Murphy si hay
    // resultado.
    onSearchPlacaMurphy(placa) {
      if (!this.puedeMurphy) return
      if (!placa || placa === '—') return
      this.$store.dispatch('map-v2/searchMurphyByPlaca', placa)
    },

    // Dispara búsqueda Murphy del conductor del device. El backend
    // /v2/drivers/{id} normalmente devuelve solo u.name con el nombre
    // completo junto; si expone apellidos separados los usamos tal cual,
    // si no, descomponemos con heurística mexicana (splitMexicanFullName).
    // Ver DRAWERS-CROSS-REFERENCES §2.1.
    onSearchConductorInMurphy() {
      var u = this.data && this.data.user
      if (!u) return
      var payload = this._buildMurphyPersonaPayload(
        u.name || u.nombre || '',
        u.apellido_1 || u.lastName || '',
        u.apellido_2 || u.secondLastName || '',
      )
      if (!payload.nombre && !payload.ap1) return
      this.$emit('search-murphy-persona', payload)
    },

    // Helper compartido: si los apellidos ya vienen separados del backend
    // los respetamos. Si no (solo hay un string con nombre completo),
    // descomponemos con heurística.
    _buildMurphyPersonaPayload(name, ap1, ap2) {
      if ((ap1 && String(ap1).trim()) || (ap2 && String(ap2).trim())) {
        return {
          nombre: String(name || '').trim(),
          ap1: String(ap1 || '').trim(),
          ap2: String(ap2 || '').trim(),
        }
      }
      return splitMexicanFullName(name)
    },

    // Llamar al teléfono del chofer vía tel: link nativo.
    onCallDevicePhone() {
      if (!this.devicePhone) return
      try { window.open('tel:' + this.devicePhone, '_self') } catch (e) { /* ignore */ }
    },

    // Llamar al teléfono del bus (contacto del transporte).
    onCallBusPhone() {
      var phone = this.data && this.data.phone
      if (!phone) return
      try { window.open('tel:' + phone, '_self') } catch (e) { /* ignore */ }
    },

    // ── KPI handlers — KPI-DRAWER-REDISEÑO §4 ─────────────────
    // Normaliza contadores del summary a porcentajes que suman 100 (los
    // segmentos de la barra apilada usan flexBasis: N%). Si el total es
    // 0 devuelve todos en 0 — la barra queda vacía visualmente.
    _pctFromSummary: function(summary, keys) {
      var out = {}
      var total = Number(summary.total) || 0
      if (!total) {
        for (var i = 0; i < keys.length; i++) out[keys[i]] = 0
        return out
      }
      for (var j = 0; j < keys.length; j++) {
        var v = Number(summary[keys[j]]) || 0
        out[keys[j]] = (v / total) * 100
      }
      return out
    },

    // Breakdown por prioridad para un motivo específico de eventos críticos.
    // Itera sobre kpiIncidentsArray agrupando por attributes.prioridad.
    // Devuelve string "N alta · N media · N baja" o null si no hay datos.
    _kpiPriorityBreakdownFor: function(motivo) {
      var arr = this.kpiIncidentsArray
      if (!arr || !arr.length) return null
      var counts = { alta: 0, media: 0, baja: 0 }
      var any = false
      for (var i = 0; i < arr.length; i++) {
        var inc = arr[i]
        var m = inc && inc.attributes && inc.attributes.motivo
        if (m !== motivo) continue
        var p = inc.attributes.prioridad
        if (!p) continue
        any = true
        var k = String(p).toLowerCase()
        if (k === 'alta' || k === 'media' || k === 'baja') counts[k]++
      }
      if (!any) return null
      var parts = []
      if (counts.alta) parts.push(counts.alta + ' alta')
      if (counts.media) parts.push(counts.media + ' media')
      if (counts.baja) parts.push(counts.baja + ' baja')
      return parts.join(' · ') || null
    },

    // Click en card de categoría (Seguridad/Médicos/P. Civil/Serv. Públ.).
    // Emite hacia MapV2 que maneja el filtro del mapa/sidebar. Segundo
    // click sobre la misma card revierte el filtro.
    onKpiCardClick: function(category) {
      this.$emit('kpi-category-filter', category)
    },

    // Click en fila de "Eventos críticos". Emite motivo específico para
    // filtrado más granular del mapa. Por ahora reutiliza el mismo flujo
    // de category-filter — el handler puede evolucionar a filtrar por
    // motivo si se decide.
    onKpiCategoryRowClick: function(motivo) {
      this.$emit('kpi-category-filter', motivo)
    },

    // Refresh manual del KPI desde el botón del contexto. Emite hacia
    // MapV2.onKpiRefresh que re-ejecuta _showKpi con el mismo query.
    onKpiRefresh: function() {
      this.$emit('kpi-refresh', this.item)
    },

    // Helper para template: determina si un motivo es crítico usando el
    // set memoizado. Usado en el tab "Todos" para destacar en rojo los
    // motivos que también aparecen en el tab "Críticos".
    isCriticalCatCached: function(motivo) {
      return !!this._kpiCriticalSet[motivo]
    },

    // Llamar al teléfono del establecimiento del sensor.
    onCallSensorPhone() {
      var phone = this.data && this.data.phone
      if (!phone) return
      try { window.open('tel:' + phone, '_self') } catch (e) { /* ignore */ }
    },

    // Llamar al contacto del sensor.
    onCallSensorContactPhone() {
      var phone = this.data && this.data.contactPhone
      if (!phone) return
      try { window.open('tel:' + phone, '_self') } catch (e) { /* ignore */ }
    },

    // Llamar al reportante del incidente.
    onCallReportante() {
      var phone = this.incidentAttrs && this.incidentAttrs.numeroReportante
      if (!phone) return
      try { window.open('tel:' + phone, '_self') } catch (e) { /* ignore */ }
    },

    // Consultar al reportante en Murphy. El backend expone solo el nombre
    // completo junto (nombreCompletoCiudadano); descomponemos heurística.
    onSearchReportanteMurphy() {
      var fullName = this.incidentAttrs && this.incidentAttrs.nombreCompletoCiudadano
      if (!fullName) return
      var payload = this._buildMurphyPersonaPayload(fullName, '', '')
      if (!payload.nombre && !payload.ap1) return
      this.$emit('search-murphy-persona', payload)
    },

    // Llamar al teléfono del reportante (variante reportant). Usamos
    // reportantPhoneRaw (sin formato) porque el esquema tel: necesita
    // dígitos puros, no HTML ni paréntesis ni la bandera unicode.
    onCallReportantPhone() {
      var phone = this.reportantPhoneRaw
      if (!phone) return
      try { window.open('tel:' + phone, '_self') } catch (e) { /* ignore */ }
    },

    // Consultar al conductor Uber del incidente en Murphy.
    onSearchUberDriverMurphy() {
      var name = this.incidentUberInfo && this.incidentUberInfo.userName
      if (!name) return
      var payload = this._buildMurphyPersonaPayload(name, '', '')
      if (!payload.nombre && !payload.ap1) return
      this.$emit('search-murphy-persona', payload)
    },

    // Llamar al conductor Uber vía tel: link.
    onCallUberDriver() {
      var phone = this.incidentUberInfo && this.incidentUberInfo.userPhone
      if (!phone) return
      try { window.open('tel:' + phone, '_self') } catch (e) { /* ignore */ }
    },

    // Consultar al llamante SOS en Murphy. El payload Colibrí trae el
    // nombre en un solo campo (userData.name / displayName).
    onSearchSosCallerMurphy() {
      var d = this._sosData()
      var ud = d.userData || {}
      var fullName = ud.name || ud.displayName || d.name
      if (!fullName) return
      var payload = this._buildMurphyPersonaPayload(fullName, '', '')
      if (!payload.nombre && !payload.ap1) return
      this.$emit('search-murphy-persona', payload)
    },

    // Llamar al teléfono del llamante SOS (desde la Zona 4).
    onCallSosPhone() {
      var d = this._sosData()
      var ud = d.userData || {}
      var num = ud.phoneNumber || d.callbackNumber || d.phone || ''
      if (!num) return
      try { window.open('tel:' + num, '_self') } catch (e) { /* ignore */ }
    },

    // Disparar la acción primaria de la Zona 2 del SOS call.
    onSosPrimaryAction() {
      var action = this.sosPrimaryAction
      if (!action) return
      if (action.event === 'sos-atender') this.atenderDesdeDrawer()
      else if (action.event === 'sos-finalizar') this.finalizarDesdeDrawer()
      else if (action.event === 'sos-descartar') this.descartarDesdeDrawer()
    },

    // Resolver acción primaria del drawer device y emitir evento adecuado.
    onDevicePrimaryAction() {
      var action = this.devicePrimaryAction
      if (!action) return
      if (action.event === 'follow') this.$emit('follow', this.item)
      else if (action.event === 'request-dispatch') {
        // Sin flow de despacho directo aún — abrimos sección de recursos
        // cercanos y emitimos el evento para que MapV2 pueda hookearlo
        // cuando el flow exista.
        this.nearbyOpen = true
        this.$emit('request-dispatch', this.item)
      }
    },

    // Formato relativo para timestamps de eventos activos del device.
    deviceEventTimeAgo(ev) {
      if (!ev) return ''
      var ts = ev.timestamp || ev.createdAt || ev.serverTime
      return ts ? timeAgo(ts) : ''
    },

    // Lightbox de la foto del hit LPR. Clic abre, clic fuera / boton X /
    // Escape cierran. Register escape listener solo mientras el zoom este
    // activo para evitar overhead.
    openHitPhoto(src) {
      if (!src) return
      this.hitPhotoZoomSrc = src
      window.addEventListener('keydown', this._onHitPhotoKey)
    },

    closeHitPhoto() {
      if (!this.hitPhotoZoomSrc) return
      this.hitPhotoZoomSrc = ''
      window.removeEventListener('keydown', this._onHitPhotoKey)
    },

    _onHitPhotoKey(e) {
      if (e.key === 'Escape') this.closeHitPhoto()
    },

    // Paridad con Map.vue legacy (selectDevice): el endpoint /devices no
    // trae el usuario joined, solo el id en attributes.conductor. Cuando
    // el operador abre un device en el drawer, resolvemos el conductor
    // via /v2/drivers/{id} y lo asignamos a data.user para que los
    // campos Usuario, Unidad Administrativa, Adscripcion y la foto se
    // puedan renderizar igual que en el mapa viejo.
    // Respaldo del retrato cuando GET /v2/drivers/{id} no resuelve para este
    // device: el directorio completo ya está en memoria si el operador buscó
    // antes, y si no, se carga una vez y sirve para todas las selecciones
    // siguientes. Solo se dispara para devices CON conductor, así que no
    // agrega tráfico al seleccionar cámaras o incidentes.
    _ensureDriverDirForPhoto: function() {
      if (!this.item || this.entityType !== 'device') return
      var raw = this.data
      if (!raw || !raw.attributes || !raw.attributes.conductor) return
      if (isDriverDirectoryLoaded()) return
      var self = this
      ensureDriverDirectory().then(function() { self.driverDirTick++ })
    },

    _maybeFetchDeviceDriver: function() {
      if (!this.item || this.entityType !== 'device') return
      var raw = this.data
      if (!raw || !raw.attributes) return
      var conductorId = raw.attributes.conductor
      if (!conductorId) return
      if (raw.user && (raw.user.name || raw.user.nombre)) return
      var self = this
      ApiService({ url: '/v2/drivers/' + conductorId, method: 'get' })
        .then(function(persona) {
          if (!persona) return
          // Montar el user en el raw del monitor para que los computeds
          // (deviceUsuario, deviceUserPhoto, etc) reflejen el dato.
          self.$set(raw, 'user', persona)
          // Re-resolver this.data al momento de resolver: durante el boot,
          // refreshSelectedItem puede REEMPLAZAR el objeto data (snapshot
          // persistido → raw fresco del initial load) mientras este fetch
          // está en vuelo; sin esto el $set cae en el objeto viejo y el
          // Operador queda "Desconocido" hasta re-seleccionar.
          var current = self.data
          if (current && current !== raw && current.attributes
            && String(current.attributes.conductor) === String(conductorId)) {
            self.$set(current, 'user', persona)
          }
          // Tambien actualizar el monitor en el cache global de MapV2
          // para que futuras reselecciones no re-pidan.
          var monitors = window.__mapV2Monitors
          if (monitors && self.item && self.item.monitorId) {
            var mon = monitors[self.item.monitorId]
            if (mon && mon.data) mon.data.user = persona
          }
          // Despertar a los computeds que dependen del retrato.
          //
          // deviceUserPhoto ya se evaluo ANTES de que existiera raw.user, y en
          // Vue 2 leer una propiedad inexistente no registra dependencia: el
          // $set de arriba crea la propiedad pero el computed no se entera y
          // se queda con el avatar dummy en cache.
          //
          // Se notaba justo en los radios, que es donde se reporto: un device
          // EN LINEA recibe updates de WS que re-renderizan el drawer por otro
          // camino y ahi la foto aparecia "sola", pero un radio fuera de linea
          // —la mayoria— no recibe nada y su retrato no llegaba nunca.
          self.driverDirTick++
        })
        .catch(function(err) { console.error('[device driver]', err) })
    },

    // Empate radio ↔ unidad ("que empate la unidad del elemento con el
    // radio"): busca otros devices que compartan attributes.conductor con
    // el item actual. Corre en memoria sobre los monitors ya cargados —
    // cero llamadas al API. Bidireccional: un radio lista las unidades del
    // elemento y una unidad lista sus radios.
    _computeConductorLinks() {
      this.conductorLinks = null
      this.equipoOpen = true
      if (!this.item || this.entityType !== 'device') return
      const monitors = window.__mapV2Monitors || {}
      const monitor = monitors[this.item.monitorId]
        || { id: this.item.monitorId, entityType: 'device', data: this.data }
      this.conductorLinks = findConductorLinks(monitor, monitors)
    },

    // Cuando el drawer muestra un LPR, fetchea el historial de hits de
    // ese device (GET /v2/analytic/records/device/:id/lasthits). Paridad
    // con legacy Map.vue:3297-3312 (selectLpr).
    _maybeFetchLprHits: function() {
      this.lprHits = []
      this.lprHitsLoading = false
      if (!this.item || this.entityType !== 'lpr') return
      var deviceId = this.data && this.data.id
      if (!deviceId) return
      var self = this
      this.lprHitsLoading = true
      ApiService({ url: '/v2/analytic/records/device/' + deviceId + '/lasthits', method: 'get' })
        .then(function(hits) {
          if (!self.item || self.item.entityType !== 'lpr' || self.data.id !== deviceId) return
          self.lprHits = (hits || []).map(function(h) {
            var placa = h && h.data && (h.data.placa || h.data.plate) || 'SIN PLACA'
            var when = h && h.startDate ? new Date(h.startDate).toLocaleString() : ''
            return { plate: placa, when: when }
          })
        })
        .catch(function(err) { console.error('[lpr hits]', err) })
        .finally(function() {
          if (self.item && self.item.entityType === 'lpr' && self.data.id === deviceId) {
            self.lprHitsLoading = false
          }
        })
    },

    toggleNearbyOpen() {
      this.nearbyOpen = !this.nearbyOpen
      // El círculo del radio en el mapa se muestra/oculta en sync con
      // este toggle — el padre escucha para dibujarlo solo cuando el
      // operador está activamente mirando los recursos cercanos.
      this.$emit('nearby-open-change', this.nearbyOpen)
    },

    onRadiusChange(value) {
      // Delega al padre para que commitee al store y recalcule. El padre
      // es el dueño del estado reactivo y de _monitors.
      this.$emit('nearby-radius-change', value)
    },

    // Método (no computed) para que no se cachee — el HMR de Vue 2 se
    // confundía al modificar el computed sensorStatusText.
    getSensorEstado() {
      const raw = this.data && this.data.status
      if (raw === 'up') return 'En línea'
      if (raw === 'down') return 'Fuera de línea'
      if (raw === 'alert') return 'Alertado'
      if (raw === 'reported') return 'Reportado'
      if (raw === 'normal') return 'Normal'
      // Fallback al estado normalizado del MapMonitor — es el mismo que
      // alimenta el chip del header, así que sabemos que está definido.
      const es = this.entityStatus
      if (es === 'alert') return 'Alertado'
      if (es === 'online') return 'En línea'
      if (es === 'offline') return 'Fuera de línea'
      if (es === 'idle') return 'Inactivo'
      return raw || 'Desconocido'
    },

    // Ámbito de la cámara seleccionada (estatal C5 / municipal / otras),
    // resuelto por el grupo. Método (no computed) por el gotcha de HMR
    // documentado en getSensorEstado. '' cuando la modalidad está apagada
    // (switch VITE_CAMERA_OWNERSHIP) o el grupo no está clasificado — la
    // fila del template se oculta con v-if.
    getCameraOwnershipLabel() {
      const own = resolveCameraOwnershipByGroupId(this.data && this.data.groupId)
      return CAMERA_OWNERSHIP_LABELS[own] || ''
    },

    // Color del grupo del botón de pánico seleccionado (el mismo de la placa
    // en el mapa), para la muestra junto a la fila "Grupo". '' = sin color.
    getSensorGroupColor() {
      return resolveSensorGroupColorByGroupId(this.data && this.data.groupId)
    },

    atenderDesdeDrawer() {
      if (!this.data) return
      this.$emit('sos-atender', this.data)
    },

    abrirOverlaySOS() {
      this.$emit('sos-abrir-panel')
    },

    finalizarDesdeDrawer() {
      // Pasamos la llamada SELECCIONADA para que MapV2 finalice por id la que
      // se ve en el drawer (puede no ser la activa del operador). Paridad con
      // legacy Map.vue selectSOSCall, que emite end_call con item.id.
      this.$emit('sos-finalizar', this._sosData())
    },

    descartarDesdeDrawer() {
      if (!this.data) return
      this.$emit('sos-descartar', this.data)
    },

    // Método (no computed) para extraer datos Colibrí del monitor.
    // Evita conflictos de reactividad con el computed "data".
    _sosData: function() {
      var mon = this.item && this.item.data
      return (mon && mon.data) || {}
    },

    _sosPhone: function() {
      var d = this._sosData()
      var ud = d.userData || {}
      var num = ud.phoneNumber || d.callbackNumber || d.phone || ''
      if (!num) return '—'
      var s = String(num)
      if (s.length === 10) return s.slice(0, 3) + ' ' + s.slice(3, 6) + ' ' + s.slice(6)
      return s
    },

    _sosType: function() {
      var t = this._sosData().type
      if (t === 'VIDEO_CALL') return 'Videollamada'
      if (t === 'VOICE_CALL' || t === 'CALL') return 'Llamada de voz'
      if (t === 'CHAT') return 'Chat'
      return 'SOS'
    },

    _sosSystem: function() {
      return this._sosData().systemName || 'Colibrí'
    },

    _sosEnded: function() {
      if (this.entityType !== 'sos_call') return false
      var s = this._sosData().status
      return s === 'CLIENT_END_CALL' || s === 'OPERATOR_END_CALL' || s === 'ENDED'
    },

    // Formato 'DD-MM-YYYY HH:mm:ss' — replica moment().format(...) del
    // fieldsMap.reportant viejo. Acepta timestamps como string o number.
    formatLongDate: function(ts) {
      if (ts == null) return '—'
      var n = typeof ts === 'number' ? ts : parseInt(ts)
      if (isNaN(n)) return '—'
      var d = new Date(n)
      if (isNaN(d.getTime())) return '—'
      var pad = function(v) { return v < 10 ? '0' + v : String(v) }
      return pad(d.getDate()) + '-' + pad(d.getMonth() + 1) + '-' + d.getFullYear()
        + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds())
    },

    copyInfo: function() {
      var rows = this.$el ? this.$el.querySelectorAll('.detail-row') : []
      var lines = []
      for (var i = 0; i < rows.length; i++) {
        var label = rows[i].querySelector('.detail-row__label')
        var value = rows[i].querySelector('.detail-row__value')
        var l = label ? label.textContent.trim() : ''
        var v = value ? value.textContent.trim() : ''
        if (l || v) lines.push((l ? l + ': ' : '') + v)
      }
      var text = ''
      if (lines.length) {
        text = this.entityTitle + ' — ' + this.entitySubtitle + '\n' + lines.join('\n')
      }
      if (text) {
        navigator.clipboard.writeText(text).catch(function() {
          var ta = document.createElement('textarea')
          ta.value = text
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          try { document.execCommand('copy') } catch (e) { /* ignore */ }
          document.body.removeChild(ta)
        })
      }
    },

    ptzMove: function(action, cam) {
      var self = this
      sendPtzCommand(action, this.data.uniqueId || '', cam.devId || '').then(function(r) {
        if (r && !r.ok) self._flashPtzError(cam.devId)
      })
    },

    // Píldora in-situ "sin respuesta" sobre la card cuyo comando PTZ falló.
    // Se auto-oculta (~2.6s, en sincronía con la animación CSS).
    _flashPtzError: function(devId) {
      this._ptzErrorTimers = this._ptzErrorTimers || {}
      if (this._ptzErrorTimers[devId]) clearTimeout(this._ptzErrorTimers[devId])
      this.$set(this.ptzErrorByDev, devId, true)
      var self = this
      this._ptzErrorTimers[devId] = setTimeout(function() {
        self.$set(self.ptzErrorByDev, devId, false)
        delete self._ptzErrorTimers[devId]
      }, 2600)
    },

    isCameraPinned(id) {
      return this.pinnedCameras.some(c => c.id === id)
    },

    togglePmiCamCollapsed: function(cam) {
      // Toggle del estado colapsado individual de una sub-cámara en el
      // drawer. El <CameraStream> se desmonta cuando el flag es true
      // (libera ancho de banda y CPU); el header queda visible para
      // que el operador pueda restaurar la cámara con el mismo botón.
      var next = !this.pmiCamCollapsed[cam.devId]
      this.$set(this.pmiCamCollapsed, cam.devId, next)
    },

    pinAllPmiCameras: function() {
      for (var i = 0; i < this.pmiCameras.length; i++) {
        this.pinPmiCamera(this.pmiCameras[i])
      }
    },

    pinPmiCamera: function(cam) {
      // toTrayCamera es la única fuente de la forma {id, name, streamUrl,
      // isPtz, uniqueId, devId} — comparte identidad con el pre-warm del
      // tray (mismo id) y aplica el gate PTZ por uniqueId en un solo lugar.
      this.$emit('pin-camera', { data: toTrayCamera(this.data, cam) })
    },


    getSubCameras: function(cam) {
      if (!cam.data || !cam.data.attributes) return []
      var attrs = cam.data.attributes
      var cameras = []
      var keys = Object.keys(attrs).filter(function(k) {
        return k.slice(0, 3) === 'dev' && !isNaN(parseInt(k.slice(3)))
      }).sort()

      for (var i = 0; i < keys.length; i++) {
        try {
          var decoded = JSON.parse(atob(attrs[keys[i]]))
          if (!decoded.server || !decoded.name) continue
          cameras.push({
            devId: keys[i],
            title: (cam.title || '') + ' - ' + (decoded.name || keys[i]),
            streamUrl: '/api/v2/video/stream/' + decoded.server + '/' + cam.data.uniqueId + '/' + decoded.name,
            isPtz: decoded.isPtz || false,
          })
        } catch (e) { /* skip */ }
      }
      return cameras
    },

    pinSubCamera: function(cam, sub) {
      // toTrayCamera completa isPtz/uniqueId/devId con el mismo gate PTZ del
      // pin normal del PMI (isPtz solo si el dueño tiene uniqueId de Traccar).
      // Antes se emitía solo {id, name, streamUrl} → las sub-cámaras PTZ
      // llegaban al tray SIN controles de movimiento.
      // OJO: cam.data (entrada nearby de _calcNearby) es un objeto ligero SIN
      // `id` — el dueño se identifica con cam.id como fallback, igual que el
      // indicador isCameraPinned del template. Pasar cam.data directo dejaría
      // ids '-devN' que colisionan entre PMIs distintos.
      var owner = {
        id: (cam.data && cam.data.id) || cam.id,
        uniqueId: (cam.data && cam.data.uniqueId) || '',
      }
      this.$emit('pin-camera', { data: toTrayCamera(owner, sub) })
    },

    toggleNearbyExpand: function(entity) {
      if (this.expandedNearby === entity.id) {
        this.expandedNearby = null
        return
      }

      this.expandedNearby = entity.id
      // CameraHLS components auto-load via their own mounted()
    },



    formatDist: function(km) {
      if (km < 1) return Math.round(km * 1000) + 'm'
      return km.toFixed(1) + 'km'
    },

    formatMetersKm(m) {
      if (m == null || !isFinite(m)) return '—'
      if (m >= 1000) return (m / 1000).toFixed(1) + ' km'
      return Math.round(m) + ' m'
    },
    formatRadius(m) {
      // Variante del radio: sin decimales si es múltiplo exacto de 1 km.
      if (m >= 1000) {
        const km = m / 1000
        return km % 1 === 0 ? km + ' km' : km.toFixed(1) + ' km'
      }
      return m + ' m'
    },
    formatSecondsMin(s) {
      if (s == null || !isFinite(s)) return '—'
      const min = Math.round(s / 60)
      if (min < 1) return '<1 min'
      if (min < 60) return min + ' min'
      return Math.floor(min / 60) + 'h ' + (min % 60) + 'min'
    },

    formatFullDate: function(date) {
      if (!date) return '—'
      try {
        return new Date(date).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
      } catch (e) { return String(date) }
    },

    // Formato numérico MX con separadores de miles. Usado por la plantilla
    // de CPR para población (5,181 en vez de 5181).
    formatNumber: function(n) {
      if (n == null || isNaN(n)) return '—'
      try { return Number(n).toLocaleString('es-MX') }
      catch (e) { return String(n) }
    },

    formatNotaDate: function(date) {
      if (!date) return ''
      try {
        return new Date(date).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
      } catch (e) { return String(date) }
    },

    _decodeHtml: function(html) {
      if (!html) return ''
      // String replace puro — sin tocar DOM (seguro frente a XSS). Decodifica
      // entidades nombradas + numéricas decimales y hex (comunes en notas de
      // CAD mexicano: &#241; = ñ, &#225; = á).
      var decodeOnce = function(s) {
        return String(s)
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .replace(/&nbsp;/g, ' ')
          .replace(/&#(\d+);/g, function(_, code) { return String.fromCharCode(parseInt(code, 10)) })
          .replace(/&#x([0-9a-fA-F]+);/g, function(_, hex) { return String.fromCharCode(parseInt(hex, 16)) })
      }
      // El backend CAD escapa las entidades MÚLTIPLES veces (cada guardado
      // re-escapa: &amp;amp;amp;…lt;). Decodificamos iterativamente hasta que se
      // estabilice, con tope de iteraciones por seguridad.
      var prev
      var cur = String(html)
      var guard = 0
      do {
        prev = cur
        cur = decodeOnce(cur)
        guard++
      } while (cur !== prev && guard < 20)
      // NO se strippean los < > : en estas notas envuelven TEXTO real
      // (marcadores tipo "< RESOLUCIÓN >"), no etiquetas HTML — strippearlos
      // borraba notas que están enteras dentro de < >. El render es {{ }}
      // (texto interpolado), así que mostrar < > es seguro (sin XSS).
      return cur.trim()
    },


    sosCallService() {
      return this.sosCallServiceGetter ? this.sosCallServiceGetter() : null
    },

    reportantDisplayName() {
      const d = this.data || {}
      const ud = d.userData || {}
      const name = [ud.name, ud.lastName1, ud.lastName2].filter(Boolean).join(' ').trim()
      return name || d.name || d.callbackNumber || ud.phoneNumber || d.phone || '—'
    },

    sosTypeLabel() {
      const t = this.data && this.data.type
      if (t === 'VIDEO_CALL') return 'Videollamada'
      if (t === 'VOICE_CALL' || t === 'CALL') return 'Llamada de voz'
      if (t === 'CHAT') return 'Chat'
      return 'SOS'
    },

    isThisCallActive() {
      const active = this.$store.state.map && this.$store.state.map.sosActiveCall
      if (!active || !this.data) return false
      return (active._id || active.id) === (this.data._id || this.data.id)
    },


    genericFields() {
      const out = {}
      if (!this.data) return out
      for (const [k, v] of Object.entries(this.data)) {
        if (GENERIC_SKIP.has(k)) continue
        if (typeof v === 'object' && v !== null) continue
        if (typeof v === 'boolean') {
          out[k] = v ? 'Sí' : 'No'
        } else {
          out[k] = v
        }
      }
      return out
    },

    // ── Generic fallback computeds (DRAWERS-VARIANTS-SPEC §8.3) ──
    // El drawer genérico usa los mismos computeds base pero reorganiza
    // los campos en las 4 zonas. Accedemos al raw payload via
    // genericRawData() (bypasa el computed this.data, que mostraba
    // "function () { [native code] }" para alertas de external_sensor —
    // el computed y el tick del drawer parecen colisionar en algun path).
    // Adicionalmente, cada getter filtra valores no-primitivos para que
    // jamas se escape una funcion u objeto al template.
    genericRawData() {
      var monitor = this.item && this.item.data
      if (!monitor || typeof monitor !== 'object') return {}
      var inner = monitor.data
      if (inner && typeof inner === 'object' && !Array.isArray(inner)) return inner
      return monitor
    },

    genericId() {
      var d = this.genericRawData
      var id = d.id || d._id || d.uniqueId || d.uuid
      if (id == null) return ''
      var t = typeof id
      if (t !== 'string' && t !== 'number') return ''
      var s = String(id)
      if (s.indexOf('[native code]') !== -1) return ''
      return s
    },

    genericStatus() {
      var d = this.genericRawData
      var status = d.status || (d.additionalData && d.additionalData.status) || ''
      if (typeof status !== 'string') return ''
      if (status.indexOf('[native code]') !== -1) return ''
      return status
    },

    genericLocation() {
      var d = this.genericRawData
      var lat = d.latitude || d.lat
      var lng = d.longitude || d.lng
      if (typeof lat !== 'number' || typeof lng !== 'number') return ''
      if (isNaN(lat) || isNaN(lng)) return ''
      return lat.toFixed(5) + ', ' + lng.toFixed(5)
    },

    genericFreshness() {
      // eslint-disable-next-line no-unused-expressions
      this.drawerFreshnessTick
      var d = this.genericRawData
      var ts = d.lastUpdate || d.updatedAt || d.serverTime || d.createdAt
      if (ts == null) return ''
      var tt = typeof ts
      if (tt !== 'string' && tt !== 'number') return ''
      return timeAgo(ts)
    },

    // additionalData parseado: si es objeto, devolvemos pares label/value
    // de sus primitivos. Si es array o string, omitimos. El spec prioriza
    // esta sección sobre otros primitivos porque suele contener la
    // información operativamente relevante del sensor externo.
    genericAdditionalData() {
      var d = this.genericRawData
      if (!d.additionalData) return []
      var ad = d.additionalData
      if (typeof ad !== 'object' || Array.isArray(ad)) return []
      var out = []
      var keys = Object.keys(ad)
      for (var i = 0; i < keys.length; i++) {
        var k = keys[i]
        var v = ad[k]
        if (v == null || v === '') continue
        var t = typeof v
        if (t === 'object' || t === 'function') continue
        if (t === 'boolean') {
          out.push({ label: k, value: v ? 'Sí' : 'No' })
        } else {
          out.push({ label: k, value: String(v) })
        }
      }
      return out
    },

    // Campos primitivos de Zona 4: todo lo del raw que no entró en Zona 1
    // ni en additionalData de Zona 3, excluyendo lo que GENERIC_SKIP ya
    // descartaba (id, timestamps).
    genericZone4Fields() {
      var d = this.genericRawData
      var zone1Keys = { id: 1, _id: 1, uniqueId: 1, uuid: 1, status: 1, latitude: 1, longitude: 1, lat: 1, lng: 1, lastUpdate: 1 }
      var out = []
      var keys = Object.keys(d)
      for (var i = 0; i < keys.length; i++) {
        var k = keys[i]
        if (GENERIC_SKIP.has(k)) continue
        if (zone1Keys[k]) continue
        if (k === 'additionalData') continue
        var v = d[k]
        if (v == null || v === '') continue
        var t = typeof v
        if (t === 'object' || t === 'function') continue
        if (t === 'boolean') {
          out.push({ label: k, value: v ? 'Sí' : 'No' })
        } else {
          out.push({ label: k, value: String(v) })
        }
      }
      return out
    },

  },
}
</script>

<style scoped>
/* Muestra del color del grupo (botones de pánico) junto a la fila "Grupo". */
.group-color-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex: none;
  align-self: center;
}

.map-drawer {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: var(--kln-bg-surface);
  border-left: 1px solid var(--kln-border-normal);
  border-right: 1px solid var(--kln-border-normal);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  pointer-events: all;
}

/* Cuando una CameraStream del drawer está maximizada, neutralizamos el
   backdrop-filter del drawer: de otro modo crea un containing block que
   confina el contenedor fixed del stream a la caja del drawer (en lugar del
   viewport). El drawer queda tapado por la vista maximizada, así que es
   invisible. Reusa el mismo iframe → sin recarga del stream. */
.map-drawer--maximized {
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

/* SimpleBar envuelve el contenido scrolleable en .simplebar-mask, que tiene
   `position: absolute; z-index: 0` → crea un stacking context que ATRAPA el
   contenedor fixed del stream maximizado: geométricamente cubre la pantalla,
   pero el tray inferior (que pinta después en el DOM) le queda encima.
   Neutralizar el z-index del mask mientras hay una cámara maximizada deja
   que el fixed (z-index 9500) escape al stacking context raíz y quede por
   encima del tray. Sólo aplica maximizado, así que no afecta el scroll. */
.map-drawer--maximized ::v-deep .simplebar-mask {
  z-index: auto !important;
}

.drawer-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 12px 8px;
  border-left: 3px solid transparent;
  flex-shrink: 0;
}

.drawer-avatar {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: 0.9;
}
/* Cuando mostramos el SVG del marker (devices), el propio SVG trae su
   container coloreado → el avatar va sin fondo y sin recorte. */
.drawer-avatar--svg {
  background: transparent !important;
  border-radius: 0;
  opacity: 1;
}
.drawer-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.drawer-title-wrap {
  flex: 1;
  min-width: 0;
}

.drawer-header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
}

.drawer-id {
  font-size: 10px;
  color: var(--kln-text-dim);
  max-width: 80px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.drawer-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--kln-text);
  /* Familia explícita de fuentes de emoji para que la bandera de país
     en el título de los reportantes ('🇲🇽 (22) 2255-0167') se renderice
     con color y escudo nativos en lugar de un fallback monocromático. */
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    "Twemoji Mozilla", "Apple Color Emoji", "Segoe UI Emoji",
    "Noto Color Emoji", sans-serif;
  /* Line-clamp a 2 líneas: nombres largos como "ECATEPEC DE MORELOS
     CUADRANTE 19" (32 chars) no caben en una sola linea con el ancho
     del drawer (340 px). Antes se truncaba a "ECATEPEC DE MORELOS C..."
     y dos cuadrantes adyacentes parecian el mismo. Con 2 lineas + tooltip
     nativo (atributo title del elemento) el operador siempre puede
     identificar la geocerca exacta. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  line-height: 1.25;
}

.drawer-subtitle {
  font-size: 11px;
  color: var(--kln-text-dim);
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.drawer-subtitle > span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

/* Bandera de país inyectada via v-html en el row "Teléfono" del
   reportante (componente hijo DrawerZoneRow). Se usa ::v-deep porque
   el span vive dentro del scope del child. La fuente de emojis
   garantiza render en color en lugar de monocromático. */
::v-deep .phone-flag {
  display: inline-block;
  margin-right: 4px;
  font-family: "Twemoji Mozilla", "Apple Color Emoji", "Segoe UI Emoji",
    "Noto Color Emoji", "Android Emoji", sans-serif;
}

/* Ícono `i` inline al lado del subtítulo (solo para incident). Mantiene
   el flujo del header sin agregar altura. */
.drawer-subtitle__info-btn {
  margin: 0 !important;
  width: 18px !important;
  height: 18px !important;
  flex-shrink: 0;
}

.drawer-close {
  color: var(--kln-text-dim) !important;
  flex-shrink: 0;
}

.drawer-status-row {
  padding: 0 12px 8px;
  flex-shrink: 0;
}

.status-chip {
  font-size: 10px !important;
}

.drawer-actions {
  display: flex;
  gap: 6px;
  padding: 0 12px 6px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.drawer-action-btn {
  font-size: 11px !important;
  height: 26px !important;
}

/* Texto e icono SIEMPRE blancos en el botón de acción SOS (Finalizar = rojo,
   Atender/Unirse = verde). El tono 'success' usa texto oscuro por defecto;
   lo forzamos a blanco solo aquí, por pedido del operador. El label hereda el
   color y el icono usa color:inherit, así que basta con el color del botón. */
.drawer-action-btn--white {
  color: #fff !important;
}

/* Variante icon-only del action button (Centrar y Maps en incident).
   Vuetify v-btn icon le da padding chico pero perdimos el outlined border;
   forzamos cuadrado del mismo alto que los botones con texto para alineación. */
.drawer-action-btn--icon {
  width: 26px !important;
  min-width: 26px !important;
  padding: 0 !important;
}

.drawer-divider {
  border-color: var(--kln-border-normal) !important;
  flex-shrink: 0;
}

.drawer-scroll-content {
  flex: 1;
  min-height: 0;
  /* SimpleBar gestiona el overflow internamente. NO seteamos
     overflow aqui porque eso interferiria con su DOM wrapper.
     SimpleBar crea hijos .simplebar-wrapper > .simplebar-mask >
     .simplebar-content-wrapper que manejan el scroll real. */
}

.drawer-details {
  padding: 0 0 16px;
}

/* ──────────────────────────────────────────────────────────
   Sistema de zonas del drawer (DRAWERS-DESIGN-SPEC §1-7).
   Anatomía universal aplicada a las 5 variantes críticas del drawer
   derecho (device, bus, sensor, incident, sos_call) y a las 2 de
   Murphy. El spacing entre zonas es mayor que entre secciones dentro
   de una zona — eso refuerza la separación conceptual del operador.
   ────────────────────────────────────────────────────────── */

.drawer-zone {
  padding-top: 10px;
}

.drawer-zone + .drawer-zone {
  margin-top: 4px;
  padding-top: 10px;
}

/* Zona 1 — Crítica: arranca inmediato tras la barra de acciones, sin
   espacio muerto. El primer campo "Identificador" debe quedar cerca de
   los botones Centrar/Dejar/Descartar para evitar la sensación de
   desconexión visual. */
.drawer-zone--1 {
  padding-top: 8px;
}

/* Zona 1B — Variante grid 2-col para campos cortos (Identificador,
   Categoría, Prioridad, Estado, Hora, Catalogado) → ahorra altura. Cada
   drawer-zone-row es un bloque flex column; el grid los reparte en celdas
   sin tocar el componente. */
.drawer-zone--grid2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 12px;
  row-gap: 0;
}

/* Padding interno de las celdas del grid reducido a la mitad. Dentro del
   grid los 8px arriba+abajo del padding default (DrawerZoneRow) generan
   ~16px entre líneas de labels, demasiado para el bloque crítico. */
.drawer-zone--grid2 ::v-deep .drawer-zone-row--zone-1 {
  padding-top: 4px;
  padding-bottom: 4px;
}

/* Bloques drawer-zone--1 consecutivos: sin gap extra para que la lectura
   del bloque crítico fluya como un único cuerpo (grid → Tipo →
   Descripción/Reportante → Dirección) en vez de tarjetas separadas. */
.drawer-zone--1 + .drawer-zone--1 {
  margin-top: 0;
  padding-top: 0;
}

/* Mismo padding reducido para drawer-zone-row sueltos fuera del grid pero
   dentro de drawer-zone--1 (Tipo, Descripción, Nombre, Teléfono, Dirección). */
.drawer-zone--1 ::v-deep .drawer-zone-row--zone-1 {
  padding-top: 4px;
  padding-bottom: 4px;
}

/* Zona 2 — Acción: contexto de acción + botón primario + secundarios. */
.drawer-zone--2 {
  padding: 10px 12px 14px 12px;
}

.drawer-zone__context {
  font-size: 12px;
  color: var(--kln-text-muted, rgba(255, 255, 255, 0.6));
  margin-bottom: 12px;
  line-height: 1.4;
}

/* Contexto con tratamiento de alerta (fondo ligeramente rojo, color saturado). */
.drawer-zone__context--alert {
  color: var(--kln-alert-danger, #ff5252);
  background: var(--kln-accent-soft, rgba(255, 82, 82, 0.08));
  border-left: 2px solid var(--kln-alert-danger, #ff5252);
  padding: 8px 10px;
  border-radius: 3px;
  font-weight: 500;
}

.drawer-zone__content {
  padding-top: 4px;
}

/* Zona 3 — Contextual: bloques con section-header. */
.drawer-zone--3 {
  padding-top: 8px;
}

/* Zona 4 — Administrativa: colapsable, contenido más comprimido.
   Cuando está colapsada no dejamos padding-bottom amplio — evita gap
   muerto entre "Datos administrativos ▼" y la sección "Recursos
   cercanos" que viene debajo. El padding real solo se abre cuando
   el contenido está visible. */
.drawer-zone--4 {
  padding-top: 6px;
  padding-bottom: 8px;
}

.drawer-zone--4 .drawer-zone__content {
  padding-top: 6px;
  padding-bottom: 16px;
}

/* Evento activo de device: item dentro del bloque "Eventos activos" */
.drawer-event-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 6px 12px;
  font-size: 12px;
  border-bottom: 1px solid var(--kln-border-dim);
}

.drawer-event-row__type {
  color: var(--kln-alert-danger);
  font-weight: 500;
}

.drawer-event-row__ts {
  color: var(--kln-text-dim, rgba(255, 255, 255, 0.4));
  font-size: 11px;
}

.drawer-camera-action {
  padding: 8px 12px;
}

.drawer-description {
  padding: 8px 12px 0;
}

.drawer-description-label {
  font-size: 10px;
  color: var(--kln-text-dim);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
}

.drawer-description-text {
  font-size: 12px;
  color: var(--kln-text-muted);
  line-height: 1.5;
}

/* ── PMI sub-cameras ── */
.pmi-cameras {
  padding: 4px 0;
}

.pmi-cameras-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px 4px;
}

.pmi-cameras-title-btn {
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--kln-text-muted);
}

.pmi-cameras-title-btn:hover {
  color: var(--kln-text);
}

.pmi-cameras-title {
  font-size: 11px;
  font-weight: 600;
  color: inherit;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.pmi-layout-toggle {
  display: flex;
  gap: 2px;
}

.pmi-layout-active {
  color: var(--kln-accent) !important;
}

.pmi-cameras-grid {
  display: grid;
  gap: 4px;
  padding: 0 8px;
}

.pmi-cam-card {
  padding: 6px 12px;
  border-bottom: 1px solid var(--kln-border-dim);
}

/* Card en estado colapsado: el <CameraStream> está desmontado, así que
   no necesita margin debajo del header. */
.pmi-cam-card--collapsed .pmi-cam-header {
  margin-bottom: 0;
}

.pmi-cam-header {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
}

.pmi-cam-collapse-btn {
  color: var(--kln-text-muted) !important;
  flex-shrink: 0;
}

.pmi-cam-name {
  flex: 1;
  font-size: 11px;
  font-weight: 500;
  color: var(--kln-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

/* Boton de pin: estado activo (camara pineada en el tray).
   Background violet sutil + color violet del icono hacen obvia
   la diferencia con el estado no-pineado (icono outline default). */
.pin-btn--active {
  background: var(--kln-accent-soft) !important;
}

.pin-btn--active:hover {
  background: var(--kln-accent-glow) !important;
}

/* ── Nearby entities ── */
.drawer-nearby {
  border-top: 1px solid var(--kln-border-normal);
  padding: 4px 0;
}

.nearby-toggle {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: none;
  color: var(--kln-text-muted);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.nearby-toggle:hover {
  color: var(--kln-text);
}

.nearby-toggle-label {
  flex: 1;
  text-align: left;
}

.nearby-toggle-radius {
  color: var(--kln-text);
  font-weight: 600;
  font-size: 12px;
  text-transform: none;
  letter-spacing: 0;
  margin-left: 8px;
}

.nearby-radius-control {
  padding: 0 12px 8px;
  border-bottom: 1px solid var(--kln-border-dim);
  margin-bottom: 4px;
}

.nearby-radius-slider {
  margin: 0;
  padding: 0;
}

.nearby-group-toggle {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 0;
  border: none;
  background: none;
  color: var(--kln-text-muted);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: left;
}

.nearby-group-toggle:hover {
  color: var(--kln-text);
}

.nearby-group-chevron {
  margin-right: 4px;
}

.nearby-empty {
  padding: 10px 12px 14px;
  display: flex;
  align-items: center;
  font-size: 12px;
  color: var(--kln-text-dim);
  font-style: italic;
}

.nearby-section {
  padding: 0 12px 8px;
}

.nearby-item {
  padding: 6px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}

.nearby-item:hover {
  background: var(--kln-border-dim);
}

.nearby-item-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.nearby-name {
  font-size: 12px;
  color: var(--kln-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.nearby-dist {
  font-size: 10px;
  color: var(--kln-text-dim);
  margin-left: 8px;
  flex-shrink: 0;
}

.nearby-status {
  font-size: 10px;
  color: var(--kln-text-dim);
}

.nearby-status--online { color: var(--kln-alert-success); }
.nearby-status--offline { color: var(--kln-alert-danger); }

/* Sección "Equipo del elemento" (empate radio ↔ unidad por conductor).
   Las filas reusan .nearby-item; aquí solo los sub-encabezados de grupo. */
.equipo-group + .equipo-group {
  margin-top: 8px;
}

.equipo-group__label {
  display: flex;
  align-items: center;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--kln-text-dim);
  padding: 4px 0 2px;
}

.equipo-group__more {
  font-size: 10px;
  color: var(--kln-text-dim);
  padding: 2px 0 0 8px;
}

/* Device empatado pero sin posición en el mapa: informativo, no clickeable. */
.equipo-item--offmap {
  cursor: default;
  opacity: 0.75;
}

.equipo-offmap-icon {
  margin-right: 2px;
  color: var(--kln-text-dim) !important;
}

.nearby-item--expanded {
  background: var(--kln-border-dim);
  border-left: 2px solid var(--kln-accent);
}

.nearby-item--active-route {
  background: rgba(255, 87, 34, 0.08);
  border-left: 2px solid var(--kln-entity-orange, #FF5722);
}

.nearby-item--active-route .nearby-name {
  color: var(--kln-entity-orange, #FF5722);
  font-weight: 600;
}

.nearby-route-clear {
  margin-left: 6px;
  color: var(--kln-entity-orange, #FF5722) !important;
  cursor: pointer;
  opacity: 0.85;
  transition: opacity 0.15s, transform 0.15s;
}

.nearby-route-clear:hover {
  opacity: 1;
  transform: scale(1.15);
}

.nearby-expanded {
  padding-top: 6px;
}

.nearby-exp-row {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  padding: 2px 0;
  color: var(--kln-text-muted);
}

.nearby-exp-label {
  color: var(--kln-text-dim);
}

.nearby-video-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 6px;
}

.nearby-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.nearby-video-offline {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: rgba(0, 0, 0, 0.7);
  font-size: 11px;
  color: rgba(255, 255, 255, 0.4);
}

.nearby-sub-cam {
  margin-bottom: 8px;
}

.nearby-exp-actions {
  display: flex;
  gap: 6px;
  padding-top: 6px;
}

/* ─── Murphy results ─── */
.kpi-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 12px;
}
.kpi-loading__text {
  font-size: 11px;
  color: var(--kln-text-muted);
}
.kpi-section {
  padding: 4px 0;
  border-bottom: 1px solid var(--kln-border-dim);
}

/* Ficha del encargado (comisario) — visible solo en geocercas de mando unico */
.kpi-encargado {
  padding: 0 0 6px 0;
}
.kpi-encargado__body {
  display: flex;
  gap: 10px;
  padding: 8px 12px;
  align-items: flex-start;
}
.kpi-encargado__photo-wrap {
  flex-shrink: 0;
}
.kpi-encargado__photo {
  width: 72px;
  height: 90px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid var(--kln-border-normal);
  background: rgba(0, 0, 0, 0.3);
  display: block;
}
.kpi-encargado__photo--fallback {
  display: flex;
  align-items: center;
  justify-content: center;
}
.kpi-encargado__info {
  flex: 1 1 auto;
  min-width: 0;
}

.kpi-incidents {
  padding: 10px 12px 12px;
}
.kpi-incidents__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 8px;
}
.kpi-incidents__title {
  font-size: 11px;
  font-weight: 600;
  color: var(--kln-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.kpi-incidents__period {
  font-size: 10px;
  color: var(--kln-text-dim);
}
.kpi-incidents__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.kpi-card {
  position: relative;
  background: var(--kln-border-dim);
  border: 1px solid var(--kln-border-normal);
  border-radius: 8px;
  padding: 10px 12px 10px 14px;
  min-height: 68px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  transition: background 0.15s ease, border-color 0.15s ease;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  color: inherit;
}
.kpi-card:hover {
  background: var(--kln-bg-card);
  border-color: var(--kpi-card-color, var(--kln-border-normal));
}
.kpi-card--active {
  background: color-mix(in srgb, var(--kpi-card-color) 15%, transparent);
  border-color: var(--kpi-card-color) !important;
}
.kpi-card--active .kpi-card__icon {
  opacity: 1;
}
.kpi-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: var(--kpi-card-color, var(--kln-kpi-publico));
}
.kpi-card__icon {
  position: absolute;
  top: 8px;
  right: 8px;
  color: var(--kpi-card-color, var(--kln-kpi-publico)) !important;
  opacity: 0.55;
}
.kpi-card__value {
  font-size: 24px;
  font-weight: 700;
  color: var(--kln-text);
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}
.kpi-card__label {
  font-size: 10px;
  color: var(--kln-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-top: 3px;
}
.kpi-card--seguridad { --kpi-card-color: var(--kln-kpi-seguridad); }
.kpi-card--medico    { --kpi-card-color: var(--kln-kpi-medico); }
.kpi-card--pcivil    { --kpi-card-color: var(--kln-kpi-pcivil); }
.kpi-card--publico   { --kpi-card-color: var(--kln-kpi-publico); }

.kpi-incidents__total {
  margin-top: 10px;
  padding: 10px 14px;
  background: var(--kln-border-dim);
  border: 1px solid var(--kln-border-normal);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.kpi-incidents__total-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--kln-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.kpi-incidents__total-value {
  font-size: 22px;
  font-weight: 700;
  color: var(--kln-text);
  font-variant-numeric: tabular-nums;
}

.kpi-cat-list {
  padding: 2px 0 6px;
}
.kpi-cat-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 7px 12px;
  border-bottom: 1px solid var(--kln-border-dim);
}
.kpi-cat-row:last-child {
  border-bottom: none;
}
.kpi-cat-row__count {
  flex: 0 0 44px;
  min-width: 44px;
  font-size: 14px;
  font-weight: 700;
  color: var(--kln-text);
  text-align: right;
  font-variant-numeric: tabular-nums;
  line-height: 1.35;
}
.kpi-cat-row__label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 11px;
  color: var(--kln-text-muted);
  line-height: 1.35;
  word-break: break-word;
}
.kpi-fuente {
  font-size: 9px;
  color: var(--kln-text-dim);
  padding: 10px 12px;
  line-height: 1.4;
}
.kpi-fuente__timestamp {
  margin-top: 4px;
  font-variant-numeric: tabular-nums;
  color: var(--kln-text-muted);
}

/* ── KPI: Contexto (alcance + frescura + refresh) ── */
.kpi-context {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--kln-border-dim);
  background: var(--kln-bg-surface);
}
.kpi-context__scope {
  display: inline-flex;
  align-items: center;
  min-width: 0;
}
.kpi-context__scope-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--kln-text, rgba(255, 255, 255, 0.85));
  letter-spacing: 0.02em;
  /* Line-clamp 2 lineas + tooltip nativo (atributo title). Antes el
     "Geocerca: ECATEPEC DE MORELOS CUADRANTE 19" se truncaba a "Geocerca:
     ECATEPEC DE MORELOS CU..." y dos cuadrantes parecian iguales. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  line-height: 1.3;
}
.kpi-context__freshness {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.kpi-freshness {
  font-size: 10px;
  color: var(--kln-text-muted, rgba(255, 255, 255, 0.55));
  font-variant-numeric: tabular-nums;
}
.kpi-freshness--stale {
  color: var(--kln-text-dim, rgba(255, 255, 255, 0.4));
  font-style: italic;
}
.kpi-context__refresh {
  width: 20px !important;
  height: 20px !important;
}

/* ── KPI: Carga operativa (barra apilada + recursos) ── */
.kpi-resource {
  padding: 8px 12px;
  border-bottom: 1px solid var(--kln-border-dim);
}
.kpi-resource:last-child {
  border-bottom: none;
}
.kpi-resource--alert {
  border-left: 2px solid var(--kln-alert-danger, #ff5252);
  padding-left: 10px;
  background: color-mix(in srgb, var(--kln-alert-danger) 3%, transparent);
}
.kpi-resource__header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 5px;
}
.kpi-resource__icon {
  color: var(--kln-text-muted, rgba(255, 255, 255, 0.55)) !important;
  flex-shrink: 0;
}
.kpi-resource__name {
  flex: 1 1 auto;
  font-size: 12px;
  font-weight: 500;
  color: var(--kln-text, rgba(255, 255, 255, 0.9));
  min-width: 0;
}
.kpi-resource__primary {
  font-size: 12px;
  font-weight: 700;
  color: var(--kln-text);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}
.kpi-resource__legend {
  margin-top: 5px;
  font-size: 10px;
  color: var(--kln-text-muted, rgba(255, 255, 255, 0.55));
  line-height: 1.35;
  font-variant-numeric: tabular-nums;
}
.kpi-resource__legend-alert {
  color: var(--kln-alert-danger, #ff5252);
  font-weight: 600;
}
.kpi-resource__legend-hint {
  display: block;
  margin-top: 2px;
  font-size: 9px;
  color: var(--kln-text-dim, rgba(255, 255, 255, 0.35));
  font-style: italic;
}
.kpi-resource--static .kpi-resource__header {
  margin-bottom: 0;
}

/* Barra horizontal apilada: flexbox con segmentos que reparten el 100%. */
.kpi-stacked-bar {
  display: flex;
  width: 100%;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
  background: var(--kln-border-dim);
}
.kpi-stacked-bar__seg {
  flex: 0 0 0%;
  min-width: 0;
  height: 100%;
  transition: flex-basis 0.3s ease;
}
.kpi-stacked-bar__seg--online { background: var(--kln-status-online, #1E9553); }
.kpi-stacked-bar__seg--alert   { background: var(--kln-status-offline, #8a3a3a); }
.kpi-stacked-bar__seg--danger  { background: var(--kln-alert-danger, #ff5252); }
.kpi-stacked-bar__seg--warning { background: var(--kln-status-alert, #e0a040); }
.kpi-stacked-bar__seg--muted   { background: var(--kln-status-idle, #7a7a80); }
.kpi-stacked-bar__seg--down    { background: var(--kln-text-dim, rgba(255, 255, 255, 0.25)); }

/* ── KPI: Listas de categorías (críticas vs actividad) ── */
.kpi-cat-row--clickable {
  cursor: pointer;
  background: transparent;
  border-left: none;
  border-right: none;
  border-top: none;
  width: 100%;
  text-align: left;
  font-family: inherit;
  color: inherit;
  transition: background 0.15s ease;
}
.kpi-cat-row--clickable:hover {
  background: var(--kln-border-dim);
}
.kpi-cat-row--critical .kpi-cat-row__count {
  color: var(--kln-alert-danger, #ff5252);
}
.kpi-cat-row__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.kpi-cat-row__priority {
  font-size: 10px;
  color: var(--kln-text-muted, rgba(255, 255, 255, 0.55));
  font-variant-numeric: tabular-nums;
}
.kpi-cat-list--compact .kpi-cat-row {
  padding: 5px 12px;
}
.kpi-cat-list--compact .kpi-cat-row__count {
  font-size: 12px;
}
.kpi-cat-list--compact .kpi-cat-row__label {
  font-size: 10.5px;
}

.kpi-section--empty .kpi-empty,
.kpi-events .kpi-empty {
  padding: 14px 12px 16px;
  font-size: 11px;
  color: var(--kln-text-muted, rgba(255, 255, 255, 0.55));
  font-style: italic;
  text-align: center;
}

/* ── KPI: Tabs de eventos (Críticos / Todos) ── */
.kpi-events {
  padding-top: 0;
}
.kpi-events-tabs {
  display: flex;
  gap: 4px;
  padding: 8px 12px 0 12px;
  border-top: 1px solid var(--kln-border-normal);
}
.kpi-events-tab {
  flex: 1 1 0;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 10px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 4px 4px 0 0;
  border-bottom: 2px solid transparent;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--kln-text-muted, rgba(255, 255, 255, 0.55));
  cursor: pointer;
  font-family: inherit;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
}
.kpi-events-tab:hover {
  color: var(--kln-text, rgba(255, 255, 255, 0.85));
  background: var(--kln-border-dim);
}
.kpi-events-tab--active {
  color: var(--kln-text, rgba(255, 255, 255, 0.95));
  border-bottom-color: var(--kln-accent, #00D4FF);
  background: var(--kln-bg-surface);
}
.kpi-events-tab--alert {
  color: var(--kln-alert-danger, #ff5252);
  border-bottom-color: var(--kln-alert-danger, #ff5252);
}
.kpi-events-tab__label {
  flex: 0 1 auto;
  white-space: nowrap;
}
.kpi-events-tab__count {
  flex: 0 0 auto;
  font-weight: 500;
  font-size: 10px;
  color: var(--kln-text-dim, rgba(255, 255, 255, 0.45));
  font-variant-numeric: tabular-nums;
}
.kpi-events-tab--active .kpi-events-tab__count {
  color: var(--kln-text-muted, rgba(255, 255, 255, 0.7));
}
.kpi-events-tab--alert .kpi-events-tab__count {
  color: var(--kln-alert-danger, #ff5252);
  opacity: 0.75;
}

/* Carga operativa: el body (contenido del colapsable) necesita un padding
   ligero porque drawer-section-header ya toma 10/6. */
.kpi-load__body {
  padding: 4px 0 8px;
}

/* ── Generic section title (KPI, etc.) ── */
.section-title {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--kln-text-muted);
  padding: 0 0 0 4px;
  border-top: 1px solid var(--kln-border-normal);
  margin-top: 0;
}

/* ── PTZ controls overlay ── */
.ptz-cam-wrap {
  position: relative;
}

/* Indicador in-situ de fallo PTZ: píldora discreta sobre el video que se
   desvanece sola (~2.6s, sincronizado con el timer del componente).
   Arriba para NO tapar el dpad PTZ de abajo (el operador está mirando/
   clickeando esos botones). */
.ptz-error-flash {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 6;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(10, 10, 15, 0.82);
  border: 1px solid var(--kln-alert-danger);
  color: rgba(255, 255, 255, 0.92);
  font-size: 11px;
  white-space: nowrap;
  pointer-events: none;
  animation: ptz-error-cycle 2.6s ease forwards;
}

.ptz-error-flash .v-icon {
  animation: ptz-error-shake 0.4s ease 0.15s;
}

@keyframes ptz-error-cycle {
  0% { opacity: 0; }
  8% { opacity: 1; }
  82% { opacity: 1; }
  100% { opacity: 0; }
}

@keyframes ptz-error-shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-3px); }
  75% { transform: translateX(3px); }
}

.ptz-controls {
  position: absolute;
  bottom: 8px;
  right: 8px;
  display: flex;
  gap: 6px;
  opacity: 0.7;
  z-index: 2;
}

.ptz-controls:hover {
  opacity: 1;
}

.ptz-dpad {
  width: 80px;
}

.ptz-zoom {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Foto del usuario del device + iconos LTE/TETRA/tipo. Replica el
   bloque FOTO del fieldsMap legacy de forma declarativa. */
.device-photo-row {
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  gap: 10px;
  padding: 8px 0;
  margin-bottom: 4px;
}

.device-photo-wrap {
  flex-shrink: 0;
}

.device-photo {
  width: 120px;
  height: 150px;
  object-fit: cover;
  image-orientation: none;
  border: 3px solid #621132;
  border-radius: 4px;
  background: var(--kln-bg-surface);
}

.device-badge-col {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
}

.device-badge {
  width: 32px;
  height: 32px;
  object-fit: contain;
  image-orientation: none;
  border-radius: 20%;
}

.device-badge--wide {
  width: 64px;
  height: 40px;
}

/* Foto del hit LPR (captura del arco). Se muestra a ancho completo del
   drawer con click para zoom. Mantiene aspect ratio natural. */
.hit-photo-row {
  display: flex;
  justify-content: center;
  padding: 8px 0;
  margin-top: 4px;
}

.hit-photo {
  max-width: 100%;
  max-height: 300px;
  border-radius: 4px;
  cursor: zoom-in;
  background: #000;
}

.hit-photo-zoom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 9500;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
}

.hit-photo-zoom__close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.4) !important;
  color: #fff !important;
}

.hit-photo-zoom__img {
  max-width: 95vw;
  max-height: 95vh;
  object-fit: contain;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

/* Value row con accion inline (ej. boton buscar Murphy junto a la placa) */
.detail-row__value--with-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

/* ── Partner logos (Carbyne / DiDi / Uber / RapidSOS) ─────────────── */
/* Fila "Fuente" del drawer del reportante. Replica el look de zone-1
   (label uppercase 10px arriba, valor 14px bold abajo) pero con un
   logo del proveedor a la izquierda del nombre. NO podemos reutilizar
   las clases del child DrawerZoneRow porque son scoped y no aplican
   fuera de ese componente. */
.partner-fuente-row {
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  gap: 2px;
}

.partner-fuente-row__label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--kln-text-muted, rgba(255, 255, 255, 0.55));
}

.partner-fuente-row__value {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Logo del proveedor con fondo blanco para garantizar legibilidad sobre
   el panel dark (algunos PNGs tienen trazo oscuro y fondo transparente).
   El fondo + padding pequeños equivalen a un mini-chip. */
.partner-fuente-row__logo {
  height: 24px;
  width: auto;
  max-width: 84px;
  background: #fff;
  padding: 2px 5px;
  border-radius: 3px;
  object-fit: contain;
  flex-shrink: 0;
}

.partner-fuente-row__name {
  font-size: 14px;
  font-weight: 600;
  color: var(--kln-text, rgba(255, 255, 255, 0.92));
}

/* Bloque "Interconexión" del drawer de incidente — logo del proveedor
   a la izquierda + label arriba + hint abajo. Comparte styling base
   del logo con la fila Fuente del reportante. */
.incident-partner-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
}

.incident-partner-row .partner-fuente-row__logo,
.incident-partner-logo {
  height: 28px;
  width: auto;
  max-width: 90px;
  background: #fff;
  padding: 3px 6px;
  border-radius: 4px;
  object-fit: contain;
  flex-shrink: 0;
}

.incident-partner-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.incident-partner-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--kln-text);
  letter-spacing: 0.02em;
}

.incident-partner-hint {
  font-size: 11px;
  font-weight: 400;
  color: var(--kln-text-dim);
  margin-top: 2px;
}

.detail-row__action {
  width: 20px !important;
  height: 20px !important;
  opacity: 0.7;
  transition: opacity 0.15s, background 0.15s;
}

.detail-row__action:hover {
  opacity: 1;
  background: var(--kln-accent-soft) !important;
}


</style>

<style>
/* Detail row — not scoped so functional component can use */
.detail-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  border-bottom: 1px solid var(--kln-border-dim);
}

.detail-row__icon {
  color: var(--kln-text-dim) !important;
  flex-shrink: 0;
}

.detail-row__label {
  font-size: 11px;
  color: var(--kln-text-dim);
  flex-shrink: 0;
}

.detail-row__value {
  font-size: 12px;
  color: var(--kln-text-muted);
  flex: 1;
  text-align: left;
  word-break: break-word;
}

.detail-row__value a {
  color: var(--kln-accent);
  text-decoration: none;
}

.detail-row__value a:hover {
  text-decoration: underline;
}

/* Sección de hits de LPR — historial de detecciones recientes */
.drawer-section-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--kln-accent);
  padding: 10px 12px 4px;
}

.drawer-section-empty {
  font-size: 12px;
  color: var(--kln-text-dim);
  padding: 4px 12px 10px;
  font-style: italic;
}

.drawer-lpr-hits {
  padding: 0 12px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.drawer-lpr-hit-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid var(--kln-border-dim);
  font-size: 12px;
}

.drawer-lpr-hit-plate-group {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.drawer-lpr-hit-plate {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-weight: 600;
  color: var(--kln-text);
  letter-spacing: 0.04em;
}

.drawer-lpr-hit-date {
  color: var(--kln-text-dim);
  font-size: 11px;
}

/* Timeline de notas / razonamientos del incidente */
.incident-notes {
  padding: 8px 12px 10px 12px;
  border-bottom: 1px solid var(--kln-border-dim);
}

.incident-notes__header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
}

.incident-notes__icon {
  color: var(--kln-text-dim) !important;
  flex-shrink: 0;
}

.incident-notes__label {
  font-size: 11px;
  color: var(--kln-text-dim);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.incident-notes__count {
  margin-left: auto;
  padding: 1px 7px;
  border-radius: 10px;
  background: var(--kln-accent-soft);
  color: var(--kln-accent);
  font-size: 10px;
  font-weight: 600;
}

.incident-notes__list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.incident-note {
  padding: 6px 10px;
  border-radius: 4px;
  background: var(--kln-bg-surface);
  border-left: 2px solid var(--kln-accent);
}

.incident-note__time {
  font-size: 10px;
  color: var(--kln-accent);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.25px;
  margin-bottom: 2px;
}

/* Línea de metadatos de cada nota: corporación + autor a la izquierda,
   fecha a la derecha (estilo paridad con legacy IncidentDispatch). */
.incident-note__meta {
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 10px;
  margin-bottom: 3px;
}

.incident-note__corp {
  color: var(--kln-accent);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.incident-note__autor {
  color: var(--kln-text-muted);
  font-weight: 500;
}

.incident-note__meta .incident-note__time {
  margin: 0 0 0 auto;
  color: var(--kln-text-dim);
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
}

.incident-note__msg {
  font-size: 12px;
  color: var(--kln-text-muted);
  line-height: 1.4;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.incident-note__msg--dim {
  color: var(--kln-text-muted);
  font-size: 11px;
  margin-top: 2px;
}

.incident-notes__empty {
  font-size: 11px;
  color: var(--kln-text-dim);
  text-align: center;
  padding: 8px 12px;
  font-style: italic;
}

/* Botón "+ Agregar" en el header de la sección Notas. Discreto, color
   accent, sin background para no competir visualmente con el listado. */
.incident-notes__add-btn {
  display: inline-flex;
  align-items: center;
  background: transparent;
  border: 1px solid var(--kln-border-dim);
  border-radius: 3px;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--kln-accent);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.incident-notes__add-btn:hover {
  background: rgba(73, 196, 232, 0.08);
  border-color: var(--kln-accent);
}

/* Modal Agregar nota: estética alineada con dialogs estándar de Kalan. */
.add-note-dialog__title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid var(--kln-border-dim);
}

.add-note-dialog__body {
  padding: 16px !important;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.add-note-dialog__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.add-note-dialog__label {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--kln-text-muted);
}

/* Contexto del hit en el modal de razonamiento (placa · arco). */
.add-note-dialog__meta {
  font-size: 11px;
  color: var(--kln-text-muted);
  margin-bottom: 10px;
}

.add-note-dialog__meta b {
  color: var(--kln-text);
  font-weight: 600;
}

/* Razonamiento ya registrado, en el cuerpo del drawer. */
.hit-raz {
  padding: 2px 12px 6px 12px;
}

.hit-raz__tipo {
  display: inline-block;
  border: 1px solid currentColor;
  border-radius: 3px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.hit-raz__text {
  margin: 6px 0 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: var(--kln-text);
  white-space: pre-wrap;
  word-break: break-word;
}

.hit-raz__by {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: var(--kln-text-muted);
}

.add-note-dialog__counter {
  font-size: 10px;
  color: var(--kln-text-dim);
  text-align: right;
  margin-top: 2px;
}

.add-note-dialog__error {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #f44336;
  padding: 6px 8px;
  background: rgba(244, 67, 54, 0.08);
  border-left: 3px solid #f44336;
  border-radius: 4px;
}

.add-note-dialog__footer {
  padding: 10px 16px !important;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid var(--kln-border-dim);
}

/* Modal Editar incidente. Reusa el patrón visual del modal Agregar nota. */
.edit-incident-dialog__title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 16px;
  background: rgba(0, 0, 0, 0.3);
  border-bottom: 1px solid var(--kln-border-dim);
}

.edit-incident-dialog__body {
  padding: 16px !important;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.edit-incident-dialog__row {
  display: flex;
  gap: 8px;
}

.edit-incident-dialog__row--align-end {
  align-items: flex-end;
}

.edit-incident-dialog__pick-btn {
  flex-shrink: 0;
  height: 38px;
}

.edit-incident-dialog__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.edit-incident-dialog__field--grow {
  flex: 1 1 0;
  min-width: 0;
}

.edit-incident-dialog__label {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: var(--kln-text-muted);
}

.edit-incident-dialog__error {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #f44336;
  padding: 6px 8px;
  background: rgba(244, 67, 54, 0.08);
  border-left: 3px solid #f44336;
  border-radius: 4px;
}

.edit-incident-dialog__footer {
  padding: 10px 16px !important;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid var(--kln-border-dim);
}

/* ─── SimpleBar overrides para el drawer ─────────────────────
   Las clases .simplebar-* las inyecta SimpleBar al DOM
   dinamicamente, por eso van fuera del scoped. */

/* CRITICO: ocultar el scrollbar nativo del browser del wrapper
   interno de SimpleBar. Sin esto, el scrollbar nativo de macOS
   se ve a traves del scrollbar custom violet (los dos al mismo
   tiempo). El global ::-webkit-scrollbar de main.css aplica a
   todos los elementos, incluido el wrapper interno de SimpleBar,
   por eso necesitamos override explicito para esos hijos. */
.drawer-scroll-content .simplebar-content-wrapper,
.drawer-scroll-content .simplebar-content {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.drawer-scroll-content .simplebar-content-wrapper::-webkit-scrollbar,
.drawer-scroll-content .simplebar-content::-webkit-scrollbar {
  display: none !important;
  width: 0 !important;
  height: 0 !important;
}

/* Track del scrollbar custom — sutil, casi invisible */
.drawer-scroll-content .simplebar-track {
  background: transparent;
  border-radius: 4px;
}

.drawer-scroll-content .simplebar-track.simplebar-vertical {
  width: 8px;
  right: 2px;
  top: 4px;
  bottom: 4px;
}

.drawer-scroll-content .simplebar-track.simplebar-horizontal {
  height: 8px;
}

/* Thumb del scrollbar custom — gris translucido sutil en idle.
   Solo en hover/active toma color violet de la paleta C4/C5. */
.drawer-scroll-content .simplebar-scrollbar::before {
  background-color: var(--kln-border-normal);
  border-radius: 4px;
  left: 1px;
  right: 1px;
  transition: background-color 0.15s;
  /* Forzar siempre visible (override del fade default de SimpleBar) */
  opacity: 1 !important;
}

.drawer-scroll-content .simplebar-scrollbar.simplebar-hover::before,
.drawer-scroll-content .simplebar-scrollbar:hover::before {
  background-color: var(--kln-border-accent);
  opacity: 1 !important;
}

.drawer-scroll-content .simplebar-scrollbar.simplebar-visible::before {
  opacity: 1 !important;
}

/* Mientras el usuario arrastra el thumb (active), pasa a violet
   para feedback claro de que esta interactuando. */
.drawer-scroll-content .simplebar-scrollbar.simplebar-dragging::before,
.drawer-scroll-content .simplebar-scrollbar:active::before {
  background-color: var(--kln-accent);
  opacity: 0.7 !important;
}

/* El thumb ocupa todo el ancho del track */
.drawer-scroll-content .simplebar-track.simplebar-vertical .simplebar-scrollbar {
  right: 0;
  left: 0;
  width: 8px;
}

.drawer-routing-degraded {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  margin: 0 12px 8px;
  background: rgba(255, 180, 0, 0.08);
  border: 1px solid rgba(255, 180, 0, 0.25);
  border-radius: 6px;
  color: var(--kln-text);
  font-size: 11px;
  line-height: 1.3;
}

.drawer-detail-mappoint {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
}
.drawer-detail-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--kln-text);
}
.drawer-detail-subtitle-mono {
  font-family: 'Roboto Mono', 'Courier New', monospace;
  font-size: 11px;
  color: var(--kln-text-muted);
  margin-top: 2px;
}
</style>
